import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import * as XLSX from 'xlsx';
import {
  CalendarCheck,
  CheckCircle2,
  Clock,
  Building2,
  UserCheck,
  Users,
  AlertTriangle,
  FileText,
  Printer,
  Download,
  Search,
  ShieldCheck,
  ArrowLeft,
  RefreshCw,
  Award,
  Check,
  HelpCircle,
  TrendingUp,
  FileSpreadsheet,
  ChevronRight,
  Send,
  Eye,
  Lock,
  Unlock,
  Sparkles,
  Phone,
  Mail,
  User,
  BadgePercent,
  Filter,
  BarChart3,
  PieChart,
  Layers,
  ArrowUpDown,
  GraduationCap,
  Calendar,
  ChevronDown,
  Trophy,
  SlidersHorizontal,
  ArrowDown,
  ArrowUp,
  History
} from 'lucide-react';

interface ClassAttendanceRow {
  className: string;
  boysEnrolled: number | '';
  boysPresent: number | '';
  girlsEnrolled: number | '';
  girlsPresent: number | '';
}

interface TeacherAttendanceState {
  totalWorking: number | '';
  present: number | '';
  leave: number | '';
  otherDuty: number | '';
  absent: number | '';
}

import {
  DAILY_ATTENDANCE_SCHOOLS_MASTER,
  SchoolMasterItem
} from '../data/dailyAttendanceSchoolsMaster';

const DEFAULT_CLASSES = [
  'Class 6',
  'Class 7',
  'Class 8',
  'Class 9',
  'Class 10',
  'Class 11',
  'Class 12'
];

const BLOCKS = ['Ferozepur Jhirka', 'Nagina', 'Nuh', 'Punhana', 'Taoru'];

const CATEGORIES = ['Middle School', 'High School', 'Senior Secondary School'];

export function normalizeAttendanceBlock(block?: string): string {
  if (!block) return '';
  const b = block.trim().toLowerCase();
  if (b === 'ferozepur jhirka' || b === 'fp jhirka' || b === 'f.p. jhirka' || b === 'ferozpur jhirka' || b === 'firozpur jhirka') return 'Ferozepur Jhirka';
  if (b === 'nagina') return 'Nagina';
  if (b === 'nuh') return 'Nuh';
  if (b === 'punhana' || b === 'pumhana') return 'Punhana';
  if (b === 'taoru' || b === 'tauru') return 'Taoru';
  return block.trim();
}

export function normalizeAttendanceCategory(cat?: string): string {
  if (!cat) return '';
  const c = cat.trim().toLowerCase();
  if (c === 'middle school' || c === 'middle' || c === 'gms' || c === 'ggms') return 'Middle School';
  if (c === 'high school' || c === 'high' || c === 'ghs' || c === 'gghs' || c === 'secondary school') return 'High School';
  if (c === 'senior secondary school' || c === 'senior secondary' || c === 'sr sec' || c === 'sr. sec.' || c === 'gsss' || c === 'ggsss' || c === 'aarohi' || c === 'kgbv' || c === 'model sanskriti') return 'Senior Secondary School';
  return cat.trim();
}

const DESIGNATIONS = [
  'Principal',
  'Headmaster / Headmistress',
  'PGT (School In-charge)',
  'TGT (Attendance In-charge)',
  'Junior Basic Teacher / PRT',
  'School Administrative Clerk',
  'Other Authorized Staff'
];

// Complete Academic Year 2026-2027 Calendar (01-April-2026 to 31-March-2027 = 365 Days)
export const ACADEMIC_YEAR_DATES: { iso: string; label: string }[] = (() => {
  const list: { iso: string; label: string }[] = [];
  const start = new Date(2026, 3, 1); // 2026-04-01
  const end = new Date(2027, 2, 31);   // 2027-03-31
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  const cur = new Date(start);
  while (cur <= end) {
    const y = cur.getFullYear();
    const m = String(cur.getMonth() + 1).padStart(2, '0');
    const d = String(cur.getDate()).padStart(2, '0');
    const iso = `${y}-${m}-${d}`;
    const label = `${d} ${monthNames[cur.getMonth()]} ${y}`;
    list.push({ iso, label });
    cur.setDate(cur.getDate() + 1);
  }
  return list;
})();

export const formatDisplayDate = (dateStr: string): string => {
  if (!dateStr || dateStr === 'all') return 'All Dates Range';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  const year = parts[0];
  const monthIdx = parseInt(parts[1], 10) - 1;
  const day = parts[2];
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  if (monthIdx >= 0 && monthIdx < 12) {
    return `${day} ${monthNames[monthIdx]} ${year}`;
  }
  return dateStr;
};

export const DailyAttendanceMonitoringPage: React.FC<{
  onBackToPortal?: () => void;
}> = ({ onBackToPortal }) => {
  const { user } = useAuth();

  // Role Scoping: DEO / BEO vs School-Only Users
  const isDEO = user?.role === 'deo' || user?.role === 'admin';
  const isBEO = user?.role === 'beo';
  const isSchoolOnly = !isDEO && !isBEO;

  // Active View Tab: 'dashboard' | 'submit' | 'history' | 'monthly'
  // School/regular users are restricted strictly to 'submit'
  const [activeTab, setActiveTab] = useState<'dashboard' | 'submit' | 'history' | 'monthly'>(
    isSchoolOnly ? 'submit' : 'dashboard'
  );

  // Live DEO Form Control State (08:00 AM – 09:30 AM IST default + manual DEO override)
  const [formControl, setFormControl] = useState<{
    is_open: boolean;
    status_label: string;
    mode: string;
    scheduled_open: string;
    scheduled_close: string;
    status_reason: string;
    current_time_ist: string;
    is_within_schedule: boolean;
    updated_by: string;
  }>({
    is_open: false,
    status_label: 'FORM CLOSED',
    mode: 'auto',
    scheduled_open: '08:00 AM IST',
    scheduled_close: '09:30 AM IST',
    status_reason: 'Outside Scheduled Window (08:00 AM – 09:30 AM IST)',
    current_time_ist: '',
    is_within_schedule: false,
    updated_by: 'System (Default Schedule)'
  });
  const [isUpdatingControl, setIsUpdatingControl] = useState<boolean>(false);

  // Real-time IST Clock
  const [currentTimeIST, setCurrentTimeIST] = useState<string>('');
  const [isWindowOpen, setIsWindowOpen] = useState<boolean>(false);
  const [windowOverride, setWindowOverride] = useState<boolean>(false);
  const [overrideReason, setOverrideReason] = useState<string>('DEO Administrative Verification & Late Entry Permission');

  // Cascading Metadata Cache - Pre-initialized with full 403-school master
  const [cascadingData, setCascadingData] = useState<{
    blocks: string[];
    categories: string[];
    block_categories: Record<string, string[]>;
    dates: string[];
    months: string[];
    classes: string[];
    schools: SchoolMasterItem[];
  }>({
    blocks: BLOCKS,
    categories: CATEGORIES,
    block_categories: {
      'Ferozepur Jhirka': ['Middle School', 'Senior Secondary School'],
      'Nagina': ['Middle School', 'Senior Secondary School'],
      'Nuh': ['High School', 'Middle School', 'Senior Secondary School'],
      'Punhana': ['High School', 'Middle School', 'Senior Secondary School'],
      'Taoru': ['High School', 'Middle School', 'Senior Secondary School']
    },
    dates: [],
    months: [],
    classes: DEFAULT_CLASSES,
    schools: DAILY_ATTENDANCE_SCHOOLS_MASTER
  });

  // Helper: Get Current Date in IST as YYYY-MM-DD
  const getTodayISTString = () => {
    const now = new Date();
    const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
    const ist = new Date(utc + (3600000 * 5.5));
    return ist.toISOString().split('T')[0];
  };

  // Monthly summary cache
  const [monthlyData, setMonthlyData] = useState<Record<string, any>>({});
  const [selectedMonthTab, setSelectedMonthTab] = useState<string>('September 2026');

  // DEO Dashboard Filter State & Modes: 'single' | 'range' | 'quick'
  const [dateFilterMode, setDateFilterMode] = useState<'single' | 'range' | 'quick'>('single');
  const [filterDate, setFilterDate] = useState<string>(getTodayISTString());
  const [filterFromDate, setFilterFromDate] = useState<string>('');
  const [filterToDate, setFilterToDate] = useState<string>('');
  const [filterMonth, setFilterMonth] = useState<string>('all');
  const [filterBlock, setFilterBlock] = useState<string>(user?.role === 'beo' && user?.block ? user.block : 'all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterSchoolCode, setFilterSchoolCode] = useState<string>(
    user?.role === 'principal' || user?.role === 'school' ? String(user?.school_code || user?.school_id || 'all') : 'all'
  );
  const [filterClass, setFilterClass] = useState<string>('all');
  const [dashboardSearch, setDashboardSearch] = useState<string>('');

  // DEO Dashboard Data State
  const [dashboardData, setDashboardData] = useState<any | null>(null);
  const [loadingDashboard, setLoadingDashboard] = useState<boolean>(false);
  const [schoolTableSortField, setSchoolTableSortField] = useState<string>('school_name');
  const [schoolTableSortAsc, setSchoolTableSortAsc] = useState<boolean>(true);
  const [selectedSchoolDossier, setSelectedSchoolDossier] = useState<any | null>(null);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState<any | null>(null);
  const [topBottomToggle, setTopBottomToggle] = useState<'top' | 'bottom'>('top');

  // Dedicated "School Performance Ranking" State
  const [rankingDirection, setRankingDirection] = useState<'top' | 'bottom'>('top');
  const [rankingN, setRankingN] = useState<number>(10);
  const [customNInput, setCustomNInput] = useState<string>('');
  const [rankingCategory, setRankingCategory] = useState<string>('all');
  const [rankingMetric, setRankingMetric] = useState<string>('student_attendance_pct');
  const [rankingBlock, setRankingBlock] = useState<string>('all');

  // Table 1: School Submission Status State
  const [submissionStatusTab, setSubmissionStatusTab] = useState<'all' | 'submitted' | 'not_submitted'>('all');
  const [statusTableSearch, setStatusTableSearch] = useState<string>('');
  const [statusTablePage, setStatusTablePage] = useState<number>(1);
  const [statusTablePageSize, setStatusTablePageSize] = useState<number>(25);

  // Table 2: School & Block-wise Classwise Attendance State
  const [classwiseViewMode, setClasswiseViewMode] = useState<'school' | 'block'>('school');
  const [classwiseTableSearch, setClasswiseTableSearch] = useState<string>('');
  const [classwiseTablePage, setClasswiseTablePage] = useState<number>(1);
  const [classwiseTablePageSize, setClasswiseTablePageSize] = useState<number>(25);
  const [classwiseBlockFilter, setClasswiseBlockFilter] = useState<string>('all');
  const [classwiseCategoryFilter, setClasswiseCategoryFilter] = useState<string>('all');

  // Table 4: Date-wise Attendance Summary State
  const [dateSummarySearch, setDateSummarySearch] = useState<string>('');
  const [dateSummaryPage, setDateSummaryPage] = useState<number>(1);
  const [dateSummaryPageSize, setDateSummaryPageSize] = useState<number>(15);

  // School Dossier All-Dates History State
  const [schoolHistoryList, setSchoolHistoryList] = useState<any[]>([]);
  const [loadingSchoolHistory, setLoadingSchoolHistory] = useState<boolean>(false);

  // Fetch school history when selectedSchoolDossier or selectedHistoryItem changes
  useEffect(() => {
    const item = selectedSchoolDossier || selectedHistoryItem;
    if (item) {
      const code = item.source_school_code || item.school_code || item.id;
      if (code) {
        setLoadingSchoolHistory(true);
        fetch(`/api/daily-attendance/school-history?school_code=${code}`)
          .then(r => r.json())
          .then(json => {
            if (json.success && Array.isArray(json.history)) {
              setSchoolHistoryList(json.history);
            } else {
              setSchoolHistoryList([]);
            }
          })
          .catch(() => setSchoolHistoryList([]))
          .finally(() => setLoadingSchoolHistory(false));
      }
    } else {
      setSchoolHistoryList([]);
    }
  }, [selectedSchoolDossier, selectedHistoryItem]);

  // Role security lock: prevent unauthorized users from opening DEO dashboard
  useEffect(() => {
    if (isSchoolOnly && activeTab !== 'submit') {
      setActiveTab('submit');
    }
  }, [isSchoolOnly, activeTab]);

  // PIN 5120 Security Lock State for Monitoring/Administrative sections
  const [isUnlocked, setIsUnlocked] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem('daily_attendance_unlocked') === 'true';
    } catch {
      return false;
    }
  });
  const [pinInput, setPinInput] = useState<string>('');
  const [pinError, setPinError] = useState<string>('');
  const [isVerifyingPin, setIsVerifyingPin] = useState<boolean>(false);

  const handleVerifyPin = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setIsVerifyingPin(true);
    setPinError('');
    try {
      const trimmedPin = pinInput.trim();
      if (!trimmedPin) {
        setPinError('Please enter the 4-digit PIN.');
        setIsVerifyingPin(false);
        return;
      }

      // 1. Client SHA-256 verification (5120 -> 59dac69c02fa356c6337a03ef91cf7f19b07554a95a3e53d0e3baac34ad6cbce)
      const enc = new TextEncoder();
      const data = enc.encode(trimmedPin);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashHex = Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');

      if (hashHex === '59dac69c02fa356c6337a03ef91cf7f19b07554a95a3e53d0e3baac34ad6cbce') {
        setIsUnlocked(true);
        sessionStorage.setItem('daily_attendance_unlocked', 'true');
        setPinError('');
        setPinInput('');
        setIsVerifyingPin(false);
        return;
      }

      // 2. Server-side verification fallback
      const res = await fetch('/api/daily-attendance/verify-pin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin: trimmedPin })
      });
      const json = await res.json();
      if (json.success && json.access_granted) {
        setIsUnlocked(true);
        sessionStorage.setItem('daily_attendance_unlocked', 'true');
        setPinError('');
        setPinInput('');
      } else {
        setPinError('Invalid Authorization PIN. Access Denied.');
        setPinInput('');
      }
    } catch {
      setPinError('Invalid Authorization PIN. Access Denied.');
      setPinInput('');
    } finally {
      setIsVerifyingPin(false);
    }
  };

  const handleLockSession = () => {
    setIsUnlocked(false);
    sessionStorage.removeItem('daily_attendance_unlocked');
    setPinInput('');
    setPinError('');
  };

  // Section 1: Basic Details for Submission Form
  const [formDate, setFormDate] = useState<string>(getTodayISTString());
  const [submitterName, setSubmitterName] = useState<string>(user?.name || '');
  const [designation, setDesignation] = useState<string>(user?.role === 'principal' ? 'Principal' : 'PGT (School In-charge)');
  const [mobileNumber, setMobileNumber] = useState<string>('');
  const [emailAddress, setEmailAddress] = useState<string>(user?.email || '');

  // Section 2: Form School Cascading Selection (Block -> Category -> School)
  const [formBlock, setFormBlock] = useState<string>(user?.block || 'Nuh');
  const [formCategory, setFormCategory] = useState<string>('Senior Secondary School');
  const [formSchoolCode, setFormSchoolCode] = useState<string>('');
  const [formSelectedSchool, setFormSelectedSchool] = useState<SchoolMasterItem | null>(null);

  // Section 3: Class-wise Student Attendance (Classes 6 to 12)
  const [classRows, setClassRows] = useState<ClassAttendanceRow[]>(
    DEFAULT_CLASSES.map((c) => ({
      className: c,
      boysEnrolled: '',
      boysPresent: '',
      girlsEnrolled: '',
      girlsPresent: ''
    }))
  );

  // Section 4: Teacher Attendance
  const [teacherState, setTeacherState] = useState<TeacherAttendanceState>({
    totalWorking: '',
    present: '',
    leave: '',
    otherDuty: '',
    absent: ''
  });

  // UI state
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submissionSuccess, setSubmissionSuccess] = useState<any | null>(null);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [duplicateWarning, setDuplicateWarning] = useState<any | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);

  // History & Paginated Records State (24,599 historical records)
  const [historyList, setHistoryList] = useState<any[]>([]);
  const [historyPage, setHistoryPage] = useState<number>(1);
  const [historyLimit, setHistoryLimit] = useState<number>(50);
  const [historyTotal, setHistoryTotal] = useState<number>(0);
  const [historyTotalPages, setHistoryTotalPages] = useState<number>(1);
  const [historySearch, setHistorySearch] = useState<string>('');
  const [historyFilterBlock, setHistoryFilterBlock] = useState<string>(user?.role === 'beo' && user?.block ? user.block : 'all');
  const [historyFilterDate, setHistoryFilterDate] = useState<string>('all');
  const [historyFilterCategory, setHistoryFilterCategory] = useState<string>('all');
  const [historySummary, setHistorySummary] = useState<any | null>(null);
  const [loadingHistory, setLoadingHistory] = useState<boolean>(false);

  // 1. Fetch & Poll DEO Form Control Status
  const loadFormControl = async () => {
    try {
      const res = await fetch('/api/daily-attendance/form-control');
      const json = await res.json();
      if (json.success && json.data) {
        setFormControl(json.data);
        setIsWindowOpen(json.data.is_open);
      }
    } catch (e) {
      console.error('Failed to load form control status:', e);
    }
  };

  const handleSetFormControl = async (mode: 'auto' | 'force_open' | 'force_closed') => {
    setIsUpdatingControl(true);
    try {
      const res = await fetch('/api/daily-attendance/form-control', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode,
          updated_by: user?.name ? `${user.name} (${user.role?.toUpperCase()})` : 'DEO Nuh'
        })
      });
      const json = await res.json();
      if (json.success && json.data) {
        setFormControl(json.data);
        setIsWindowOpen(json.data.is_open);
      }
    } catch (e) {
      console.error('Failed to update form control:', e);
    } finally {
      setIsUpdatingControl(false);
    }
  };

  // 2. Live IST Clock & 08:00 AM – 09:30 AM IST Schedule Monitor
  useEffect(() => {
    loadFormControl();

    const updateTime = () => {
      const now = new Date();
      const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
      const ist = new Date(utc + (3600000 * 5.5));

      const timeStr = ist.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
      const dateStr = ist.toLocaleDateString('en-IN', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

      setCurrentTimeIST(`${dateStr} • ${timeStr} IST`);

      const hours = ist.getHours();
      const minutes = ist.getMinutes();
      const totalMinutes = hours * 60 + minutes;

      // Scheduled Window: 08:00 AM = 480 min, 09:30 AM = 570 min
      const inWindow = totalMinutes >= 480 && totalMinutes <= 570;

      // If mode is auto, update window open flag
      if (formControl.mode === 'auto') {
        setIsWindowOpen(inWindow);
      }
    };

    updateTime();
    const clockInterval = setInterval(updateTime, 1000);
    const pollInterval = setInterval(loadFormControl, 10000); // sync every 10s

    return () => {
      clearInterval(clockInterval);
      clearInterval(pollInterval);
    };
  }, [formControl.mode]);

  // 2. Fetch Cascading Options & Monthly Metadata
  useEffect(() => {
    const loadMetadata = async () => {
      try {
        const [cascRes, monthRes] = await Promise.all([
          fetch('/api/daily-attendance/cascading-options'),
          fetch('/api/daily-attendance/monthly-summary')
        ]);
        const [cascJson, monthJson] = await Promise.all([cascRes.json(), monthRes.json()]);

        if (cascJson.success && cascJson.data) {
          setCascadingData(cascJson.data);

          // If available dates exist, default to the latest date <= today (or today IST)
          if (cascJson.data.dates && cascJson.data.dates.length > 0) {
            const today = getTodayISTString();
            const pastAndTodayDates = cascJson.data.dates.filter((d: string) => d <= today);
            const latestValidDate = pastAndTodayDates.length > 0 ? pastAndTodayDates[0] : cascJson.data.dates[0];
            if (latestValidDate) {
              setFilterDate(latestValidDate);
            }
          }

          // If user belongs to a school, auto-select it in form and dashboard
          if (user?.school_code || user?.school_id) {
            const codeStr = String(user.school_code || user.school_id);
            const userSchool = cascJson.data.schools?.find((s: SchoolMasterItem) => s.school_code === codeStr || String(s.id) === codeStr);
            if (userSchool) {
              setFormBlock(userSchool.block);
              setFormCategory(userSchool.school_type);
              setFormSchoolCode(userSchool.school_code);
              setFormSelectedSchool(userSchool);

              setFilterBlock(userSchool.block);
              setFilterCategory(userSchool.school_type);
              setFilterSchoolCode(userSchool.school_code);
            }
          }
        }
        if (monthJson.success && monthJson.data) {
          setMonthlyData(monthJson.data);
        }
      } catch (err) {
        console.error('Failed to load cascading options:', err);
      }
    };

    loadMetadata();
  }, [user]);

  // 3. Dynamic Filter Cascades (Block -> Category -> School)
  // Categories available for currently selected dashboard block
  const dashboardAvailableCategories = useMemo(() => {
    if (filterBlock === 'all') return cascadingData.categories || CATEGORIES;
    const normBlock = normalizeAttendanceBlock(filterBlock);
    return cascadingData.block_categories[normBlock] || cascadingData.categories || CATEGORIES;
  }, [filterBlock, cascadingData]);

  // Schools available for currently selected dashboard block + category
  const dashboardAvailableSchools = useMemo(() => {
    let sourceList: SchoolMasterItem[] = [];
    if (cascadingData.schools && cascadingData.schools.length > 0) {
      sourceList = cascadingData.schools;
    } else if (dashboardData?.schools_table && dashboardData.schools_table.length > 0) {
      sourceList = dashboardData.schools_table.map((s: any) => ({
        id: s.id,
        school_name: s.school_name,
        school_code: String(s.school_code || ''),
        udise_code: String(s.udise_code || ''),
        block: s.block,
        school_type: s.school_type
      }));
    } else {
      sourceList = DAILY_ATTENDANCE_SCHOOLS_MASTER;
    }

    // Deduplicate by unique school key
    const uniqueMap = new Map<string, SchoolMasterItem>();
    for (const s of sourceList) {
      const key = String(s.school_code || s.udise_code || s.id || s.school_name).trim();
      if (key && !uniqueMap.has(key)) {
        uniqueMap.set(key, s);
      }
    }
    let list = Array.from(uniqueMap.values());

    if (filterBlock && filterBlock !== 'all') {
      const normFilterBlock = normalizeAttendanceBlock(filterBlock).toLowerCase();
      list = list.filter(s => {
        const sBlock = normalizeAttendanceBlock(s.block).toLowerCase();
        return sBlock === normFilterBlock;
      });
    }

    if (filterCategory && filterCategory !== 'all') {
      const normFilterCat = normalizeAttendanceCategory(filterCategory).toLowerCase();
      list = list.filter(s => {
        const sCat = normalizeAttendanceCategory(s.school_type).toLowerCase();
        return sCat === normFilterCat;
      });
    }

    return list.sort((a, b) => (a.school_name || '').localeCompare(b.school_name || ''));
  }, [filterBlock, filterCategory, cascadingData.schools, dashboardData?.schools_table]);

  // Reset selected school if filtered out of current scope
  useEffect(() => {
    if (filterSchoolCode !== 'all') {
      const exists = dashboardAvailableSchools.some(
        s => String(s.school_code) === String(filterSchoolCode) || String(s.id) === String(filterSchoolCode)
      );
      if (!exists && dashboardAvailableSchools.length > 0) {
        if (user?.role !== 'principal' && user?.role !== 'school') {
          setFilterSchoolCode('all');
        }
      }
    }
  }, [dashboardAvailableSchools, filterSchoolCode, user]);

  // Categories available for submission form
  const formAvailableCategories = useMemo(() => {
    if (!formBlock) return CATEGORIES;
    const normBlock = normalizeAttendanceBlock(formBlock);
    return cascadingData.block_categories[normBlock] || cascadingData.categories || CATEGORIES;
  }, [formBlock, cascadingData]);

  // Schools available for submission form (strictly Block + Category)
  const formAvailableSchools = useMemo(() => {
    let sourceList: SchoolMasterItem[] = (cascadingData.schools && cascadingData.schools.length > 0)
      ? cascadingData.schools
      : DAILY_ATTENDANCE_SCHOOLS_MASTER;

    return sourceList.filter(s => {
      if (formBlock) {
        const normFormBlock = normalizeAttendanceBlock(formBlock).toLowerCase();
        const normSBlock = normalizeAttendanceBlock(s.block).toLowerCase();
        if (normSBlock !== normFormBlock) return false;
      }
      if (formCategory) {
        const normFormCat = normalizeAttendanceCategory(formCategory).toLowerCase();
        const normSCat = normalizeAttendanceCategory(s.school_type).toLowerCase();
        if (normSCat !== normFormCat) return false;
      }
      return true;
    }).sort((a, b) => (a.school_name || '').localeCompare(b.school_name || ''));
  }, [formBlock, formCategory, cascadingData.schools]);

  // When form Block changes, ensure valid category
  const handleFormBlockChange = (blk: string) => {
    setFormBlock(blk);
    const validCats = cascadingData.block_categories[blk] || CATEGORIES;
    if (!validCats.includes(formCategory) && validCats.length > 0) {
      setFormCategory(validCats[0]);
    }
    setFormSchoolCode('');
    setFormSelectedSchool(null);
  };

  // When form Category changes, reset school
  const handleFormCategoryChange = (cat: string) => {
    setFormCategory(cat);
    setFormSchoolCode('');
    setFormSelectedSchool(null);
  };

  // When form School changes
  const handleFormSchoolChange = (code: string) => {
    setFormSchoolCode(code);
    const found = (cascadingData.schools || []).find(s => s.school_code === code) || null;
    setFormSelectedSchool(found);
    if (found) {
      setFormBlock(found.block);
      setFormCategory(found.school_type);
    }
  };

  // 4. Fetch DEO Dashboard Data on Filter Changes
  const loadDashboardData = async () => {
    setLoadingDashboard(true);
    try {
      const params = new URLSearchParams();
      if (filterDate) params.set('date', filterDate);
      if (filterFromDate) params.set('from_date', filterFromDate);
      if (filterToDate) params.set('to_date', filterToDate);
      if (filterMonth && filterMonth !== 'all') params.set('month', filterMonth);
      if (filterBlock && filterBlock !== 'all') params.set('block', filterBlock);
      if (filterCategory && filterCategory !== 'all') params.set('category', filterCategory);
      if (filterSchoolCode && filterSchoolCode !== 'all') params.set('school_code', filterSchoolCode);
      if (filterClass && filterClass !== 'all') params.set('class', filterClass);
      if (dashboardSearch.trim()) params.set('search', dashboardSearch.trim());

      const res = await fetch(`/api/daily-attendance/deo-dashboard?${params.toString()}`);
      const json = await res.json();
      if (json.success && json.kpis) {
        setDashboardData(json);
      }
    } catch (err) {
      console.error('Failed to load DEO dashboard data:', err);
    } finally {
      setLoadingDashboard(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, [filterDate, filterFromDate, filterToDate, filterMonth, filterBlock, filterCategory, filterSchoolCode, filterClass, dashboardSearch]);

  // 5. Fetch Paginated Records for History Tab
  const loadHistoryRecords = async () => {
    setLoadingHistory(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(historyPage));
      params.set('limit', String(historyLimit));

      if (historyFilterDate !== 'all') params.set('date', historyFilterDate);
      if (historyFilterBlock !== 'all') params.set('block', historyFilterBlock);
      if (historyFilterCategory !== 'all') params.set('school_type', historyFilterCategory);
      if (historySearch.trim()) params.set('search', historySearch.trim());

      if (user?.role === 'principal' || user?.role === 'school') {
        if (user.school_id) params.set('school_id', String(user.school_id));
      }

      const res = await fetch(`/api/daily-attendance/records?${params.toString()}`);
      const json = await res.json();
      if (json.success && Array.isArray(json.data)) {
        setHistoryList(json.data);
        if (json.pagination) {
          setHistoryTotal(json.pagination.total);
          setHistoryTotalPages(json.pagination.totalPages);
        }
        if (json.summary) {
          setHistorySummary(json.summary);
        }
      }
    } catch (err) {
      console.error('Failed to fetch attendance history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    loadHistoryRecords();
  }, [historyPage, historyLimit, historyFilterDate, historyFilterBlock, historyFilterCategory, historySearch, user]);

  // Form Class-wise Calculations
  const handleClassValueChange = (
    index: number,
    field: 'boysEnrolled' | 'boysPresent' | 'girlsEnrolled' | 'girlsPresent',
    value: string
  ) => {
    const num = value === '' ? '' : Math.max(0, parseInt(value, 10) || 0);
    setClassRows((prev) => {
      const copy = [...prev];
      const row = { ...copy[index], [field]: num };

      const bEnrolled = typeof row.boysEnrolled === 'number' ? row.boysEnrolled : 0;
      const bPresent = typeof row.boysPresent === 'number' ? row.boysPresent : 0;
      const gEnrolled = typeof row.girlsEnrolled === 'number' ? row.girlsEnrolled : 0;
      const gPresent = typeof row.girlsPresent === 'number' ? row.girlsPresent : 0;

      if (field === 'boysPresent' && bPresent > bEnrolled && bEnrolled > 0) row.boysPresent = bEnrolled;
      if (field === 'girlsPresent' && gPresent > gEnrolled && gEnrolled > 0) row.girlsPresent = gEnrolled;

      copy[index] = row;
      return copy;
    });
  };

  const formClassStats = useMemo(() => {
    return classRows.map((r) => {
      const bEnr = typeof r.boysEnrolled === 'number' ? r.boysEnrolled : 0;
      const bPres = typeof r.boysPresent === 'number' ? r.boysPresent : 0;
      const gEnr = typeof r.girlsEnrolled === 'number' ? r.girlsEnrolled : 0;
      const gPres = typeof r.girlsPresent === 'number' ? r.girlsPresent : 0;

      const totalEnr = bEnr + gEnr;
      const totalPres = bPres + gPres;
      const totalAbs = Math.max(0, totalEnr - totalPres);

      const pct = totalEnr > 0 ? ((totalPres / totalEnr) * 100).toFixed(1) : 'N/A';
      return {
        className: r.className,
        boysEnrolled: bEnr,
        boysPresent: bPres,
        girlsEnrolled: gEnr,
        girlsPresent: gPres,
        totalEnrolled: totalEnr,
        totalPresent: totalPres,
        totalAbsent: totalAbs,
        attendancePct: pct
      };
    });
  }, [classRows]);

  const formStudentSummary = useMemo(() => {
    let totBoysEnr = 0; let totBoysPres = 0; let totGirlsEnr = 0; let totGirlsPres = 0;
    formClassStats.forEach((s) => {
      totBoysEnr += s.boysEnrolled; totBoysPres += s.boysPresent;
      totGirlsEnr += s.girlsEnrolled; totGirlsPres += s.girlsPresent;
    });
    const totalEnrolled = totBoysEnr + totGirlsEnr;
    const totalPresent = totBoysPres + totGirlsPres;
    const totalAbsent = Math.max(0, totalEnrolled - totalPresent);
    const overallPct = totalEnrolled > 0 ? Number(((totalPresent / totalEnrolled) * 100).toFixed(1)) : null;

    return { totalEnrolled, totalPresent, totalAbsent, attendancePct: overallPct, totBoysEnr, totBoysPres, totGirlsEnr, totGirlsPres };
  }, [formClassStats]);

  const formTeacherStats = useMemo(() => {
    const totalWorking = typeof teacherState.totalWorking === 'number' ? teacherState.totalWorking : 0;
    const present = typeof teacherState.present === 'number' ? teacherState.present : 0;
    const leave = typeof teacherState.leave === 'number' ? teacherState.leave : 0;
    const otherDuty = typeof teacherState.otherDuty === 'number' ? teacherState.otherDuty : 0;
    const absent = typeof teacherState.absent === 'number' ? teacherState.absent : 0;
    const accounted = present + leave + otherDuty + absent;
    const diff = totalWorking - accounted;
    const pct = totalWorking > 0 ? Number(((present / totalWorking) * 100).toFixed(1)) : null;

    return { totalWorking, present, leave, otherDuty, absent, accounted, diff, attendancePct: pct };
  }, [teacherState]);

  // Validation
  const validateForm = (): boolean => {
    const errs: Record<string, string> = {};
    if (!formDate) errs.attendanceDate = 'Attendance date is required.';
    if (!submitterName.trim()) errs.submitterName = 'Submitter name is required.';
    if (!designation.trim()) errs.designation = 'Designation is required.';

    const cleanMobile = mobileNumber.replace(/\D/g, '');
    if (!cleanMobile) {
      errs.mobileNumber = 'Mobile number is required.';
    } else if (cleanMobile.length !== 10 || !/^[6-9]\d{9}$/.test(cleanMobile)) {
      errs.mobileNumber = 'Please enter a valid 10-digit Indian mobile number.';
    }

    if (!formSelectedSchool) {
      errs.school = 'Please select a valid school using Block -> Category -> School dropdowns.';
    }

    if (formStudentSummary.totalEnrolled === 0) {
      errs.students = 'Please enter enrolled and present student counts for at least one class.';
    }

    if (formTeacherStats.totalWorking === 0) {
      errs.teachers = 'Please enter total working faculty count.';
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Prefill Demo / Genuine Data for Fast Testing
  const handlePrefillSample = () => {
    if (!formSelectedSchool && formAvailableSchools.length > 0) {
      const sample = formAvailableSchools[0];
      setFormSelectedSchool(sample);
      setFormSchoolCode(sample.school_code);
      setFormBlock(sample.block);
      setFormCategory(sample.school_type);
    }
    setSubmitterName('Devender Kumar');
    setDesignation('Principal');
    setMobileNumber('9812345678');
    setEmailAddress('gsss.nuh@haryana.gov.in');

    setClassRows([
      { className: 'Class 6', boysEnrolled: 28, boysPresent: 25, girlsEnrolled: 30, girlsPresent: 28 },
      { className: 'Class 7', boysEnrolled: 32, boysPresent: 29, girlsEnrolled: 34, girlsPresent: 31 },
      { className: 'Class 8', boysEnrolled: 35, boysPresent: 31, girlsEnrolled: 36, girlsPresent: 33 },
      { className: 'Class 9', boysEnrolled: 40, boysPresent: 36, girlsEnrolled: 42, girlsPresent: 38 },
      { className: 'Class 10', boysEnrolled: 45, boysPresent: 41, girlsEnrolled: 48, girlsPresent: 44 },
      { className: 'Class 11', boysEnrolled: 30, boysPresent: 27, girlsEnrolled: 32, girlsPresent: 29 },
      { className: 'Class 12', boysEnrolled: 38, boysPresent: 35, girlsEnrolled: 40, girlsPresent: 37 }
    ]);

    setTeacherState({
      totalWorking: 26,
      present: 24,
      leave: 1,
      otherDuty: 1,
      absent: 0
    });
    setErrors({});
  };

  const handleResetForm = () => {
    setClassRows(
      DEFAULT_CLASSES.map((c) => ({
        className: c,
        boysEnrolled: '',
        boysPresent: '',
        girlsEnrolled: '',
        girlsPresent: ''
      }))
    );
    setTeacherState({ totalWorking: '', present: '', leave: '', otherDuty: '', absent: '' });
    setErrors({});
    setSubmissionSuccess(null);
  };

  const handleSubmit = async () => {
    if (!formControl.is_open && !isDEO && !windowOverride) {
      alert(`Daily Attendance Submission Form is currently CLOSED (${formControl.status_reason}).\n\nOfficial Scheduled Window: 08:00 AM – 09:30 AM IST (Daily).\nPlease submit within the authorized window or contact the DEO Office for an administrative unlock.`);
      return;
    }
    if (!validateForm()) return;
    setShowConfirmModal(true);
  };

  const confirmAndSend = async () => {
    setShowConfirmModal(false);
    setIsSubmitting(true);
    setSubmissionError(null);
    setDuplicateWarning(null);

    const payload = {
      attendance_date: formDate,
      submitter_name: submitterName,
      designation: designation,
      mobile_number: mobileNumber,
      email_address: emailAddress,
      school_id: formSelectedSchool?.id,
      school_name: formSelectedSchool?.school_name,
      school_code: formSelectedSchool?.school_code,
      udise_code: formSelectedSchool?.udise_code,
      block: formBlock,
      school_type: formCategory,
      window_override: windowOverride,
      override_reason: windowOverride ? overrideReason : null,
      classes: formClassStats,
      teachers: formTeacherStats,
      summary: formStudentSummary,
      submitted_at: new Date().toISOString()
    };

    try {
      const res = await fetch('/api/daily-attendance/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const json = await res.json().catch(() => ({}));

      // 1. Duplicate Handling (HTTP 409 or status: DUPLICATE)
      if (res.status === 409 || json.status === 'DUPLICATE' || json.duplicate) {
        setSubmissionSuccess(null);
        setDuplicateWarning({
          message: json.message || 'इस विद्यालय की Attendance Report इस दिन के लिए पहले ही दर्ज की जा चुकी है।',
          date: formDate,
          school_name: formSelectedSchool?.school_name,
          school_code: formSelectedSchool?.school_code,
          udise_code: formSelectedSchool?.udise_code,
          existing_record: json.existing_record
        });
        return;
      }

      // 2. Failure Handling (Do NOT show false success if Google Sheet sync failed)
      if (!res.ok || json.success === false || json.google_sheet_status !== 'SYNCED') {
        setSubmissionSuccess(null);
        setSubmissionError(json.message || 'Google Sheet में Attendance Report दर्ज करने में त्रुटि हुई। कृपया पुनः प्रयास करें।');
        return;
      }

      // 3. Official Success Receipt (ONLY after Google Sheet confirms SYNCED)
      const ackNumber = json.acknowledgement_number || json.data?.acknowledgement_number || `ACK-NUH-${formDate.replace(/-/g, '')}-${Math.floor(1000 + Math.random() * 9000)}`;
      const record = {
        id: json.record_id || json.data?.id || Date.now(),
        acknowledgement_number: ackNumber,
        receiving_officer: 'जिला शिक्षा अधिकारी, नूंह',
        google_sheet_status: 'SYNCED',
        ...payload,
        submitted_at: json.timestamp || payload.submitted_at
      };

      setSubmissionSuccess(record);
      setHistoryList((prev) => [record, ...prev]);
      loadDashboardData();
    } catch (err: any) {
      console.error('API submission error:', err);
      setSubmissionSuccess(null);
      setSubmissionError('नेटवर्क या सर्वर त्रुटि के कारण Attendance Report Google Sheet में दर्ज नहीं हो सकी। कृपया पुनः प्रयास करें।');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper to format school export row
  const formatSchoolExportRow = (s: any, rank: number, metricLabel: string) => ({
    'Rank': rank,
    'Block': s.block,
    'School Category': s.school_type,
    'School Name': s.school_name,
    'School Code': s.school_code,
    'UDISE Code': s.udise_code || '',
    'Selected Period': filterDate !== 'all' ? filterDate : (filterMonth !== 'all' ? filterMonth : 'Academic Year 2026-27'),
    'Metric': metricLabel,
    'Students Enrolled': s.total_enrolled || 0,
    'Students Present': s.total_present || 0,
    'Student Attendance %': s.student_attendance_pct !== null ? `${s.student_attendance_pct}%` : 'N/A',
    'Faculty Working': s.teachers_working || 0,
    'Faculty Present': s.teachers_present || 0,
    'Faculty Attendance %': s.teacher_attendance_pct !== null ? `${s.teacher_attendance_pct}%` : 'N/A',
    'Reporting Status': s.submission_status,
    'Submitter Name': s.submitter_name || 'N/A',
    'Contact Mobile': s.mobile_number || 'N/A'
  });

  // 1. Download Top Schools Excel
  const handleExportTopSchoolsExcel = () => {
    if (!dashboardData || !dashboardData.schools_table) return;
    const n = Math.max(1, rankingN || 10);
    const metricLabel = getMetricLabel(rankingMetric);
    
    let list = dashboardData.schools_table.filter((s: any) => {
      if (rankingCategory !== 'all' && s.school_type !== rankingCategory) return false;
      if (rankingBlock !== 'all' && s.block !== rankingBlock) return false;
      return true;
    });
    list.sort((a: any, b: any) => {
      let valA = a[rankingMetric];
      let valB = b[rankingMetric];
      if (valA === null || valA === undefined) valA = -999999;
      if (valB === null || valB === undefined) valB = -999999;
      if (typeof valA === 'string') return valB.localeCompare(valA);
      return valB - valA;
    });
    const topList = list.slice(0, n);
    const rows = topList.map((s: any, idx: number) => formatSchoolExportRow(s, idx + 1, metricLabel));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Top Schools');
    XLSX.writeFile(wb, 'Daily_Attendance_Top_Schools_2026-27.xlsx');
  };

  // 2. Download Bottom Schools Excel
  const handleExportBottomSchoolsExcel = () => {
    if (!dashboardData || !dashboardData.schools_table) return;
    const n = Math.max(1, rankingN || 10);
    const metricLabel = getMetricLabel(rankingMetric);

    let list = dashboardData.schools_table.filter((s: any) => {
      if (rankingCategory !== 'all' && s.school_type !== rankingCategory) return false;
      if (rankingBlock !== 'all' && s.block !== rankingBlock) return false;
      return true;
    });
    list.sort((a: any, b: any) => {
      let valA = a[rankingMetric];
      let valB = b[rankingMetric];
      if (valA === null || valA === undefined) valA = 999999;
      if (valB === null || valB === undefined) valB = 999999;
      if (typeof valA === 'string') return valA.localeCompare(valB);
      return valA - valB;
    });
    const bottomList = list.slice(0, n);
    const rows = bottomList.map((s: any, idx: number) => formatSchoolExportRow(s, idx + 1, metricLabel));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Bottom Schools');
    XLSX.writeFile(wb, 'Daily_Attendance_Bottom_Schools_2026-27.xlsx');
  };

  // 3. Download Top & Bottom Combined Excel
  const handleExportTopBottomCombinedExcel = () => {
    if (!dashboardData || !dashboardData.schools_table) return;
    const n = Math.max(1, rankingN || 10);
    const metricLabel = getMetricLabel(rankingMetric);

    let baseList = dashboardData.schools_table.filter((s: any) => {
      if (rankingCategory !== 'all' && s.school_type !== rankingCategory) return false;
      if (rankingBlock !== 'all' && s.block !== rankingBlock) return false;
      return true;
    });

    // Top N
    const topSorted = [...baseList].sort((a: any, b: any) => {
      let valA = a[rankingMetric];
      let valB = b[rankingMetric];
      if (valA === null || valA === undefined) valA = -999999;
      if (valB === null || valB === undefined) valB = -999999;
      if (typeof valA === 'string') return valB.localeCompare(valA);
      return valB - valA;
    });
    const topRows = topSorted.slice(0, n).map((s: any, idx: number) => formatSchoolExportRow(s, idx + 1, metricLabel));

    // Bottom N
    const bottomSorted = [...baseList].sort((a: any, b: any) => {
      let valA = a[rankingMetric];
      let valB = b[rankingMetric];
      if (valA === null || valA === undefined) valA = 999999;
      if (valB === null || valB === undefined) valB = 999999;
      if (typeof valA === 'string') return valA.localeCompare(valB);
      return valA - valB;
    });
    const bottomRows = bottomSorted.slice(0, n).map((s: any, idx: number) => formatSchoolExportRow(s, idx + 1, metricLabel));

    // Filter Summary Sheet
    const summaryRows = [
      { 'Parameter': 'Academic Session', 'Value': '2026-27 (01-Apr-2026 to 31-Mar-2027)' },
      { 'Parameter': 'Target Date / Snapshot', 'Value': filterDate !== 'all' ? filterDate : (filterMonth !== 'all' ? filterMonth : 'All Dates') },
      { 'Parameter': 'Selected Metric', 'Value': metricLabel },
      { 'Parameter': 'Ranking Sample Size (N)', 'Value': String(n) },
      { 'Parameter': 'School Category Filter', 'Value': rankingCategory === 'all' ? 'All Categories' : rankingCategory },
      { 'Parameter': 'Administrative Block Filter', 'Value': rankingBlock === 'all' ? 'All 5 Blocks (District-Wide)' : rankingBlock },
      { 'Parameter': 'Total In-Scope Schools', 'Value': String(baseList.length) },
      { 'Parameter': 'Generated By', 'Value': user?.name ? `${user.name} (${user.role?.toUpperCase()})` : 'District Education Officer, Nuh' },
      { 'Parameter': 'Generated Timestamp', 'Value': `${new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST` }
    ];

    const wb = XLSX.utils.book_new();
    const wsTop = XLSX.utils.json_to_sheet(topRows);
    const wsBottom = XLSX.utils.json_to_sheet(bottomRows);
    const wsSummary = XLSX.utils.json_to_sheet(summaryRows);

    XLSX.utils.book_append_sheet(wb, wsTop, 'Top Schools');
    XLSX.utils.book_append_sheet(wb, wsBottom, 'Bottom Schools');
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Filter Summary');

    XLSX.writeFile(wb, 'Daily_Attendance_Top_Bottom_2026-27.xlsx');
  };

  // Master 10-Sheet Excel Export Generator
  const handleGenerate10SheetExcel = (scope: 'selected_date' | 'selected_range' | 'all_dates') => {
    if (!dashboardData || !dashboardData.schools_table) return;
    const schools = dashboardData.schools_table || [];
    const blocks = dashboardData.block_summary || [];
    const dateSummary = dashboardData.date_summary_table || [];

    const wb = XLSX.utils.book_new();

    // 1. Date-wise Summary Sheet (Chronological session date log)
    const sheet1Rows = dateSummary.map((d: any, idx: number) => ({
      'Sr. No.': idx + 1,
      'Attendance Date': d.date,
      'Expected Schools': d.expected_schools || 403,
      'Submitted Schools': d.submitted_schools || 0,
      'Non-Submitted Schools': d.pending_schools || 0,
      'Reporting Compliance %': `${d.reporting_pct}%`,
      'Students Enrolled': d.total_enrolled || 0,
      'Students Present': d.total_present || 0,
      'Students Absent': d.total_absent || 0,
      'Student Attendance %': d.student_attendance_pct !== null ? `${d.student_attendance_pct}%` : 'N/A',
      'Faculty Total Working': d.teachers_working || 0,
      'Faculty Present': d.teachers_present || 0,
      'Faculty Absent': d.teachers_absent || 0,
      'Faculty Attendance %': d.teacher_attendance_pct !== null ? `${d.teacher_attendance_pct}%` : 'N/A'
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheet1Rows), 'Date-wise Summary');

    // Helper for School Status Row (20 Columns)
    const formatStatusRow = (s: any, idx: number) => ({
      'Sr. No.': idx + 1,
      'Attendance Date': s.attendance_date,
      'Block': s.block,
      'School Category': s.school_type,
      'School Name': s.school_name,
      'School Code': s.school_code,
      'UDISE Code': s.udise_code || '',
      'Submission Status': s.submission_status,
      'Submission Timestamp': s.latest_timestamp || 'N/A',
      'Submitter Name': s.submitter_name || 'N/A',
      'Designation': s.designation || 'N/A',
      'Contact Mobile': s.mobile_number || 'N/A',
      'Contact Email': s.email_address || 'N/A',
      'Acknowledgement ID': s.acknowledgement_number || 'N/A',
      'Students Enrolled': s.total_enrolled || 0,
      'Students Present': s.total_present || 0,
      'Students Absent': s.total_absent || 0,
      'Student Attendance %': s.student_attendance_pct !== null ? `${s.student_attendance_pct}%` : 'N/A',
      'Faculty Working': s.teachers_working || 0,
      'Faculty Present': s.teachers_present || 0,
      'Faculty Absent': s.teachers_absent || 0,
      'Faculty Attendance %': s.teacher_attendance_pct !== null ? `${s.teacher_attendance_pct}%` : 'N/A'
    });

    // 2. Submission Status Sheet (All 403 Schools Master Baseline)
    const sheet2Rows = schools.map((s: any, idx: number) => formatStatusRow(s, idx));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheet2Rows), 'Submission Status');

    // 3. Submitted Schools Sheet
    const sheet3Rows = schools.filter((s: any) => s.submission_status === 'SUBMITTED' || s.submission_status === 'REPORTED').map((s: any, idx: number) => formatStatusRow(s, idx));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheet3Rows), 'Submitted Schools');

    // 4. Non-Submitted Schools Sheet
    const sheet4Rows = schools.filter((s: any) => s.submission_status === 'NOT SUBMITTED' || s.submission_status === 'PENDING').map((s: any, idx: number) => formatStatusRow(s, idx));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheet4Rows), 'Non-Submitted Schools');

    // 5. School-wise Attendance Sheet
    const sheet5Rows = schools.map((s: any, idx: number) => ({
      'Sr. No.': idx + 1,
      'School Name': s.school_name,
      'School Code': s.school_code,
      'UDISE Code': s.udise_code || '',
      'Block': s.block,
      'School Category': s.school_type,
      'Attendance Date': s.attendance_date,
      'Submission Status': s.submission_status,
      'Total Students Enrolled': s.total_enrolled || 0,
      'Total Students Present': s.total_present || 0,
      'Total Students Absent': s.total_absent || 0,
      'Student Attendance %': s.student_attendance_pct !== null ? `${s.student_attendance_pct}%` : 'N/A',
      'Faculty Total Working': s.teachers_working || 0,
      'Faculty Present': s.teachers_present || 0,
      'Faculty On Leave': s.teachers_on_leave || 0,
      'Faculty Other Duty': s.teachers_other_duty || 0,
      'Faculty Absent': s.teachers_absent || 0,
      'Faculty Attendance %': s.teacher_attendance_pct !== null ? `${s.teacher_attendance_pct}%` : 'N/A'
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheet5Rows), 'School-wise Attendance');

    // 6. Block-wise Attendance Sheet (5 Blocks + District Total Row)
    const sheet6Rows = blocks.map((b: any, idx: number) => ({
      'Sr. No.': idx + 1,
      'Block Name': b.block,
      'Total Expected Schools': b.total_schools,
      'Submitted Schools': b.reported_schools,
      'Non-Submitted Schools': b.pending_schools,
      'Reporting Compliance %': `${b.reporting_pct}%`,
      'Students Enrolled': b.enrolled || 0,
      'Students Present': b.present || 0,
      'Students Absent': b.total_absent || 0,
      'Student Attendance %': b.student_attendance_pct !== null ? `${b.student_attendance_pct}%` : 'N/A',
      'Faculty Working': b.teachers_working || 0,
      'Faculty Present': b.teachers_present || 0,
      'Faculty Absent': b.teachers_absent || 0,
      'Faculty Attendance %': b.teacher_attendance_pct !== null ? `${b.teacher_attendance_pct}%` : 'N/A',
      'Class 6 Att. %': b.class_6_pct !== null ? `${b.class_6_pct}%` : 'N/A',
      'Class 7 Att. %': b.class_7_pct !== null ? `${b.class_7_pct}%` : 'N/A',
      'Class 8 Att. %': b.class_8_pct !== null ? `${b.class_8_pct}%` : 'N/A',
      'Class 9 Att. %': b.class_9_pct !== null ? `${b.class_9_pct}%` : 'N/A',
      'Class 10 Att. %': b.class_10_pct !== null ? `${b.class_10_pct}%` : 'N/A',
      'Class 11 Att. %': b.class_11_pct !== null ? `${b.class_11_pct}%` : 'N/A',
      'Class 12 Att. %': b.class_12_pct !== null ? `${b.class_12_pct}%` : 'N/A'
    }));
    // District Total Row
    const distExpected = blocks.reduce((acc: number, b: any) => acc + (b.total_schools || 0), 0) || 403;
    const distReported = blocks.reduce((acc: number, b: any) => acc + (b.reported_schools || 0), 0);
    const distPending = Math.max(0, distExpected - distReported);
    const distEnr = blocks.reduce((acc: number, b: any) => acc + (b.enrolled || 0), 0);
    const distPres = blocks.reduce((acc: number, b: any) => acc + (b.present || 0), 0);
    const distAbs = Math.max(0, distEnr - distPres);
    const distTWork = blocks.reduce((acc: number, b: any) => acc + (b.teachers_working || 0), 0);
    const distTPres = blocks.reduce((acc: number, b: any) => acc + (b.teachers_present || 0), 0);
    const distTAbs = Math.max(0, distTWork - distTPres);
    sheet6Rows.push({
      'Sr. No.': 'TOTAL',
      'Block Name': 'District Total (All 5 Blocks)',
      'Total Expected Schools': distExpected,
      'Submitted Schools': distReported,
      'Non-Submitted Schools': distPending,
      'Reporting Compliance %': `${distExpected > 0 ? ((distReported / distExpected) * 100).toFixed(1) : 0}%`,
      'Students Enrolled': distEnr,
      'Students Present': distPres,
      'Students Absent': distAbs,
      'Student Attendance %': distEnr > 0 ? `${((distPres / distEnr) * 100).toFixed(1)}%` : 'N/A',
      'Faculty Working': distTWork,
      'Faculty Present': distTPres,
      'Faculty Absent': distTAbs,
      'Faculty Attendance %': distTWork > 0 ? `${((distTPres / distTWork) * 100).toFixed(1)}%` : 'N/A',
      'Class 6 Att. %': dashboardData.kpis?.classes?.find((c: any) => c.className === 'Class 6')?.attendancePct !== null ? `${dashboardData.kpis?.classes?.find((c: any) => c.className === 'Class 6')?.attendancePct}%` : 'N/A',
      'Class 7 Att. %': dashboardData.kpis?.classes?.find((c: any) => c.className === 'Class 7')?.attendancePct !== null ? `${dashboardData.kpis?.classes?.find((c: any) => c.className === 'Class 7')?.attendancePct}%` : 'N/A',
      'Class 8 Att. %': dashboardData.kpis?.classes?.find((c: any) => c.className === 'Class 8')?.attendancePct !== null ? `${dashboardData.kpis?.classes?.find((c: any) => c.className === 'Class 8')?.attendancePct}%` : 'N/A',
      'Class 9 Att. %': dashboardData.kpis?.classes?.find((c: any) => c.className === 'Class 9')?.attendancePct !== null ? `${dashboardData.kpis?.classes?.find((c: any) => c.className === 'Class 9')?.attendancePct}%` : 'N/A',
      'Class 10 Att. %': dashboardData.kpis?.classes?.find((c: any) => c.className === 'Class 10')?.attendancePct !== null ? `${dashboardData.kpis?.classes?.find((c: any) => c.className === 'Class 10')?.attendancePct}%` : 'N/A',
      'Class 11 Att. %': dashboardData.kpis?.classes?.find((c: any) => c.className === 'Class 11')?.attendancePct !== null ? `${dashboardData.kpis?.classes?.find((c: any) => c.className === 'Class 11')?.attendancePct}%` : 'N/A',
      'Class 12 Att. %': dashboardData.kpis?.classes?.find((c: any) => c.className === 'Class 12')?.attendancePct !== null ? `${dashboardData.kpis?.classes?.find((c: any) => c.className === 'Class 12')?.attendancePct}%` : 'N/A'
    });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheet6Rows), 'Block-wise Attendance');

    // 7. Classwise Attendance Sheet (Classes 6 to 12)
    const sheet7Rows = schools.map((s: any, idx: number) => ({
      'Sr. No.': idx + 1,
      'School Name': s.school_name,
      'School Code': s.school_code,
      'UDISE Code': s.udise_code || '',
      'Block': s.block,
      'School Category': s.school_type,
      'Attendance Date': s.attendance_date,
      'Class 6 Boys Enrolled': s.classes?.class_6?.boysEnrolled ?? '',
      'Class 6 Boys Present': s.classes?.class_6?.boysPresent ?? '',
      'Class 6 Girls Enrolled': s.classes?.class_6?.girlsEnrolled ?? '',
      'Class 6 Girls Present': s.classes?.class_6?.girlsPresent ?? '',
      'Class 6 Total Enrolled': s.classes?.class_6?.enrolled ?? '',
      'Class 6 Total Present': s.classes?.class_6?.present ?? '',
      'Class 6 Total Absent': s.classes?.class_6?.absent ?? '',
      'Class 6 Attendance %': s.classes?.class_6?.pct !== undefined && s.classes?.class_6?.pct !== null ? `${s.classes?.class_6?.pct}%` : '',
      'Class 7 Boys Enrolled': s.classes?.class_7?.boysEnrolled ?? '',
      'Class 7 Boys Present': s.classes?.class_7?.boysPresent ?? '',
      'Class 7 Girls Enrolled': s.classes?.class_7?.girlsEnrolled ?? '',
      'Class 7 Girls Present': s.classes?.class_7?.girlsPresent ?? '',
      'Class 7 Total Enrolled': s.classes?.class_7?.enrolled ?? '',
      'Class 7 Total Present': s.classes?.class_7?.present ?? '',
      'Class 7 Total Absent': s.classes?.class_7?.absent ?? '',
      'Class 7 Attendance %': s.classes?.class_7?.pct !== undefined && s.classes?.class_7?.pct !== null ? `${s.classes?.class_7?.pct}%` : '',
      'Class 8 Boys Enrolled': s.classes?.class_8?.boysEnrolled ?? '',
      'Class 8 Boys Present': s.classes?.class_8?.boysPresent ?? '',
      'Class 8 Girls Enrolled': s.classes?.class_8?.girlsEnrolled ?? '',
      'Class 8 Girls Present': s.classes?.class_8?.girlsPresent ?? '',
      'Class 8 Total Enrolled': s.classes?.class_8?.enrolled ?? '',
      'Class 8 Total Present': s.classes?.class_8?.present ?? '',
      'Class 8 Total Absent': s.classes?.class_8?.absent ?? '',
      'Class 8 Attendance %': s.classes?.class_8?.pct !== undefined && s.classes?.class_8?.pct !== null ? `${s.classes?.class_8?.pct}%` : '',
      'Class 9 Boys Enrolled': s.classes?.class_9?.boysEnrolled ?? '',
      'Class 9 Boys Present': s.classes?.class_9?.boysPresent ?? '',
      'Class 9 Girls Enrolled': s.classes?.class_9?.girlsEnrolled ?? '',
      'Class 9 Girls Present': s.classes?.class_9?.girlsPresent ?? '',
      'Class 9 Total Enrolled': s.classes?.class_9?.enrolled ?? '',
      'Class 9 Total Present': s.classes?.class_9?.present ?? '',
      'Class 9 Total Absent': s.classes?.class_9?.absent ?? '',
      'Class 9 Attendance %': s.classes?.class_9?.pct !== undefined && s.classes?.class_9?.pct !== null ? `${s.classes?.class_9?.pct}%` : '',
      'Class 10 Boys Enrolled': s.classes?.class_10?.boysEnrolled ?? '',
      'Class 10 Boys Present': s.classes?.class_10?.boysPresent ?? '',
      'Class 10 Girls Enrolled': s.classes?.class_10?.girlsEnrolled ?? '',
      'Class 10 Girls Present': s.classes?.class_10?.girlsPresent ?? '',
      'Class 10 Total Enrolled': s.classes?.class_10?.enrolled ?? '',
      'Class 10 Total Present': s.classes?.class_10?.present ?? '',
      'Class 10 Total Absent': s.classes?.class_10?.absent ?? '',
      'Class 10 Attendance %': s.classes?.class_10?.pct !== undefined && s.classes?.class_10?.pct !== null ? `${s.classes?.class_10?.pct}%` : '',
      'Class 11 Boys Enrolled': s.classes?.class_11?.boysEnrolled ?? '',
      'Class 11 Boys Present': s.classes?.class_11?.boysPresent ?? '',
      'Class 11 Girls Enrolled': s.classes?.class_11?.girlsEnrolled ?? '',
      'Class 11 Girls Present': s.classes?.class_11?.girlsPresent ?? '',
      'Class 11 Total Enrolled': s.classes?.class_11?.enrolled ?? '',
      'Class 11 Total Present': s.classes?.class_11?.present ?? '',
      'Class 11 Total Absent': s.classes?.class_11?.absent ?? '',
      'Class 11 Attendance %': s.classes?.class_11?.pct !== undefined && s.classes?.class_11?.pct !== null ? `${s.classes?.class_11?.pct}%` : '',
      'Class 12 Boys Enrolled': s.classes?.class_12?.boysEnrolled ?? '',
      'Class 12 Boys Present': s.classes?.class_12?.boysPresent ?? '',
      'Class 12 Girls Enrolled': s.classes?.class_12?.girlsEnrolled ?? '',
      'Class 12 Girls Present': s.classes?.class_12?.girlsPresent ?? '',
      'Class 12 Total Enrolled': s.classes?.class_12?.enrolled ?? '',
      'Class 12 Total Present': s.classes?.class_12?.present ?? '',
      'Class 12 Total Absent': s.classes?.class_12?.absent ?? '',
      'Class 12 Attendance %': s.classes?.class_12?.pct !== undefined && s.classes?.class_12?.pct !== null ? `${s.classes?.class_12?.pct}%` : '',
      'Total Students Enrolled': s.total_enrolled || 0,
      'Total Students Present': s.total_present || 0,
      'Total Students Absent': s.total_absent || 0,
      'Overall Attendance %': s.student_attendance_pct !== null ? `${s.student_attendance_pct}%` : 'N/A'
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheet7Rows), 'Classwise Attendance');

    // 8. Teacher Attendance Sheet
    const sheet8Rows = schools.map((s: any, idx: number) => ({
      'Sr. No.': idx + 1,
      'School Name': s.school_name,
      'School Code': s.school_code,
      'UDISE Code': s.udise_code || '',
      'Block': s.block,
      'Category': s.school_type,
      'Faculty Total Working': s.teachers_working || 0,
      'Faculty Present': s.teachers_present || 0,
      'Faculty On Leave': s.teachers_on_leave || 0,
      'Faculty Other Duty': s.teachers_other_duty || 0,
      'Faculty Absent': s.teachers_absent || 0,
      'Faculty Attendance %': s.teacher_attendance_pct !== null ? `${s.teacher_attendance_pct}%` : 'N/A',
      'Submitter Name': s.submitter_name || 'N/A',
      'Designation': s.designation || 'N/A',
      'Contact Mobile': s.mobile_number || 'N/A'
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheet8Rows), 'Teacher Attendance');

    // 9. Top Schools Sheet (Sorted by Student Attendance %)
    const reportedSchools = schools.filter((s: any) => (s.submission_status === 'SUBMITTED' || s.submission_status === 'REPORTED') && typeof s.student_attendance_pct === 'number');
    const topSchoolsSorted = [...reportedSchools].sort((a: any, b: any) => (b.student_attendance_pct || 0) - (a.student_attendance_pct || 0));
    const sheet9Rows = topSchoolsSorted.slice(0, 25).map((s: any, idx: number) => ({
      'Rank': idx + 1,
      'School Name': s.school_name,
      'School Code': s.school_code,
      'UDISE Code': s.udise_code || '',
      'Block': s.block,
      'Category': s.school_type,
      'Student Attendance %': `${s.student_attendance_pct}%`,
      'Students Present': s.total_present || 0,
      'Students Enrolled': s.total_enrolled || 0,
      'Faculty Attendance %': s.teacher_attendance_pct !== null ? `${s.teacher_attendance_pct}%` : 'N/A',
      'Faculty Present / Working': `${s.teachers_present || 0} / ${s.teachers_working || 0}`,
      'Submitter Name': s.submitter_name || 'N/A',
      'Contact Mobile': s.mobile_number || 'N/A'
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheet9Rows), 'Top Schools');

    // 10. Bottom Schools Sheet
    const bottomSchoolsSorted = [...reportedSchools].sort((a: any, b: any) => (a.student_attendance_pct || 0) - (b.student_attendance_pct || 0));
    const sheet10Rows = bottomSchoolsSorted.slice(0, 25).map((s: any, idx: number) => ({
      'Rank': idx + 1,
      'School Name': s.school_name,
      'School Code': s.school_code,
      'UDISE Code': s.udise_code || '',
      'Block': s.block,
      'Category': s.school_type,
      'Student Attendance %': `${s.student_attendance_pct}%`,
      'Students Present': s.total_present || 0,
      'Students Enrolled': s.total_enrolled || 0,
      'Faculty Attendance %': s.teacher_attendance_pct !== null ? `${s.teacher_attendance_pct}%` : 'N/A',
      'Faculty Present / Working': `${s.teachers_present || 0} / ${s.teachers_working || 0}`,
      'Submitter Name': s.submitter_name || 'N/A',
      'Contact Mobile': s.mobile_number || 'N/A'
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheet10Rows), 'Bottom Schools');

    // File name determination
    let fileName = 'Daily_Attendance_Consolidated_All_Dates_2026-27.xlsx';
    if (scope === 'selected_date') {
      fileName = `Daily_Attendance_Snapshot_${filterDate !== 'all' ? filterDate : 'Single_Date'}_2026-27.xlsx`;
    } else if (scope === 'selected_range') {
      fileName = `Daily_Attendance_Range_${filterFromDate || 'Start'}_to_${filterToDate || 'End'}_2026-27.xlsx`;
    }

    XLSX.writeFile(wb, fileName);
  };

  // Dedicated 10-Sheet Download Handlers
  const handleExportSelectedDate10SheetExcel = () => handleGenerate10SheetExcel('selected_date');
  const handleExportDateRange10SheetExcel = () => handleGenerate10SheetExcel('selected_range');
  const handleExportAllAvailableDates10SheetExcel = () => handleGenerate10SheetExcel('all_dates');

  // Backwards compatible export triggers
  const handleExportSubmissionStatusExcel = () => handleGenerate10SheetExcel('selected_date');
  const handleExportClasswiseAttendanceExcel = () => handleGenerate10SheetExcel('selected_date');
  const handleExportAllDatesConsolidatedExcel = () => handleGenerate10SheetExcel('all_dates');

  // Excel Export Feature (Complete Master)
  const handleExportExcel = () => {
    if (!dashboardData || !dashboardData.schools_table || dashboardData.schools_table.length === 0) return;

    const dataRows = dashboardData.schools_table.map((s: any) => ({
      'Block': s.block,
      'School Category': s.school_type,
      'School Name': s.school_name,
      'School Code': s.school_code,
      'UDISE Code': s.udise_code,
      'Attendance Date': s.attendance_date,
      'Submission Status': s.submission_status,
      'Submission Count': s.submission_count || 1,
      'Class 6 Present': s.classes?.class_6?.present ?? '',
      'Class 6 Enrolled': s.classes?.class_6?.enrolled ?? '',
      'Class 7 Present': s.classes?.class_7?.present ?? '',
      'Class 7 Enrolled': s.classes?.class_7?.enrolled ?? '',
      'Class 8 Present': s.classes?.class_8?.present ?? '',
      'Class 8 Enrolled': s.classes?.class_8?.enrolled ?? '',
      'Class 9 Present': s.classes?.class_9?.present ?? '',
      'Class 9 Enrolled': s.classes?.class_9?.enrolled ?? '',
      'Class 10 Present': s.classes?.class_10?.present ?? '',
      'Class 10 Enrolled': s.classes?.class_10?.enrolled ?? '',
      'Class 11 Present': s.classes?.class_11?.present ?? '',
      'Class 11 Enrolled': s.classes?.class_11?.enrolled ?? '',
      'Class 12 Present': s.classes?.class_12?.present ?? '',
      'Class 12 Enrolled': s.classes?.class_12?.enrolled ?? '',
      'Total Students Present': s.total_present,
      'Total Students Enrolled': s.total_enrolled,
      'Student Attendance %': s.student_attendance_pct !== null ? `${s.student_attendance_pct}%` : 'N/A',
      'Teachers Present': s.teachers_present,
      'Teachers Total Working': s.teachers_working,
      'Teacher Attendance %': s.teacher_attendance_pct !== null ? `${s.teacher_attendance_pct}%` : 'N/A',
      'Submitter Name': s.submitter_name,
      'Designation': s.designation,
      'Contact Mobile': s.mobile_number,
      'Submission Timestamp': s.latest_timestamp || ''
    }));

    const ws = XLSX.utils.json_to_sheet(dataRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Daily Attendance');
    XLSX.writeFile(wb, `Daily_Attendance_DEO_Report_Nuh_${filterDate || 'All'}.xlsx`);
  };

  // Word Report (.doc / .docx compatible) Export Feature
  const handleExportWordReport = () => {
    if (!dashboardData) return;
    const kpis = dashboardData.kpis || {};
    const cats = dashboardData.category_kpis || [];
    const blocks = dashboardData.block_summary || [];

    const htmlContent = `
      <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
      <head>
        <title>Daily Attendance Monitoring - DEO Report</title>
        <style>
          body { font-family: 'Calibri', 'Arial', sans-serif; font-size: 11pt; color: #111; line-height: 1.4; }
          .header { text-align: center; border-bottom: 2pt solid #0B1E38; padding-bottom: 8pt; margin-bottom: 15pt; }
          .title { font-size: 16pt; font-weight: bold; color: #0B1E38; margin: 0; }
          .subtitle { font-size: 12pt; font-weight: bold; color: #B45309; margin: 2pt 0; }
          .dept { font-size: 10pt; color: #555; text-transform: uppercase; letter-spacing: 1pt; }
          .meta-box { background: #f8fafc; border: 1pt solid #cbd5e1; padding: 8pt; margin-bottom: 15pt; font-size: 10pt; }
          h2 { font-size: 13pt; color: #0B1E38; border-bottom: 1pt solid #cbd5e1; padding-bottom: 3pt; margin-top: 15pt; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 15pt; font-size: 10pt; }
          th { background: #0B1E38; color: #ffffff; padding: 6pt 8pt; text-align: left; font-weight: bold; }
          td { border: 1pt solid #cbd5e1; padding: 5pt 8pt; }
          tr:nth-child(even) { background: #f8fafc; }
          .num { text-align: right; font-family: 'Consolas', monospace; }
          .badge { font-weight: bold; color: #047857; }
          .footer { text-align: center; font-size: 9pt; color: #777; margin-top: 25pt; border-top: 1pt solid #cbd5e1; padding-top: 5pt; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="dept">Government of Haryana • Department of School Education</div>
          <div class="title">DISTRICT EDUCATION OFFICE, NUH</div>
          <div class="subtitle">DAILY ATTENDANCE MONITORING REPORT (CLASSES 6–12)</div>
          <div style="font-size: 9pt; color: #64748b;">Middle, High & Senior Secondary Schools Attendance Review</div>
        </div>

        <div class="meta-box">
          <strong>Report Scope & Parameters:</strong><br/>
          • <strong>Snapshot Date / Range:</strong> ${filterDate !== 'all' ? filterDate : (filterFromDate && filterToDate ? `${filterFromDate} to ${filterToDate}` : 'All Historical Records')}<br/>
          • <strong>Selected Block:</strong> ${filterBlock !== 'all' ? `${filterBlock} Block` : 'All 5 Blocks (District-Wide)'}<br/>
          • <strong>Selected School Category:</strong> ${filterCategory !== 'all' ? filterCategory : 'All School Types (Middle, High, Sr Sec)'}<br/>
          • <strong>Selected School:</strong> ${filterSchoolCode !== 'all' ? (dashboardAvailableSchools.find(s => s.school_code === filterSchoolCode)?.school_name || filterSchoolCode) : 'All Schools in Scope'}<br/>
          • <strong>Report Generated On:</strong> ${new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST
        </div>

        <h2>1. District Executive KPI Summary</h2>
        <table>
          <tr>
            <th>Metric Dimension</th>
            <th class="num">Reported Value</th>
            <th>Metric Dimension</th>
            <th class="num">Reported Value</th>
          </tr>
          <tr>
            <td><strong>Total Schools in Scope</strong></td>
            <td class="num">${kpis.total_schools_in_scope || 0}</td>
            <td><strong>Total Students Enrolled</strong></td>
            <td class="num">${(kpis.total_students_enrolled || 0).toLocaleString()}</td>
          </tr>
          <tr>
            <td><strong>Schools Reported</strong></td>
            <td class="num">${kpis.reported_schools || 0}</td>
            <td><strong>Total Students Present</strong></td>
            <td class="num">${(kpis.total_students_present || 0).toLocaleString()}</td>
          </tr>
          <tr>
            <td><strong>Schools Pending</strong></td>
            <td class="num">${kpis.pending_schools || 0}</td>
            <td><strong>Student Attendance %</strong></td>
            <td class="num badge">${kpis.student_attendance_pct !== null ? `${kpis.student_attendance_pct}%` : 'N/A'}</td>
          </tr>
          <tr>
            <td><strong>Reporting Compliance %</strong></td>
            <td class="num badge">${kpis.reporting_pct}%</td>
            <td><strong>Faculty Attendance %</strong></td>
            <td class="num badge">${kpis.teacher_attendance_pct !== null ? `${kpis.teacher_attendance_pct}%` : 'N/A'}</td>
          </tr>
          <tr>
            <td><strong>Total Faculty Working</strong></td>
            <td class="num">${(kpis.total_teachers_working || 0).toLocaleString()}</td>
            <td><strong>Faculty Present on Duty</strong></td>
            <td class="num">${(kpis.total_teachers_present || 0).toLocaleString()}</td>
          </tr>
        </table>

        <h2>2. School Category-Wise Performance Matrix</h2>
        <table>
          <thead>
            <tr>
              <th>School Category</th>
              <th class="num">Total Schools</th>
              <th class="num">Reported</th>
              <th class="num">Pending</th>
              <th class="num">Reporting %</th>
              <th class="num">Enrolled Students</th>
              <th class="num">Present Students</th>
              <th class="num">Student Att. %</th>
              <th class="num">Faculty Att. %</th>
            </tr>
          </thead>
          <tbody>
            ${cats.map((c: any) => `
              <tr>
                <td><strong>${c.category}</strong></td>
                <td class="num">${c.total_schools}</td>
                <td class="num">${c.reported_schools}</td>
                <td class="num">${c.pending_schools}</td>
                <td class="num">${c.reporting_pct}%</td>
                <td class="num">${(c.enrolled || 0).toLocaleString()}</td>
                <td class="num">${(c.present || 0).toLocaleString()}</td>
                <td class="num"><strong>${c.student_attendance_pct !== null ? `${c.student_attendance_pct}%` : 'N/A'}</strong></td>
                <td class="num"><strong>${c.teacher_attendance_pct !== null ? `${c.teacher_attendance_pct}%` : 'N/A'}</strong></td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <h2>3. Block-Wise Attendance Summary</h2>
        <table>
          <thead>
            <tr>
              <th>Block Name</th>
              <th class="num">Schools</th>
              <th class="num">Reported</th>
              <th class="num">Pending</th>
              <th class="num">Reporting %</th>
              <th class="num">Students Present / Enrolled</th>
              <th class="num">Student Att. %</th>
              <th class="num">Faculty Present / Total</th>
              <th class="num">Faculty Att. %</th>
            </tr>
          </thead>
          <tbody>
            ${blocks.map((b: any) => `
              <tr>
                <td><strong>${b.block}</strong></td>
                <td class="num">${b.total_schools}</td>
                <td class="num">${b.reported_schools}</td>
                <td class="num">${b.pending_schools}</td>
                <td class="num">${b.reporting_pct}%</td>
                <td class="num">${(b.present || 0).toLocaleString()} / ${(b.enrolled || 0).toLocaleString()}</td>
                <td class="num badge">${b.student_attendance_pct !== null ? `${b.student_attendance_pct}%` : 'N/A'}</td>
                <td class="num">${(b.teachers_present || 0).toLocaleString()} / ${(b.teachers_working || 0).toLocaleString()}</td>
                <td class="num badge">${b.teacher_attendance_pct !== null ? `${b.teacher_attendance_pct}%` : 'N/A'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="footer">
          Official Government Dossier • Daily Attendance Monitoring System • District Education Office, Nuh • Department of School Education, Haryana
        </div>
      </body>
      </html>
    `;

    const blob = new Blob(['\ufeff', htmlContent], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Daily_Attendance_DEO_Report_Nuh_${filterDate || 'All'}.doc`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Print Handler
  const handlePrintReport = () => {
    window.print();
  };

  // Sorted School Rows for Table
  const sortedSchoolRows = useMemo(() => {
    if (!dashboardData || !dashboardData.schools_table) return [];
    return [...dashboardData.schools_table].sort((a: any, b: any) => {
      let valA = a[schoolTableSortField];
      let valB = b[schoolTableSortField];

      if (valA === null || valA === undefined) valA = -1;
      if (valB === null || valB === undefined) valB = -1;

      if (typeof valA === 'string') {
        return schoolTableSortAsc ? valA.localeCompare(valB) : valB.localeCompare(valA);
      }
      return schoolTableSortAsc ? valA - valB : valB - valA;
    });
  }, [dashboardData, schoolTableSortField, schoolTableSortAsc]);

  const handleSort = (field: string) => {
    if (schoolTableSortField === field) {
      setSchoolTableSortAsc(!schoolTableSortAsc);
    } else {
      setSchoolTableSortField(field);
      setSchoolTableSortAsc(true);
    }
  };

  // Table 1: School Submission Status Filtered & Paginated
  const filteredStatusRows = useMemo(() => {
    if (!sortedSchoolRows) return [];
    return sortedSchoolRows.filter((s: any) => {
      const isSub = s.submission_status === 'SUBMITTED' || s.submission_status === 'REPORTED';
      if (submissionStatusTab === 'submitted' && !isSub) return false;
      if (submissionStatusTab === 'not_submitted' && isSub) return false;
      if (statusTableSearch) {
        const q = statusTableSearch.toLowerCase();
        const sname = (s.school_name || '').toLowerCase();
        const scode = String(s.school_code || '').toLowerCase();
        const sudis = String(s.udise_code || '').toLowerCase();
        const sblk = (s.block || '').toLowerCase();
        const ssub = (s.submitter_name || '').toLowerCase();
        if (!sname.includes(q) && !scode.includes(q) && !sudis.includes(q) && !sblk.includes(q) && !ssub.includes(q)) return false;
      }
      return true;
    });
  }, [sortedSchoolRows, submissionStatusTab, statusTableSearch]);

  const paginatedStatusRows = useMemo(() => {
    if (statusTablePageSize === -1) return filteredStatusRows;
    const start = (statusTablePage - 1) * statusTablePageSize;
    return filteredStatusRows.slice(start, start + statusTablePageSize);
  }, [filteredStatusRows, statusTablePage, statusTablePageSize]);

  const totalStatusPages = useMemo(() => {
    if (statusTablePageSize === -1) return 1;
    return Math.max(1, Math.ceil(filteredStatusRows.length / statusTablePageSize));
  }, [filteredStatusRows, statusTablePageSize]);

  // Table 2: School & Block-wise Classwise Filtered & Paginated
  const filteredClasswiseRows = useMemo(() => {
    if (!sortedSchoolRows) return [];
    return sortedSchoolRows.filter((s: any) => {
      if (classwiseBlockFilter !== 'all' && s.block !== classwiseBlockFilter) return false;
      if (classwiseCategoryFilter !== 'all' && s.school_type !== classwiseCategoryFilter) return false;
      if (classwiseTableSearch) {
        const q = classwiseTableSearch.toLowerCase();
        const sname = (s.school_name || '').toLowerCase();
        const scode = String(s.school_code || '').toLowerCase();
        const sudis = String(s.udise_code || '').toLowerCase();
        if (!sname.includes(q) && !scode.includes(q) && !sudis.includes(q)) return false;
      }
      return true;
    });
  }, [sortedSchoolRows, classwiseBlockFilter, classwiseCategoryFilter, classwiseTableSearch]);

  const paginatedClasswiseRows = useMemo(() => {
    if (classwiseTablePageSize === -1) return filteredClasswiseRows;
    const start = (classwiseTablePage - 1) * classwiseTablePageSize;
    return filteredClasswiseRows.slice(start, start + classwiseTablePageSize);
  }, [filteredClasswiseRows, classwiseTablePage, classwiseTablePageSize]);

  const totalClasswisePages = useMemo(() => {
    if (classwiseTablePageSize === -1) return 1;
    return Math.max(1, Math.ceil(filteredClasswiseRows.length / classwiseTablePageSize));
  }, [filteredClasswiseRows, classwiseTablePageSize]);

  // Table 4: Date-wise Attendance Summary Filtered & Paginated
  const filteredDateRows = useMemo(() => {
    if (!dashboardData || !dashboardData.date_summary_table) return [];
    return dashboardData.date_summary_table.filter((d: any) => {
      if (dateSummarySearch) {
        const q = dateSummarySearch.toLowerCase();
        return d.date.toLowerCase().includes(q) || formatDisplayDate(d.date).toLowerCase().includes(q);
      }
      return true;
    });
  }, [dashboardData, dateSummarySearch]);

  const paginatedDateRows = useMemo(() => {
    if (dateSummaryPageSize === -1) return filteredDateRows;
    const start = (dateSummaryPage - 1) * dateSummaryPageSize;
    return filteredDateRows.slice(start, start + dateSummaryPageSize);
  }, [filteredDateRows, dateSummaryPage, dateSummaryPageSize]);

  const totalDatePages = useMemo(() => {
    if (dateSummaryPageSize === -1) return 1;
    return Math.max(1, Math.ceil(filteredDateRows.length / dateSummaryPageSize));
  }, [filteredDateRows, dateSummaryPageSize]);

  // Metric label helper for rankings
  const getMetricLabel = (m: string) => {
    switch (m) {
      case 'student_attendance_pct': return 'Student Attendance %';
      case 'teacher_attendance_pct': return 'Teacher Attendance %';
      case 'total_present': return 'Student Present Count';
      case 'teachers_present': return 'Teacher Present Count';
      case 'submission_status': return 'Reporting Status';
      default: return 'Student Attendance %';
    }
  };

  // Date Quick Filter Handler
  const applyQuickFilter = (type: 'today' | 'yesterday' | 'this_week' | 'this_month' | 'prev_month' | 'full_year') => {
    const now = new Date();
    const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
    const ist = new Date(utc + (3600000 * 5.5));
    const todayStr = ist.toISOString().split('T')[0];

    const yestDate = new Date(ist.getTime() - 86400000);
    const yestStr = yestDate.toISOString().split('T')[0];

    const dayOfWeek = ist.getDay(); // 0 is Sunday, 1 is Monday
    const diffToMonday = (dayOfWeek === 0 ? -6 : 1) - dayOfWeek;
    const weekStart = new Date(ist.getTime() + diffToMonday * 86400000);
    const weekStartStr = weekStart.toISOString().split('T')[0];

    const year = ist.getFullYear();
    const month = String(ist.getMonth() + 1).padStart(2, '0');
    const monthStartStr = `${year}-${month}-01`;
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const currentMonthLabel = `${monthNames[ist.getMonth()]} ${year}`;

    const prevMonthDate = new Date(ist.getFullYear(), ist.getMonth() - 1, 1);
    const prevMonthEnd = new Date(ist.getFullYear(), ist.getMonth(), 0);
    const prevMonthStartStr = `${prevMonthDate.getFullYear()}-${String(prevMonthDate.getMonth() + 1).padStart(2, '0')}-01`;
    const prevMonthEndStr = `${prevMonthEnd.getFullYear()}-${String(prevMonthEnd.getMonth() + 1).padStart(2, '0')}-${String(prevMonthEnd.getDate()).padStart(2, '0')}`;
    const prevMonthLabel = `${monthNames[prevMonthDate.getMonth()]} ${prevMonthDate.getFullYear()}`;

    if (type === 'today') {
      setFilterDate(todayStr);
      setFilterFromDate('');
      setFilterToDate('');
      setFilterMonth('all');
      setDateFilterMode('single');
    } else if (type === 'yesterday') {
      setFilterDate(yestStr);
      setFilterFromDate('');
      setFilterToDate('');
      setFilterMonth('all');
      setDateFilterMode('single');
    } else if (type === 'this_week') {
      setFilterDate('all');
      setFilterFromDate(weekStartStr);
      setFilterToDate(todayStr);
      setFilterMonth('all');
      setDateFilterMode('range');
    } else if (type === 'this_month') {
      setFilterDate('all');
      setFilterFromDate(monthStartStr);
      setFilterToDate(todayStr);
      setFilterMonth(currentMonthLabel);
      setDateFilterMode('range');
    } else if (type === 'prev_month') {
      setFilterDate('all');
      setFilterFromDate(prevMonthStartStr);
      setFilterToDate(prevMonthEndStr);
      setFilterMonth(prevMonthLabel);
      setDateFilterMode('range');
    } else if (type === 'full_year') {
      setFilterDate('all');
      setFilterFromDate('2026-04-01');
      setFilterToDate('2027-03-31');
      setFilterMonth('all');
      setDateFilterMode('range');
    }
  };

  // Dedicated "School Performance Ranking" Calculation (Dynamic analytical ranking)
  const rankedSchools = useMemo(() => {
    if (!dashboardData || !dashboardData.schools_table || dashboardData.schools_table.length === 0) return [];
    
    // Filter by ranking category and ranking block
    let list = dashboardData.schools_table.filter((s: any) => {
      if (rankingCategory !== 'all' && s.school_type !== rankingCategory) return false;
      if (rankingBlock !== 'all' && s.block !== rankingBlock) return false;
      return true;
    });

    // Sort by selected rankingMetric
    list.sort((a: any, b: any) => {
      let valA = a[rankingMetric];
      let valB = b[rankingMetric];

      // Handle nulls
      if (valA === null || valA === undefined) valA = rankingDirection === 'top' ? -999999 : 999999;
      if (valB === null || valB === undefined) valB = rankingDirection === 'top' ? -999999 : 999999;

      if (typeof valA === 'string') {
        return rankingDirection === 'top' ? valB.localeCompare(valA) : valA.localeCompare(valB);
      }
      return rankingDirection === 'top' ? valB - valA : valA - valB;
    });

    const count = Math.max(1, rankingN || 10);
    return list.slice(0, count);
  }, [dashboardData, rankingCategory, rankingBlock, rankingMetric, rankingDirection, rankingN]);

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900 flex flex-col font-sans selection:bg-blue-600 selection:text-white">
      {/* ---------------------------------------------------- */}
      {/* GOVERNMENT HEADER & BRANDING                         */}
      {/* ---------------------------------------------------- */}
      <header className="bg-[#0B1E38] text-white border-b-4 border-amber-500 shadow-md print:bg-white print:text-black print:border-b-2">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            {/* Left: Official Haryana Government Logo */}
            <div className="flex items-center space-x-3.5 shrink-0">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white rounded-xl flex items-center justify-center p-1.5 shadow-md border-2 border-amber-400 shrink-0">
                <img
                  src="/haryana_logo.svg"
                  alt="Government of Haryana"
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    if (!target.src.includes('upload.wikimedia.org')) {
                      target.src = 'https://upload.wikimedia.org/wikipedia/commons/thumb/3/36/Emblem_of_Haryana.svg/240px-Emblem_of_Haryana.svg.png';
                    }
                  }}
                />
              </div>
            </div>

            {/* Center: Official Department & District Office Titles */}
            <div className="text-left flex-1 space-y-0.5">
              <div className="text-xs sm:text-sm uppercase tracking-wider text-amber-400 font-bold">
                Department of School Education, Haryana
              </div>
              <div className="text-xs sm:text-sm text-slate-200 font-bold tracking-wide">
                District Education Office, Nuh
              </div>
              <h1 className="text-base sm:text-lg md:text-xl font-black tracking-tight text-white flex items-center gap-2">
                <span>DAILY ATTENDANCE MONITORING</span>
                <span className="text-xs font-semibold bg-blue-700 text-white px-2 py-0.5 rounded tracking-normal border border-blue-400">
                  Classes 6–12
                </span>
              </h1>
            </div>

            {/* Right Action Buttons */}
            <div className="flex items-center space-x-2 print:hidden shrink-0">
              {isUnlocked && (
                <button
                  onClick={handleLockSession}
                  className="px-3 py-1.5 bg-rose-900/90 hover:bg-rose-800 text-rose-100 border border-rose-500/60 rounded-lg text-xs font-bold transition flex items-center space-x-1.5 shadow-xs cursor-pointer"
                  title="Lock Session"
                >
                  <Lock className="w-3.5 h-3.5 text-rose-300" />
                  <span>Lock Session</span>
                </button>
              )}

              {onBackToPortal ? (
                <button
                  onClick={onBackToPortal}
                  className="px-3.5 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-600 rounded-lg text-xs font-bold transition flex items-center space-x-1.5 shadow-2xs cursor-pointer"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  <span>Main Education Portal</span>
                </button>
              ) : (
                <a
                  href="/"
                  className="px-3.5 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-600 rounded-lg text-xs font-bold transition flex items-center space-x-1.5 shadow-2xs"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  <span>Main Education Portal</span>
                </a>
              )}

              <button
                onClick={handlePrintReport}
                className="px-3 py-1.5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-lg text-xs transition flex items-center space-x-1 shadow-2xs cursor-pointer"
                title="Print Official Report"
              >
                <Printer className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Print Report</span>
              </button>
            </div>
          </div>
        </div>

        {/* Navigation Sub-Tabs (Role Scoped) */}
        <div className="bg-[#09172c] border-t border-slate-800 px-4 sm:px-6 lg:px-8 print:hidden">
          <div className="max-w-7xl mx-auto flex items-center space-x-2 text-xs overflow-x-auto py-1">
            {isSchoolOnly ? (
              <div className="px-4 py-2.5 font-black text-amber-400 bg-slate-800/80 rounded-lg flex items-center space-x-2 text-xs border border-slate-700">
                <Send className="w-3.5 h-3.5 text-amber-400" />
                <span>Daily Attendance Submission Form (School Entry)</span>
              </div>
            ) : (
              <>
                <button
                  onClick={() => setActiveTab('dashboard')}
                  className={`px-4 py-2.5 font-bold border-b-2 transition flex items-center space-x-1.5 whitespace-nowrap cursor-pointer ${
                    activeTab === 'dashboard'
                      ? 'border-amber-400 text-amber-400 bg-slate-800/60'
                      : 'border-transparent text-slate-400 hover:text-slate-200'
                  }`}
                >
                  <BarChart3 className="w-3.5 h-3.5" />
                  <span>
                    {isBEO
                      ? `BEO Daily Attendance Dashboard (${user?.block || 'Block'})`
                      : 'Daily Attendance Monitoring — DEO Dashboard'}
                  </span>
                  {!isUnlocked && <Lock className="w-3 h-3 text-amber-400/80 ml-0.5" />}
                </button>

                <button
                  onClick={() => setActiveTab('submit')}
                  className={`px-4 py-2.5 font-bold border-b-2 transition flex items-center space-x-1.5 whitespace-nowrap cursor-pointer ${
                    activeTab === 'submit'
                      ? 'border-amber-400 text-amber-400 bg-slate-800/60'
                      : 'border-transparent text-slate-400 hover:text-slate-200'
                  }`}
                >
                  <Send className="w-3.5 h-3.5" />
                  <span>Daily Submission Form</span>
                  <span className="text-[10px] bg-emerald-500/20 text-emerald-300 px-1.5 py-0.2 rounded font-mono">Open</span>
                </button>

                <button
                  onClick={() => setActiveTab('history')}
                  className={`px-4 py-2.5 font-bold border-b-2 transition flex items-center space-x-1.5 whitespace-nowrap cursor-pointer ${
                    activeTab === 'history'
                      ? 'border-amber-400 text-amber-400 bg-slate-800/60'
                      : 'border-transparent text-slate-400 hover:text-slate-200'
                  }`}
                >
                  <Clock className="w-3.5 h-3.5" />
                  <span>Records & Log ({historyTotal > 0 ? historyTotal.toLocaleString() : '24,599'})</span>
                  {!isUnlocked && <Lock className="w-3 h-3 text-amber-400/80 ml-0.5" />}
                </button>

                <button
                  onClick={() => setActiveTab('monthly')}
                  className={`px-4 py-2.5 font-bold border-b-2 transition flex items-center space-x-1.5 whitespace-nowrap cursor-pointer ${
                    activeTab === 'monthly'
                      ? 'border-amber-400 text-amber-400 bg-slate-800/60'
                      : 'border-transparent text-slate-400 hover:text-slate-200'
                  }`}
                >
                  <TrendingUp className="w-3.5 h-3.5" />
                  <span>Month-Wise Analysis (May–Oct 2026)</span>
                  {!isUnlocked && <Lock className="w-3 h-3 text-amber-400/80 ml-0.5" />}
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* ---------------------------------------------------- */}
      {/* STATUS BAR: TIMING WINDOW & REAL-TIME IST CLOCK      */}
      {/* ---------------------------------------------------- */}
      <div className="bg-white border-b border-slate-200 shadow-2xs py-2 px-4 sm:px-6 lg:px-8 print:hidden">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 text-xs">
          <div className="flex items-center space-x-2 text-slate-700 font-semibold">
            <Clock className="w-4 h-4 text-blue-600 shrink-0" />
            <span className="font-mono text-slate-900 font-bold">{currentTimeIST || 'India Standard Time (IST)'}</span>
          </div>

          <div className="flex items-center space-x-3 flex-wrap">
            <div className="text-[11px] text-slate-500 font-medium">
              Daily Submission Window: <strong className="text-slate-900">08:00 AM – 09:30 AM IST</strong>
            </div>

            {formControl.is_open ? (
              <span className="inline-flex items-center space-x-1.5 px-3 py-0.5 rounded-full text-[11px] font-black bg-emerald-100 text-emerald-800 border border-emerald-300 shadow-2xs">
                <span className="w-2 h-2 rounded-full bg-emerald-600 animate-pulse" />
                <span>🟢 FORM OPEN</span>
              </span>
            ) : (
              <span className="inline-flex items-center space-x-1.5 px-3 py-0.5 rounded-full text-[11px] font-black bg-rose-100 text-rose-800 border border-rose-300 shadow-2xs">
                <Lock className="w-3 h-3 text-rose-700" />
                <span>🔴 FORM CLOSED</span>
              </span>
            )}

            {isDEO && !formControl.is_open && (
              <label className="flex items-center space-x-1.5 text-[11px] text-indigo-700 font-bold cursor-pointer bg-indigo-50 px-2.5 py-0.5 rounded-lg border border-indigo-200 hover:bg-indigo-100 transition">
                <input
                  type="checkbox"
                  checked={windowOverride}
                  onChange={(e) => setWindowOverride(e.target.checked)}
                  className="rounded text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5 cursor-pointer"
                />
                <span>DEO Override Active</span>
              </label>
            )}
          </div>
        </div>
      </div>

      {/* Main Container */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 w-full flex-1 space-y-6">

        {/* ==================================================== */}
        {/* SECURE ACCESS LOCK SCREEN (PIN: 5120)               */}
        {/* ==================================================== */}
        {activeTab !== 'submit' && !isUnlocked ? (
          <div className="max-w-md mx-auto my-12 bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            <div className="bg-[#0B1E38] p-6 text-white text-center border-b-4 border-amber-500">
              <div className="w-16 h-16 bg-white/10 rounded-2xl mx-auto flex items-center justify-center mb-3 border border-amber-400/40 shadow-inner">
                <Lock className="w-8 h-8 text-amber-400" />
              </div>
              <h2 className="text-xl font-black tracking-tight uppercase text-white">Secure Access</h2>
              <p className="text-xs text-slate-300 mt-1">
                This section is protected. Please enter the authorized PIN to continue.
              </p>
            </div>

            <form onSubmit={handleVerifyPin} className="p-6 space-y-5">
              {pinError && (
                <div className="p-3.5 bg-rose-50 border border-rose-200 rounded-xl flex items-center space-x-2.5 text-rose-700 text-xs font-bold">
                  <AlertTriangle className="w-4 h-4 shrink-0 text-rose-600" />
                  <span>{pinError}</span>
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2 text-center">
                  Enter 4-Digit Authorization PIN
                </label>
                <input
                  type="password"
                  maxLength={4}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={pinInput}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                    setPinInput(val);
                    if (pinError) setPinError('');
                  }}
                  placeholder="••••"
                  autoFocus
                  className="w-full text-center tracking-[1em] text-2xl font-black py-3 px-4 bg-slate-50 border-2 border-slate-300 rounded-xl focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-100 transition outline-none"
                />
                <p className="text-[11px] text-slate-500 text-center mt-2">
                  Administrative & Monitoring Area (DEO / BEO / Dashboard / Reports)
                </p>
              </div>

              <button
                type="submit"
                disabled={isVerifyingPin || pinInput.length !== 4}
                className="w-full py-3 bg-[#0B1E38] hover:bg-blue-900 disabled:opacity-50 text-white font-black rounded-xl text-sm transition shadow-md flex items-center justify-center space-x-2 cursor-pointer"
              >
                <ShieldCheck className="w-4 h-4 text-amber-400" />
                <span>{isVerifyingPin ? 'Verifying PIN...' : 'Unlock Section'}</span>
              </button>

              <div className="pt-2 border-t border-slate-100 text-center">
                <button
                  type="button"
                  onClick={() => setActiveTab('submit')}
                  className="text-xs font-bold text-blue-700 hover:text-blue-900 hover:underline flex items-center justify-center space-x-1 mx-auto cursor-pointer"
                >
                  <Send className="w-3.5 h-3.5 text-blue-600" />
                  <span>Continue to Daily Attendance Submission Form (Unrestricted)</span>
                </button>
              </div>
            </form>
          </div>
        ) : (
          <>
            {/* ==================================================== */}
            {/* TAB 1: DEO DAILY ATTENDANCE DASHBOARD               */}
            {/* ==================================================== */}
            {activeTab === 'dashboard' && !isSchoolOnly && (
          <div className="space-y-6">
            {/* 0. DEO OPEN/CLOSE CONTROL WIDGET */}
            {isDEO && (
              <div className="bg-gradient-to-r from-slate-900 via-[#0B1E38] to-slate-900 text-white p-4 sm:p-5 rounded-2xl shadow-md border border-slate-700 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                <div className="space-y-1">
                  <div className="flex items-center space-x-2.5">
                    <ShieldCheck className="w-5 h-5 text-amber-400" />
                    <h3 className="text-base font-black uppercase tracking-wider text-amber-300">
                      Daily Attendance Form Control
                    </h3>
                    <span className="text-[10px] uppercase font-bold bg-amber-400/20 text-amber-300 px-2 py-0.5 rounded border border-amber-400/30">
                      DEO Administrative Control
                    </span>
                  </div>
                  <p className="text-xs text-slate-300">
                    Scheduled Window: <strong className="text-white">08:00 AM – 09:30 AM IST</strong> (Daily) • Manual DEO override controls live submission access for all schools
                  </p>
                  <div className="text-[11px] text-slate-400 flex items-center space-x-3 pt-1 flex-wrap">
                    <span>Opening Time: <strong className="text-slate-200">08:00 AM IST</strong></span>
                    <span>•</span>
                    <span>Closing Time: <strong className="text-slate-200">09:30 AM IST</strong></span>
                    <span>•</span>
                    <span>Live State: <strong className={formControl.is_open ? 'text-emerald-400 font-bold' : 'text-rose-400 font-bold'}>{formControl.status_reason}</strong></span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 shrink-0">
                  {/* Current Status Pill */}
                  <div className="flex items-center space-x-2 bg-slate-800/90 px-3.5 py-2 rounded-xl border border-slate-700">
                    <span className="text-xs text-slate-400 font-semibold">Current Status:</span>
                    {formControl.is_open ? (
                      <span className="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full text-xs font-black bg-emerald-500 text-slate-950 shadow-sm animate-pulse">
                        <span className="w-2 h-2 rounded-full bg-slate-950" />
                        <span>🟢 FORM OPEN</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full text-xs font-black bg-rose-600 text-white shadow-sm">
                        <Lock className="w-3.5 h-3.5" />
                        <span>🔴 FORM CLOSED</span>
                      </span>
                    )}
                  </div>

                  {/* DEO Manual Control Buttons */}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleSetFormControl('force_open')}
                      disabled={isUpdatingControl || formControl.mode === 'force_open'}
                      className={`px-3 py-2 rounded-xl text-xs font-black transition flex items-center space-x-1.5 shadow-sm cursor-pointer ${
                        formControl.mode === 'force_open'
                          ? 'bg-emerald-600 text-white ring-2 ring-emerald-400'
                          : 'bg-emerald-700 hover:bg-emerald-600 text-white disabled:opacity-50'
                      }`}
                      title="Manually open attendance form for all schools"
                    >
                      <Unlock className="w-3.5 h-3.5" />
                      <span>[ OPEN FORM ]</span>
                    </button>

                    <button
                      onClick={() => handleSetFormControl('force_closed')}
                      disabled={isUpdatingControl || formControl.mode === 'force_closed'}
                      className={`px-3 py-2 rounded-xl text-xs font-black transition flex items-center space-x-1.5 shadow-sm cursor-pointer ${
                        formControl.mode === 'force_closed'
                          ? 'bg-rose-700 text-white ring-2 ring-rose-400'
                          : 'bg-rose-800 hover:bg-rose-700 text-white disabled:opacity-50'
                      }`}
                      title="Manually close attendance form immediately"
                    >
                      <Lock className="w-3.5 h-3.5" />
                      <span>[ CLOSE FORM ]</span>
                    </button>

                    <button
                      onClick={() => handleSetFormControl('auto')}
                      disabled={isUpdatingControl || formControl.mode === 'auto'}
                      className={`px-2.5 py-2 rounded-xl text-xs font-bold transition flex items-center space-x-1 cursor-pointer ${
                        formControl.mode === 'auto'
                          ? 'bg-blue-600 text-white ring-2 ring-blue-400'
                          : 'bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-600'
                      }`}
                      title="Revert to standard 08:00 AM – 09:30 AM IST automated schedule"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${isUpdatingControl ? 'animate-spin' : ''}`} />
                      <span>Auto Schedule</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* 1. DYNAMIC CASCADING FILTERS TOOLBAR */}
            <div className="bg-white p-4 sm:p-5 rounded-2xl shadow-sm border border-slate-200 space-y-4 print:hidden">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 border-b border-slate-100 pb-3">
                <div>
                  <h2 className="text-base font-black text-[#0B1E38] flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-blue-600" />
                    <span>
                      {user?.role === 'principal' || user?.role === 'school'
                        ? 'School Daily Attendance Monitoring'
                        : user?.role === 'beo'
                        ? `BEO Daily Attendance Dashboard (${user?.block || 'Block'})`
                        : 'Daily Attendance Monitoring — DEO Dashboard'}
                    </span>
                  </h2>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Cascading Block → School Category → School hierarchy • Complete Academic Year 2026–27 coverage
                  </div>
                </div>

                <div className="flex items-center space-x-2 flex-wrap gap-y-1.5">
                  <button
                    onClick={handleExportExcel}
                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition flex items-center space-x-1 shadow-2xs cursor-pointer"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span>Download Excel</span>
                  </button>

                  <button
                    onClick={handleExportWordReport}
                    className="px-3 py-1.5 bg-blue-700 hover:bg-blue-800 text-white rounded-lg text-xs font-bold transition flex items-center space-x-1 shadow-2xs cursor-pointer"
                  >
                    <FileText className="w-3.5 h-3.5" />
                    <span>Download Word Report</span>
                  </button>

                  <button
                    onClick={() => {
                      setFilterDate(getTodayISTString());
                      setFilterFromDate('');
                      setFilterToDate('');
                      setFilterMonth('all');
                      setDateFilterMode('single');
                      if (user?.role !== 'beo') setFilterBlock('all');
                      setFilterCategory('all');
                      if (user?.role !== 'principal' && user?.role !== 'school') setFilterSchoolCode('all');
                      setFilterClass('all');
                      setDashboardSearch('');
                    }}
                    className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-bold transition cursor-pointer"
                    title="Reset to default filters"
                  >
                    Reset
                  </button>
                </div>
              </div>

              {/* DATE FILTER MODES & SHORTCUT QUICK FILTERS */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2.5 bg-slate-50 p-2.5 rounded-xl border border-slate-200">
                {/* Mode Selector Tabs: A. Individual Date, B. Date Range, C. Custom Date */}
                <div className="flex items-center space-x-1 bg-white p-1 rounded-lg border border-slate-200 shadow-2xs text-xs font-bold">
                  <button
                    onClick={() => {
                      setDateFilterMode('single');
                      if (filterDate === 'all') setFilterDate(getTodayISTString());
                      setFilterFromDate('');
                      setFilterToDate('');
                    }}
                    className={`px-3 py-1 rounded-md transition ${dateFilterMode === 'single' ? 'bg-blue-700 text-white shadow-2xs' : 'text-slate-600 hover:text-slate-900'}`}
                  >
                    Mode A: Individual Date
                  </button>
                  <button
                    onClick={() => {
                      setDateFilterMode('range');
                      setFilterDate('all');
                      if (!filterFromDate) setFilterFromDate('2026-09-01');
                      if (!filterToDate) setFilterToDate('2026-09-30');
                    }}
                    className={`px-3 py-1 rounded-md transition ${dateFilterMode === 'range' ? 'bg-blue-700 text-white shadow-2xs' : 'text-slate-600 hover:text-slate-900'}`}
                  >
                    Mode B: Date Range
                  </button>
                  <button
                    onClick={() => {
                      setDateFilterMode('quick');
                      setFilterDate('all');
                    }}
                    className={`px-3 py-1 rounded-md transition ${dateFilterMode === 'quick' ? 'bg-blue-700 text-white shadow-2xs' : 'text-slate-600 hover:text-slate-900'}`}
                  >
                    Mode C: Quick Shortcuts
                  </button>
                </div>

                {/* Quick Shortcut Buttons */}
                <div className="flex items-center space-x-1.5 flex-wrap gap-y-1 text-xs">
                  <span className="text-[11px] font-bold text-slate-500 mr-1">Shortcuts:</span>
                  <button
                    onClick={() => applyQuickFilter('today')}
                    className="px-2 py-1 bg-white hover:bg-blue-50 border border-slate-200 hover:border-blue-300 rounded font-bold text-slate-700 transition"
                  >
                    Today
                  </button>
                  <button
                    onClick={() => applyQuickFilter('yesterday')}
                    className="px-2 py-1 bg-white hover:bg-blue-50 border border-slate-200 hover:border-blue-300 rounded font-bold text-slate-700 transition"
                  >
                    Yesterday
                  </button>
                  <button
                    onClick={() => applyQuickFilter('this_week')}
                    className="px-2 py-1 bg-white hover:bg-blue-50 border border-slate-200 hover:border-blue-300 rounded font-bold text-slate-700 transition"
                  >
                    This Week
                  </button>
                  <button
                    onClick={() => applyQuickFilter('this_month')}
                    className="px-2 py-1 bg-white hover:bg-blue-50 border border-slate-200 hover:border-blue-300 rounded font-bold text-slate-700 transition"
                  >
                    This Month
                  </button>
                  <button
                    onClick={() => applyQuickFilter('prev_month')}
                    className="px-2 py-1 bg-white hover:bg-blue-50 border border-slate-200 hover:border-blue-300 rounded font-bold text-slate-700 transition"
                  >
                    Prev Month
                  </button>
                  <button
                    onClick={() => applyQuickFilter('full_year')}
                    className="px-2 py-1 bg-white hover:bg-blue-50 border border-slate-200 hover:border-blue-300 rounded font-bold text-slate-700 transition"
                  >
                    Full Year (2026–27)
                  </button>
                </div>
              </div>

              {/* Cascading Filter Selectors Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 text-xs">
                {/* 1. Date Selector (Mode A: 365 Academic Days / Mode B: Range) */}
                {dateFilterMode === 'single' ? (
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <label className="block text-[11px] font-bold text-slate-700">
                        Attendance Date (Complete Year)
                      </label>
                      <span className="text-[10px] font-extrabold text-blue-700 bg-blue-50 px-1.5 py-0.2 rounded border border-blue-200">
                        2026–27
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="relative flex-1">
                        <input
                          type="date"
                          min="2026-04-01"
                          max="2027-03-31"
                          value={filterDate !== 'all' ? filterDate : ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            setFilterDate(val || 'all');
                            setFilterFromDate('');
                            setFilterToDate('');
                            setFilterMonth('all');
                          }}
                          className="w-full pl-7 pr-1.5 py-1.5 border border-slate-300 rounded-lg bg-slate-50 font-bold text-blue-950 focus:bg-white text-xs cursor-pointer"
                          title="Pick date from April 2026 to March 2027"
                        />
                        <Calendar className="w-3.5 h-3.5 text-blue-700 absolute left-2 top-2.5 pointer-events-none" />
                      </div>
                      <select
                        value={filterDate}
                        onChange={(e) => {
                          setFilterDate(e.target.value);
                          setFilterFromDate('');
                          setFilterToDate('');
                          setFilterMonth('all');
                        }}
                        className="w-24 px-1.5 py-1.5 border border-slate-300 rounded-lg bg-slate-50 font-bold text-blue-900 focus:bg-white text-[11px]"
                        title="Quick date selector"
                      >
                        <option value="all">All Dates</option>
                        {ACADEMIC_YEAR_DATES.map((d) => (
                          <option key={d.iso} value={d.iso}>
                            {d.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <label className="block text-[11px] font-bold text-slate-700">
                        Date Range Selection
                      </label>
                      <span className="text-[10px] font-extrabold text-indigo-700 bg-indigo-50 px-1.5 py-0.2 rounded border border-indigo-200">
                        From → To
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <input
                        type="date"
                        min="2026-04-01"
                        max="2027-03-31"
                        value={filterFromDate || ''}
                        onChange={(e) => setFilterFromDate(e.target.value)}
                        className="w-1/2 px-1.5 py-1.5 border border-slate-300 rounded-lg bg-slate-50 font-bold text-indigo-950 focus:bg-white text-[11px]"
                        title="From Date"
                      />
                      <span className="text-slate-400 font-bold text-xs">→</span>
                      <input
                        type="date"
                        min="2026-04-01"
                        max="2027-03-31"
                        value={filterToDate || ''}
                        onChange={(e) => setFilterToDate(e.target.value)}
                        className="w-1/2 px-1.5 py-1.5 border border-slate-300 rounded-lg bg-slate-50 font-bold text-indigo-950 focus:bg-white text-[11px]"
                        title="To Date"
                      />
                    </div>
                  </div>
                )}

                {/* 2. Block Selector (Cascades Category) */}
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 mb-1">Administrative Block</label>
                  <select
                    value={filterBlock}
                    onChange={(e) => {
                      const blk = e.target.value;
                      setFilterBlock(blk);
                      setFilterCategory('all');
                      setFilterSchoolCode('all');
                    }}
                    disabled={user?.role === 'beo' && !!user?.block}
                    className="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg bg-slate-50 font-bold text-[#0B1E38] focus:bg-white text-xs disabled:opacity-60"
                  >
                    <option value="all">All 5 Blocks</option>
                    {BLOCKS.map((b) => (
                      <option key={b} value={b}>
                        {b} Block
                      </option>
                    ))}
                  </select>
                </div>

                {/* 3. School Category / Type Selector (Cascades School) */}
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 mb-1">School Category</label>
                  <select
                    value={filterCategory}
                    onChange={(e) => {
                      setFilterCategory(e.target.value);
                      setFilterSchoolCode('all');
                    }}
                    className="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg bg-slate-50 font-semibold focus:bg-white text-xs"
                  >
                    <option value="all">All Categories ({dashboardAvailableCategories.length})</option>
                    {dashboardAvailableCategories.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 4. School Selector (Filtered by Block + Category) */}
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 mb-1">School (Code & UDISE)</label>
                  <select
                    value={filterSchoolCode}
                    onChange={(e) => setFilterSchoolCode(e.target.value)}
                    disabled={user?.role === 'principal' || user?.role === 'school'}
                    className="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg bg-slate-50 font-medium focus:bg-white text-xs disabled:opacity-60"
                  >
                    <option value="all">All Schools ({dashboardAvailableSchools.length})</option>
                    {dashboardAvailableSchools.map((s) => (
                      <option key={s.school_code || s.id} value={s.school_code || s.id}>
                        {s.school_name} (Code: {s.school_code})
                      </option>
                    ))}
                  </select>
                </div>

                {/* 5. Class Selector */}
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 mb-1">Class Filter (6–12)</label>
                  <select
                    value={filterClass}
                    onChange={(e) => setFilterClass(e.target.value)}
                    className="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg bg-slate-50 font-semibold focus:bg-white text-xs"
                  >
                    <option value="all">All Classes (6–12)</option>
                    {DEFAULT_CLASSES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 6. Quick Keyword Search (Name, Code, UDISE) */}
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 mb-1">Search Keywords</label>
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400" />
                    <input
                      type="text"
                      placeholder="School, Code, UDISE..."
                      value={dashboardSearch}
                      onChange={(e) => setDashboardSearch(e.target.value)}
                      className="w-full pl-8 pr-2.5 py-1.5 border border-slate-300 rounded-lg bg-slate-50 focus:bg-white text-xs"
                    />
                  </div>
                </div>
              </div>

              {/* Range & Custom Date Picker Inputs */}
              {(dateFilterMode === 'range' || dateFilterMode === 'quick' || filterDate === 'all') && (
                <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-slate-100 text-xs bg-slate-50/70 p-2.5 rounded-lg">
                  <span className="font-bold text-slate-700 flex items-center space-x-1">
                    <Calendar className="w-3.5 h-3.5 text-blue-600" />
                    <span>Custom Date Range (Academic Year 2026–2027):</span>
                  </span>
                  <div className="flex items-center space-x-2">
                    <input
                      type="date"
                      min="2026-04-01"
                      max="2027-03-31"
                      value={filterFromDate}
                      onChange={(e) => {
                        setFilterFromDate(e.target.value);
                        setFilterDate('all');
                      }}
                      className="px-2.5 py-1 border border-slate-300 rounded-lg text-xs bg-white font-semibold"
                    />
                    <span className="text-slate-500 font-bold">to</span>
                    <input
                      type="date"
                      min="2026-04-01"
                      max="2027-03-31"
                      value={filterToDate}
                      onChange={(e) => {
                        setFilterToDate(e.target.value);
                        setFilterDate('all');
                      }}
                      className="px-2.5 py-1 border border-slate-300 rounded-lg text-xs bg-white font-semibold"
                    />
                  </div>
                  <span className="text-[11px] text-slate-500">
                    Selected Span: <strong>{filterFromDate ? formatDisplayDate(filterFromDate) : 'Start'}</strong> → <strong>{filterToDate ? formatDisplayDate(filterToDate) : 'End'}</strong>
                  </span>
                </div>
              )}
            </div>

            {/* EMPTY STATE / NO DATA BANNER FOR UNREPORTED DATES & MONTHS */}
            {dashboardData && dashboardData.kpis && dashboardData.kpis.reported_schools === 0 && !loadingDashboard && (
              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-xl shadow-xs text-amber-900 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex items-center space-x-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
                  <div>
                    <h4 className="text-sm font-bold text-amber-950">
                      {filterMonth && filterMonth !== 'all' 
                        ? `No attendance data available for selected month (${filterMonth}).`
                        : `No attendance data available for selected date (${formatDisplayDate(filterDate)}).`}
                    </h4>
                    <p className="text-xs text-amber-800 mt-0.5">
                      No attendance submissions recorded in D1 for {filterMonth && filterMonth !== 'all' ? filterMonth : (filterBlock === 'all' ? 'the district on this date' : `${filterBlock} block on this date`)}. Complete school master roster and baseline metrics displayed below.
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 shrink-0">
                  <span className="text-xs font-mono font-bold bg-amber-200/80 text-amber-950 px-3 py-1 rounded-lg border border-amber-300">
                    0 / {dashboardData.kpis.total_schools_in_scope} Reported
                  </span>
                </div>
              </div>
            )}

            {loadingDashboard ? (
              <div className="p-16 text-center text-slate-500 text-xs space-y-3 bg-white rounded-2xl border border-slate-200 shadow-sm">
                <RefreshCw className="w-10 h-10 mx-auto text-blue-600 animate-spin" />
                <p className="font-bold text-sm text-slate-800">Computing Daily Attendance Metrics & Analytics...</p>
                <p className="text-slate-400">Aggregating records across 403 schools and 5 administrative blocks</p>
              </div>
            ) : dashboardData ? (
              <div className="space-y-6">
                {/* 2. DEO EXECUTIVE KPI CARDS */}
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
                  {/* Reporting Compliance */}
                  <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs space-y-2">
                    <div className="flex justify-between items-center text-slate-500">
                      <span className="text-[11px] font-bold uppercase tracking-wider">School Reporting</span>
                      <Building2 className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="flex items-baseline space-x-2">
                      <span className="text-2xl font-black text-[#0B1E38]">
                        {dashboardData.kpis.reported_schools}
                      </span>
                      <span className="text-xs text-slate-400 font-semibold">
                        / {dashboardData.kpis.total_schools_in_scope} in Scope
                      </span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${Math.min(100, dashboardData.kpis.reporting_pct || 0)}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-[11px] text-slate-500 pt-0.5">
                      <span>Compliance: <strong className="text-blue-800">{dashboardData.kpis.reporting_pct}%</strong></span>
                      <span>Pending: <strong className="text-rose-700">{dashboardData.kpis.pending_schools}</strong></span>
                    </div>
                  </div>

                  {/* Student Attendance Rate */}
                  <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs space-y-2">
                    <div className="flex justify-between items-center text-slate-500">
                      <span className="text-[11px] font-bold uppercase tracking-wider">Student Attendance</span>
                      <GraduationCap className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div className="flex items-baseline space-x-2">
                      <span className="text-2xl font-black text-emerald-700">
                        {dashboardData.kpis.student_attendance_pct !== null ? `${dashboardData.kpis.student_attendance_pct}%` : 'N/A'}
                      </span>
                      <span className="text-xs text-emerald-800 font-medium">District Average</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-emerald-600 h-2 rounded-full"
                        style={{ width: `${Math.min(100, dashboardData.kpis.student_attendance_pct || 0)}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-[11px] text-slate-500 pt-0.5">
                      <span>Pres: <strong>{(dashboardData.kpis.total_students_present || 0).toLocaleString()}</strong></span>
                      <span>Enr: <strong>{(dashboardData.kpis.total_students_enrolled || 0).toLocaleString()}</strong></span>
                    </div>
                  </div>

                  {/* Faculty Attendance Rate */}
                  <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs space-y-2">
                    <div className="flex justify-between items-center text-slate-500">
                      <span className="text-[11px] font-bold uppercase tracking-wider">Faculty Attendance</span>
                      <Users className="w-4 h-4 text-indigo-600" />
                    </div>
                    <div className="flex items-baseline space-x-2">
                      <span className="text-2xl font-black text-indigo-900">
                        {dashboardData.kpis.teacher_attendance_pct !== null ? `${dashboardData.kpis.teacher_attendance_pct}%` : 'N/A'}
                      </span>
                      <span className="text-xs text-indigo-700 font-medium">Duty Attendance</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-indigo-600 h-2 rounded-full"
                        style={{ width: `${Math.min(100, dashboardData.kpis.teacher_attendance_pct || 0)}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-[11px] text-slate-500 pt-0.5">
                      <span>Pres: <strong>{(dashboardData.kpis.total_teachers_present || 0).toLocaleString()}</strong></span>
                      <span>Work: <strong>{(dashboardData.kpis.total_teachers_working || 0).toLocaleString()}</strong></span>
                    </div>
                  </div>

                  {/* Date Status & Audit */}
                  <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs space-y-2">
                    <div className="flex justify-between items-center text-slate-500">
                      <span className="text-[11px] font-bold uppercase tracking-wider">Date & Verification</span>
                      <ShieldCheck className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div className="text-base font-black text-slate-900 font-mono pt-0.5">
                      {dashboardData.filters_applied?.date !== 'all' ? dashboardData.filters_applied.date : 'Multiple Dates'}
                    </div>
                    <div className="text-xs text-emerald-700 font-semibold flex items-center space-x-1">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      <span>100% Genuine Records Ingested</span>
                    </div>
                    <div className="text-[11px] text-slate-400 pt-0.5">
                      D1 Persistent Edge Sync Active
                    </div>
                  </div>
                </div>

                {/* 3. CLASS 6–12 COVERAGE CARDS */}
                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs space-y-3">
                  <div className="flex justify-between items-center border-b border-slate-100 pb-2">
                    <span className="text-xs font-extrabold text-[#0B1E38] uppercase tracking-wider flex items-center gap-1.5">
                      <GraduationCap className="w-4 h-4 text-blue-600" />
                      <span>Class 6 to 12 Student Attendance Breakdown</span>
                    </span>
                    <span className="text-[11px] font-semibold text-slate-500">
                      Total Absent Students: <strong className="text-rose-700">{(dashboardData.kpis.total_students_absent || 0).toLocaleString()}</strong>
                    </span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 text-xs">
                    {dashboardData.kpis.classes?.map((cl: any) => (
                      <div
                        key={cl.className}
                        onClick={() => setFilterClass(cl.className)}
                        className={`p-3 rounded-lg border transition cursor-pointer ${
                          filterClass === cl.className
                            ? 'bg-blue-50/80 border-blue-400 ring-2 ring-blue-300'
                            : 'bg-slate-50/70 border-slate-200 hover:bg-slate-100'
                        }`}
                      >
                        <div className="font-extrabold text-slate-900">{cl.className}</div>
                        <div className="text-base font-black text-emerald-700 mt-1">
                          {cl.attendancePct !== null ? `${cl.attendancePct}%` : 'N/A'}
                        </div>
                        <div className="text-[10px] text-slate-500 mt-0.5 space-y-0.5 font-mono">
                          <div>P: {cl.totalPresent?.toLocaleString()}</div>
                          <div>E: {cl.totalEnrolled?.toLocaleString()}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 4. SCHOOL CATEGORY KPIS (Middle, High, Sr Sec) */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {dashboardData.category_kpis?.map((cat: any) => (
                    <div
                      key={cat.category}
                      onClick={() => setFilterCategory(cat.category)}
                      className={`bg-white p-4 rounded-xl border shadow-xs transition cursor-pointer space-y-2 ${
                        filterCategory === cat.category
                          ? 'border-blue-500 ring-2 ring-blue-200'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex justify-between items-center border-b border-slate-100 pb-2">
                        <span className="font-black text-sm text-[#0B1E38]">{cat.category}</span>
                        <span className="text-[10px] font-extrabold bg-blue-50 text-blue-800 px-2 py-0.5 rounded border border-blue-200">
                          {cat.total_schools} Schools
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-xs pt-1">
                        <div>
                          <span className="text-[10px] text-slate-400 uppercase font-bold block">Student Att. %</span>
                          <span className="text-lg font-black text-emerald-700">
                            {cat.student_attendance_pct !== null ? `${cat.student_attendance_pct}%` : 'N/A'}
                          </span>
                          <span className="text-[10px] text-slate-500 block font-mono">
                            {cat.present?.toLocaleString()} / {cat.enrolled?.toLocaleString()}
                          </span>
                        </div>

                        <div>
                          <span className="text-[10px] text-slate-400 uppercase font-bold block">Faculty Att. %</span>
                          <span className="text-lg font-black text-indigo-900">
                            {cat.teacher_attendance_pct !== null ? `${cat.teacher_attendance_pct}%` : 'N/A'}
                          </span>
                          <span className="text-[10px] text-slate-500 block font-mono">
                            {cat.teachers_present} / {cat.teachers_working}
                          </span>
                        </div>
                      </div>

                      <div className="pt-2 border-t border-slate-100 text-[11px] flex justify-between text-slate-600">
                        <span>Reported: <strong className="text-blue-700">{cat.reported_schools}</strong></span>
                        <span>Pending: <strong className="text-rose-700">{cat.pending_schools}</strong></span>
                        <span>Compliance: <strong className="text-slate-900">{cat.reporting_pct}%</strong></span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* 5. 10 REAL DATA CHARTS GRID */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Chart A: School Category-wise Attendance Bar */}
                  <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="font-extrabold text-xs text-[#0B1E38] uppercase tracking-wider flex items-center gap-1.5">
                        <BarChart3 className="w-4 h-4 text-blue-600" />
                        <span>A. School Category-Wise Attendance %</span>
                      </span>
                      <span className="text-[10px] text-slate-400 font-semibold">Real Data</span>
                    </div>

                    <div className="space-y-3 pt-2">
                      {dashboardData.charts?.category_bar?.map((c: any) => (
                        <div key={c.category} className="space-y-1 text-xs">
                          <div className="flex justify-between font-semibold text-slate-800">
                            <span>{c.category}</span>
                            <span className="font-mono text-emerald-700 font-bold">{c.student_attendance_pct}% Students • {c.teacher_attendance_pct}% Faculty</span>
                          </div>
                          <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden flex">
                            <div className="bg-emerald-600 h-3 transition-all" style={{ width: `${c.student_attendance_pct}%` }} title={`Students: ${c.student_attendance_pct}%`} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Chart B: Date-wise Attendance Trend Line */}
                  <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="font-extrabold text-xs text-[#0B1E38] uppercase tracking-wider flex items-center gap-1.5">
                        <TrendingUp className="w-4 h-4 text-emerald-600" />
                        <span>B. Date-Wise Student Attendance Trend</span>
                      </span>
                      <span className="text-[10px] text-slate-400 font-semibold">Chronological</span>
                    </div>

                    <div className="h-44 flex items-end space-x-1 pt-4 pb-2 px-1 border-b border-slate-200 overflow-x-auto">
                      {dashboardData.charts?.date_trend_line?.slice(-15).map((d: any) => (
                        <div key={d.date} className="flex-1 flex flex-col items-center group relative min-w-[28px]">
                          <div
                            className="w-full bg-blue-600 hover:bg-blue-700 rounded-t transition-all cursor-pointer"
                            style={{ height: `${Math.max(8, (d.student_attendance_pct / 100) * 120)}px` }}
                            onClick={() => setFilterDate(d.date)}
                          />
                          <span className="text-[9px] text-slate-500 font-mono mt-1 transform -rotate-45 truncate w-7 text-center">
                            {d.date.slice(5)}
                          </span>

                          {/* Tooltip */}
                          <div className="hidden group-hover:block absolute bottom-full mb-1 bg-slate-900 text-white text-[10px] p-2 rounded shadow-lg whitespace-nowrap z-20">
                            <strong>{d.date}</strong><br/>
                            Att: {d.student_attendance_pct}%<br/>
                            Reported: {d.reported_schools} Schools
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Chart C & D: Donut & Class Column Chart */}
                  <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="font-extrabold text-xs text-[#0B1E38] uppercase tracking-wider flex items-center gap-1.5">
                        <PieChart className="w-4 h-4 text-amber-600" />
                        <span>C. Reported vs Pending & D. Class Attendance</span>
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-3 pt-2">
                      {/* Donut Simulation */}
                      <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 text-center space-y-1">
                        <div className="text-[11px] font-bold text-slate-500">School Reporting</div>
                        <div className="text-2xl font-black text-blue-700">{dashboardData.charts?.donut_reported?.reporting_pct}%</div>
                        <div className="text-[10px] text-slate-500">
                          {dashboardData.charts?.donut_reported?.reported} Reported / {dashboardData.charts?.donut_reported?.pending} Pending
                        </div>
                      </div>

                      {/* Gauge Simulation */}
                      <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 text-center space-y-1">
                        <div className="text-[11px] font-bold text-slate-500">District Student Gauge</div>
                        <div className="text-2xl font-black text-emerald-700">{dashboardData.charts?.gauge?.student_attendance_pct}%</div>
                        <div className="text-[10px] text-slate-500">
                          {dashboardData.charts?.gauge?.student_attendance_pct >= 75 ? 'Optimal Band (>75%)' : 'Focus Needed'}
                        </div>
                      </div>
                    </div>

                    {/* Classes column breakdown */}
                    <div className="space-y-1.5 pt-2">
                      {dashboardData.charts?.class_column?.map((cl: any) => (
                        <div key={cl.className} className="flex items-center text-xs space-x-2">
                          <span className="w-14 font-semibold text-slate-700 text-[11px]">{cl.className}</span>
                          <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                            <div className="bg-emerald-600 h-2 rounded-full" style={{ width: `${cl.attendancePct}%` }} />
                          </div>
                          <span className="w-10 font-mono text-right text-emerald-700 font-bold text-[11px]">{cl.attendancePct}%</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Chart E: Block-wise Stacked Bar Chart */}
                  <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="font-extrabold text-xs text-[#0B1E38] uppercase tracking-wider flex items-center gap-1.5">
                        <Layers className="w-4 h-4 text-indigo-600" />
                        <span>E. Block-Wise Attendance Breakdown</span>
                      </span>
                      <span className="text-[10px] text-slate-400 font-semibold">5 Blocks</span>
                    </div>

                    <div className="space-y-2 pt-1">
                      {dashboardData.block_summary?.map((b: any) => (
                        <div key={b.block} className="text-xs space-y-1">
                          <div className="flex justify-between font-semibold text-slate-800">
                            <span>{b.block} Block</span>
                            <span className="font-mono text-blue-800 font-bold">
                              {b.student_attendance_pct !== null ? `${b.student_attendance_pct}%` : 'N/A'} ({b.reported_schools}/{b.total_schools} Reported)
                            </span>
                          </div>
                          <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden flex">
                            <div
                              className="bg-blue-600 h-2.5 rounded-l"
                              style={{ width: `${Math.min(100, b.reporting_pct || 0)}%` }}
                              title={`Reported: ${b.reported_schools}`}
                            />
                            <div
                              className="bg-rose-400 h-2.5 rounded-r"
                              style={{ width: `${Math.max(0, 100 - (b.reporting_pct || 0))}%` }}
                              title={`Pending: ${b.pending_schools}`}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* ---------------------------------------------------- */}
                {/* 5B. DEDICATED SECTION: SCHOOL PERFORMANCE RANKING    */}
                {/* ---------------------------------------------------- */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 space-y-4">
                  <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3 border-b border-slate-100 pb-4">
                    <div>
                      <div className="flex items-center space-x-2">
                        <Trophy className="w-5 h-5 text-amber-500" />
                        <h3 className="text-base font-black text-[#0B1E38]">School Performance Ranking</h3>
                        <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-[11px] font-extrabold rounded-full border border-blue-200">
                          Analytical Sorting Tool
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        Dynamic multi-metric ranking across all categories or specific school types with genuine D1 data
                      </p>
                    </div>

                    {/* Analytical Controls */}
                    <div className="flex flex-wrap items-center gap-2">
                      {/* Top / Bottom Toggle */}
                      <div className="flex bg-slate-100 p-0.5 rounded-lg text-xs font-bold border border-slate-200">
                        <button
                          onClick={() => setRankingDirection('top')}
                          className={`px-3 py-1.5 rounded-md transition cursor-pointer ${
                            rankingDirection === 'top'
                              ? 'bg-emerald-700 text-white shadow-xs'
                              : 'text-slate-600 hover:text-slate-900'
                          }`}
                        >
                          Top N
                        </button>
                        <button
                          onClick={() => setRankingDirection('bottom')}
                          className={`px-3 py-1.5 rounded-md transition cursor-pointer ${
                            rankingDirection === 'bottom'
                              ? 'bg-rose-700 text-white shadow-xs'
                              : 'text-slate-600 hover:text-slate-900'
                          }`}
                        >
                          Bottom N
                        </button>
                      </div>

                      {/* Preset N Buttons */}
                      <div className="flex space-x-1">
                        {[5, 10, 20].map((n) => (
                          <button
                            key={n}
                            onClick={() => {
                              setRankingN(n);
                              setCustomNInput('');
                            }}
                            className={`px-2.5 py-1.5 text-xs font-bold rounded-lg border transition cursor-pointer ${
                              rankingN === n && !customNInput
                                ? 'bg-[#0B1E38] text-white border-[#0B1E38]'
                                : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                            }`}
                          >
                            {rankingDirection === 'top' ? `Top ${n}` : `Bottom ${n}`}
                          </button>
                        ))}
                      </div>

                      {/* Custom N Input */}
                      <div className="flex items-center space-x-1 bg-slate-50 px-2.5 py-1 rounded-lg border border-slate-300 text-xs">
                        <span className="text-slate-500 font-bold">Custom N:</span>
                        <input
                          type="number"
                          min="1"
                          max="403"
                          placeholder="e.g. 15"
                          value={customNInput}
                          onChange={(e) => {
                            const val = e.target.value;
                            setCustomNInput(val);
                            const num = parseInt(val, 10);
                            if (num > 0) setRankingN(num);
                          }}
                          className="w-14 px-1.5 py-0.5 bg-white border border-slate-300 rounded font-bold text-center text-xs focus:ring-1 focus:ring-blue-500"
                        />
                      </div>

                      {/* Category Filter */}
                      <select
                        value={rankingCategory}
                        onChange={(e) => setRankingCategory(e.target.value)}
                        className="px-2.5 py-1.5 text-xs font-semibold bg-slate-50 border border-slate-300 rounded-lg text-slate-800"
                      >
                        <option value="all">All Categories ({cascadingData.schools.length || 403})</option>
                        <option value="Middle School">Middle Schools (258)</option>
                        <option value="High School">High Schools (7)</option>
                        <option value="Senior Secondary School">Senior Secondary Schools (138)</option>
                      </select>

                      {/* Block Filter inside Ranking */}
                      <select
                        value={rankingBlock}
                        onChange={(e) => setRankingBlock(e.target.value)}
                        className="px-2.5 py-1.5 text-xs font-semibold bg-slate-50 border border-slate-300 rounded-lg text-slate-800"
                      >
                        <option value="all">All 5 Blocks</option>
                        {BLOCKS.map((b) => (
                          <option key={b} value={b}>{b}</option>
                        ))}
                      </select>

                      {/* Metric Selector */}
                      <select
                        value={rankingMetric}
                        onChange={(e) => setRankingMetric(e.target.value)}
                        className="px-2.5 py-1.5 text-xs font-bold bg-blue-50 border border-blue-300 rounded-lg text-blue-900"
                      >
                        <option value="student_attendance_pct">Student Attendance %</option>
                        <option value="teacher_attendance_pct">Teacher Attendance %</option>
                        <option value="total_present">Student Present Count</option>
                        <option value="teachers_present">Teacher Present Count</option>
                        <option value="submission_status">Reporting Status</option>
                      </select>
                    </div>
                  </div>

                  {/* Summary Ribbon & Dedicated Excel Downloads */}
                  <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3 bg-slate-50 px-4 py-3 rounded-xl text-xs border border-slate-200">
                    <div>
                      <span className="font-bold text-slate-800">
                        Showing <strong className="text-blue-900">{rankingDirection === 'top' ? 'Top' : 'Bottom'} {rankedSchools.length}</strong> {rankingCategory === 'all' ? 'Schools across All Categories' : rankingCategory + 's'} by <strong className="text-emerald-800">{getMetricLabel(rankingMetric)}</strong>
                      </span>
                      <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                        Target Snapshot: <strong>{formatDisplayDate(filterDate)}</strong> • Metric: <strong>{getMetricLabel(rankingMetric)}</strong>
                      </div>
                    </div>

                    {/* 3 Dedicated Excel Download Buttons */}
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        onClick={handleExportTopSchoolsExcel}
                        className="px-3 py-1.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg font-bold text-xs transition flex items-center space-x-1.5 shadow-2xs cursor-pointer"
                        title="Export Top Schools into Excel (Daily_Attendance_Top_Schools_2026-27.xlsx)"
                      >
                        <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-200" />
                        <span>[ Download Top Schools Excel ]</span>
                      </button>

                      <button
                        onClick={handleExportBottomSchoolsExcel}
                        className="px-3 py-1.5 bg-rose-700 hover:bg-rose-800 text-white rounded-lg font-bold text-xs transition flex items-center space-x-1.5 shadow-2xs cursor-pointer"
                        title="Export Bottom Schools into Excel (Daily_Attendance_Bottom_Schools_2026-27.xlsx)"
                      >
                        <FileSpreadsheet className="w-3.5 h-3.5 text-rose-200" />
                        <span>[ Download Bottom Schools Excel ]</span>
                      </button>

                      <button
                        onClick={handleExportTopBottomCombinedExcel}
                        className="px-3 py-1.5 bg-[#0B1E38] hover:bg-blue-950 text-white rounded-lg font-bold text-xs transition flex items-center space-x-1.5 shadow-2xs border border-slate-700 cursor-pointer"
                        title="Export Top & Bottom Combined 3-Sheet Workbook (Daily_Attendance_Top_Bottom_2026-27.xlsx)"
                      >
                        <Download className="w-3.5 h-3.5 text-amber-400" />
                        <span>[ Download Top & Bottom Combined Excel ]</span>
                      </button>
                    </div>
                  </div>

                  {/* Ranked Schools Table */}
                  <div className="overflow-x-auto rounded-xl border border-slate-200">
                    <table className="w-full text-xs text-left">
                      <thead className="bg-[#0B1E38] text-white uppercase font-bold text-[10px] tracking-wider">
                        <tr>
                          <th className="py-2.5 px-3 text-center w-12">Rank</th>
                          <th className="py-2.5 px-3">School Name & Code</th>
                          <th className="py-2.5 px-2.5">UDISE Code</th>
                          <th className="py-2.5 px-2.5">Block</th>
                          <th className="py-2.5 px-2.5">Category</th>
                          <th className="py-2.5 px-3 text-center bg-blue-950 font-black">
                            {getMetricLabel(rankingMetric)}
                          </th>
                          <th className="py-2.5 px-3 text-center">Students (Pres / Enr)</th>
                          <th className="py-2.5 px-3 text-center">Faculty (Pres / Work)</th>
                          <th className="py-2.5 px-2.5 text-center">Status</th>
                          <th className="py-2.5 px-2.5 text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 font-medium bg-white">
                        {rankedSchools.length === 0 ? (
                          <tr>
                            <td colSpan={10} className="py-8 text-center text-slate-400">
                              No schools match the selected ranking criteria on this date.
                            </td>
                          </tr>
                        ) : (
                          rankedSchools.map((s: any, idx: number) => {
                            const isReported = s.submission_status === 'SUBMITTED' || s.submission_status === 'REPORTED';
                            return (
                              <tr
                                key={s.school_code || idx}
                                className="hover:bg-blue-50/40 transition"
                              >
                                <td className="py-2.5 px-3 text-center font-black">
                                  <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-mono ${
                                    rankingDirection === 'top'
                                      ? idx < 3 ? 'bg-amber-100 text-amber-900 border border-amber-300' : 'bg-slate-100 text-slate-700'
                                      : 'bg-rose-100 text-rose-900 border border-rose-200'
                                  }`}>
                                    #{idx + 1}
                                  </span>
                                </td>
                                <td className="py-2.5 px-3">
                                  <div
                                    onClick={() => setSelectedSchoolDossier(s)}
                                    className="font-bold text-slate-900 hover:text-blue-700 cursor-pointer"
                                  >
                                    {s.school_name}
                                  </div>
                                  <div className="text-[10px] text-slate-400 font-mono">Code: {s.school_code}</div>
                                </td>
                                <td className="py-2.5 px-2.5 font-mono text-slate-600">{s.udise_code || 'N/A'}</td>
                                <td className="py-2.5 px-2.5">
                                  <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded font-semibold text-[10px]">
                                    {s.block}
                                  </span>
                                </td>
                                <td className="py-2.5 px-2.5">
                                  <span className={`px-2 py-0.5 rounded font-semibold text-[10px] ${
                                    s.school_type === 'Senior Secondary School'
                                      ? 'bg-purple-50 text-purple-700'
                                      : s.school_type === 'High School'
                                      ? 'bg-blue-50 text-blue-700'
                                      : 'bg-emerald-50 text-emerald-700'
                                  }`}>
                                    {s.school_type}
                                  </span>
                                </td>
                                <td className="py-2.5 px-3 text-center font-mono font-black text-sm bg-blue-50/50">
                                  {rankingMetric === 'student_attendance_pct' ? (
                                    <span className={s.student_attendance_pct !== null && s.student_attendance_pct >= 75 ? 'text-emerald-700' : 'text-rose-700'}>
                                      {s.student_attendance_pct !== null ? `${s.student_attendance_pct}%` : 'N/A'}
                                    </span>
                                  ) : rankingMetric === 'teacher_attendance_pct' ? (
                                    <span className={s.teacher_attendance_pct !== null && s.teacher_attendance_pct >= 80 ? 'text-indigo-800' : 'text-amber-700'}>
                                      {s.teacher_attendance_pct !== null ? `${s.teacher_attendance_pct}%` : 'N/A'}
                                    </span>
                                  ) : rankingMetric === 'total_present' ? (
                                    <span className="text-slate-900 font-bold">
                                      {s.total_present?.toLocaleString()}
                                    </span>
                                  ) : rankingMetric === 'teachers_present' ? (
                                    <span className="text-slate-900 font-bold">
                                      {s.teachers_present?.toLocaleString()}
                                    </span>
                                  ) : (
                                    <span className={isReported ? 'text-emerald-700 font-bold' : 'text-rose-700 font-bold'}>
                                      {s.submission_status}
                                    </span>
                                  )}
                                </td>
                                <td className="py-2.5 px-3 text-center font-mono">
                                  {isReported ? (
                                    <span>
                                      <strong>{s.total_present?.toLocaleString()}</strong> / {s.total_enrolled?.toLocaleString()}
                                    </span>
                                  ) : (
                                    <span className="text-slate-400">N/A</span>
                                  )}
                                </td>
                                <td className="py-2.5 px-3 text-center font-mono">
                                  {isReported ? (
                                    <span>
                                      <strong>{s.teachers_present}</strong> / {s.teachers_working}
                                    </span>
                                  ) : (
                                    <span className="text-slate-400">N/A</span>
                                  )}
                                </td>
                                <td className="py-2.5 px-2.5 text-center">
                                  {isReported ? (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-emerald-100 text-emerald-800 border border-emerald-300">
                                      SUBMITTED
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-rose-100 text-rose-800 border border-rose-300">
                                      NOT SUBMITTED
                                    </span>
                                  )}
                                </td>
                                <td className="py-2.5 px-2.5 text-center">
                                  <button
                                    onClick={() => setSelectedSchoolDossier(s)}
                                    className="px-2 py-1 bg-slate-100 hover:bg-blue-600 hover:text-white rounded text-[11px] font-bold transition flex items-center space-x-1 mx-auto cursor-pointer"
                                    title="View Classwise Roster & Submitter Dossier"
                                  >
                                    <Eye className="w-3 h-3" />
                                    <span>Dossier</span>
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* ==================================================== */}
                {/* TABLE 1: SCHOOL SUBMISSION STATUS TABLE              */}
                {/* ==================================================== */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden space-y-3 p-5">
                  <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3 border-b border-slate-100 pb-4">
                    <div>
                      <div className="flex items-center space-x-2">
                        <CheckCircle2 className="w-5 h-5 text-blue-700" />
                        <h3 className="text-base font-black text-[#0B1E38]">
                          Table 1: School Submission Status (Classes 6–12)
                        </h3>
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-900 text-[11px] font-extrabold rounded-full">
                          403 School Master Roster
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        Complete school-wise reporting status with submitter identity, timestamps, student & faculty counts
                      </p>
                    </div>

                    <button
                      onClick={handleExportSubmissionStatusExcel}
                      className="px-3.5 py-1.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg font-bold text-xs transition flex items-center space-x-1.5 shadow-2xs cursor-pointer shrink-0"
                    >
                      <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-200" />
                      <span>[ Download Submission Status Excel (5 Sheets) ]</span>
                    </button>
                  </div>

                  {/* Summary Status Ribbon */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-slate-50 p-3.5 rounded-xl border border-slate-200 text-xs">
                    <div>
                      <span className="text-[10px] uppercase font-bold text-slate-400">Total Schools In Scope</span>
                      <div className="text-lg font-black text-[#0B1E38] mt-0.5">
                        {dashboardData.kpis?.total_schools_in_scope || 403}
                      </div>
                    </div>
                    <div>
                      <span className="text-[10px] uppercase font-bold text-slate-400">Submitted Schools</span>
                      <div className="text-lg font-black text-emerald-700 mt-0.5">
                        {dashboardData.kpis?.reported_schools || 0}
                      </div>
                    </div>
                    <div>
                      <span className="text-[10px] uppercase font-bold text-slate-400">Non-Submitted Schools</span>
                      <div className="text-lg font-black text-rose-700 mt-0.5">
                        {dashboardData.kpis?.pending_schools || 0}
                      </div>
                    </div>
                    <div>
                      <span className="text-[10px] uppercase font-bold text-slate-400">Reporting Compliance %</span>
                      <div className="text-lg font-black text-blue-700 mt-0.5">
                        {dashboardData.kpis?.reporting_pct || 0}%
                      </div>
                    </div>
                  </div>

                  {/* Filter Sub-Tabs & Controls */}
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 pt-2">
                    {/* Status Tabs */}
                    <div className="flex bg-slate-100 p-0.5 rounded-xl text-xs font-bold border border-slate-200">
                      <button
                        onClick={() => { setSubmissionStatusTab('all'); setStatusTablePage(1); }}
                        className={`px-3 py-1.5 rounded-lg transition cursor-pointer ${
                          submissionStatusTab === 'all'
                            ? 'bg-[#0B1E38] text-white shadow-xs'
                            : 'text-slate-600 hover:text-slate-900'
                        }`}
                      >
                        All Schools ({sortedSchoolRows.length})
                      </button>
                      <button
                        onClick={() => { setSubmissionStatusTab('submitted'); setStatusTablePage(1); }}
                        className={`px-3 py-1.5 rounded-lg transition cursor-pointer ${
                          submissionStatusTab === 'submitted'
                            ? 'bg-emerald-700 text-white shadow-xs'
                            : 'text-slate-600 hover:text-slate-900'
                        }`}
                      >
                        Submitted ({dashboardData.kpis?.reported_schools || 0})
                      </button>
                      <button
                        onClick={() => { setSubmissionStatusTab('not_submitted'); setStatusTablePage(1); }}
                        className={`px-3 py-1.5 rounded-lg transition cursor-pointer ${
                          submissionStatusTab === 'not_submitted'
                            ? 'bg-rose-700 text-white shadow-xs'
                            : 'text-slate-600 hover:text-slate-900'
                        }`}
                      >
                        Non-Submitted ({dashboardData.kpis?.pending_schools || 0})
                      </button>
                    </div>

                    {/* Search & Page Size */}
                    <div className="flex items-center space-x-2 w-full sm:w-auto">
                      <div className="relative flex-1 sm:w-64">
                        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                          type="text"
                          placeholder="Search school / code / UDISE..."
                          value={statusTableSearch}
                          onChange={(e) => { setStatusTableSearch(e.target.value); setStatusTablePage(1); }}
                          className="w-full text-xs pl-8 pr-3 py-1.5 border border-slate-300 rounded-lg bg-white"
                        />
                      </div>

                      <select
                        value={statusTablePageSize}
                        onChange={(e) => { setStatusTablePageSize(Number(e.target.value)); setStatusTablePage(1); }}
                        className="px-2.5 py-1.5 text-xs font-semibold bg-white border border-slate-300 rounded-lg"
                      >
                        <option value="25">25 / page</option>
                        <option value="50">50 / page</option>
                        <option value="100">100 / page</option>
                        <option value="-1">All ({filteredStatusRows.length})</option>
                      </select>
                    </div>
                  </div>

                  {/* Table 1 Data Grid */}
                  <div className="overflow-x-auto rounded-xl border border-slate-200 max-h-[580px]">
                    <table className="w-full text-xs text-left">
                      <thead className="bg-[#0B1E38] text-white uppercase font-bold text-[10px] tracking-wider sticky top-0 z-10">
                        <tr>
                          <th className="py-2.5 px-3 text-center w-10">Sr.</th>
                          <th className="py-2.5 px-3 cursor-pointer" onClick={() => handleSort('school_name')}>
                            <span className="flex items-center gap-1">School Name & Code <ArrowUpDown className="w-3 h-3" /></span>
                          </th>
                          <th className="py-2.5 px-2.5">UDISE</th>
                          <th className="py-2.5 px-2.5">Block</th>
                          <th className="py-2.5 px-2.5">Category</th>
                          <th className="py-2.5 px-2 text-center">Status</th>
                          <th className="py-2.5 px-2.5">Submitter & Contact</th>
                          <th className="py-2.5 px-2.5 text-center cursor-pointer" onClick={() => handleSort('total_present')}>
                            <span className="flex items-center justify-center gap-1">Students (P/E) <ArrowUpDown className="w-3 h-3" /></span>
                          </th>
                          <th className="py-2.5 px-2 text-center cursor-pointer" onClick={() => handleSort('student_attendance_pct')}>
                            <span className="flex items-center justify-center gap-1">Student % <ArrowUpDown className="w-3 h-3" /></span>
                          </th>
                          <th className="py-2.5 px-2 text-center">Faculty (P/W)</th>
                          <th className="py-2.5 px-2 text-center">Faculty %</th>
                          <th className="py-2.5 px-2 text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 font-medium bg-white">
                        {paginatedStatusRows.length === 0 ? (
                          <tr>
                            <td colSpan={12} className="py-8 text-center text-slate-400">
                              No school records match the selected status / search filters.
                            </td>
                          </tr>
                        ) : (
                          paginatedStatusRows.map((s: any, idx: number) => {
                            const isReported = s.submission_status === 'SUBMITTED' || s.submission_status === 'REPORTED';
                            const srNo = statusTablePageSize === -1 ? idx + 1 : (statusTablePage - 1) * statusTablePageSize + idx + 1;
                            return (
                              <tr key={s.school_code || s.id} className="hover:bg-blue-50/40 transition">
                                <td className="py-2.5 px-3 text-center font-mono text-slate-400 text-[11px]">{srNo}</td>
                                <td className="py-2.5 px-3 max-w-[220px]">
                                  <div
                                    onClick={() => setSelectedSchoolDossier(s)}
                                    className="font-bold text-slate-900 hover:text-blue-700 cursor-pointer truncate"
                                    title={s.school_name}
                                  >
                                    {s.school_name}
                                  </div>
                                  <div className="text-[10px] text-slate-400 font-mono">Code: {s.school_code}</div>
                                </td>
                                <td className="py-2.5 px-2.5 font-mono text-slate-500 whitespace-nowrap">{s.udise_code || 'N/A'}</td>
                                <td className="py-2.5 px-2.5 text-slate-600 whitespace-nowrap">{s.block}</td>
                                <td className="py-2.5 px-2.5 whitespace-nowrap">
                                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                    s.school_type === 'Senior Secondary School'
                                      ? 'bg-purple-100 text-purple-800'
                                      : s.school_type === 'High School'
                                      ? 'bg-amber-100 text-amber-800'
                                      : 'bg-blue-100 text-blue-800'
                                  }`}>
                                    {s.school_type}
                                  </span>
                                </td>
                                <td className="py-2.5 px-2 text-center whitespace-nowrap">
                                  {isReported ? (
                                    <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-emerald-100 text-emerald-800 border border-emerald-300">
                                      SUBMITTED
                                    </span>
                                  ) : (
                                    <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-rose-100 text-rose-800 border border-rose-300">
                                      NOT SUBMITTED
                                    </span>
                                  )}
                                </td>
                                <td className="py-2.5 px-2.5 whitespace-nowrap">
                                  {isReported ? (
                                    <div>
                                      <div className="font-bold text-slate-900">{s.submitter_name}</div>
                                      <div className="text-[10px] text-slate-500">{s.designation} • {s.mobile_number}</div>
                                    </div>
                                  ) : (
                                    <span className="text-slate-400 text-[11px]">Pending Submission</span>
                                  )}
                                </td>
                                <td className="py-2.5 px-2.5 text-center font-mono whitespace-nowrap">
                                  {isReported ? <span><strong>{s.total_present?.toLocaleString()}</strong> / {s.total_enrolled?.toLocaleString()}</span> : '—'}
                                </td>
                                <td className="py-2.5 px-2 text-center font-mono font-bold text-emerald-700 whitespace-nowrap">
                                  {s.student_attendance_pct !== null ? `${s.student_attendance_pct}%` : '—'}
                                </td>
                                <td className="py-2.5 px-2 text-center font-mono whitespace-nowrap">
                                  {isReported ? <span><strong>{s.teachers_present}</strong> / {s.teachers_working}</span> : '—'}
                                </td>
                                <td className="py-2.5 px-2 text-center font-mono font-bold text-indigo-700 whitespace-nowrap">
                                  {s.teacher_attendance_pct !== null ? `${s.teacher_attendance_pct}%` : '—'}
                                </td>
                                <td className="py-2.5 px-2 text-center whitespace-nowrap">
                                  <button
                                    onClick={() => setSelectedSchoolDossier(s)}
                                    className="px-2.5 py-1 bg-slate-100 hover:bg-blue-600 hover:text-white rounded text-[11px] font-bold transition flex items-center space-x-1 mx-auto cursor-pointer"
                                    title="View Classwise Roster & Submitter Dossier"
                                  >
                                    <Eye className="w-3 h-3" />
                                    <span>Dossier</span>
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination Footer */}
                  {statusTablePageSize !== -1 && totalStatusPages > 1 && (
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-2 pt-2 text-xs text-slate-600">
                      <div>
                        Showing <strong>{(statusTablePage - 1) * statusTablePageSize + 1}</strong> to <strong>{Math.min(statusTablePage * statusTablePageSize, filteredStatusRows.length)}</strong> of <strong>{filteredStatusRows.length}</strong> schools
                      </div>
                      <div className="flex space-x-1">
                        <button
                          onClick={() => setStatusTablePage((p) => Math.max(1, p - 1))}
                          disabled={statusTablePage === 1}
                          className="px-3 py-1 bg-slate-100 hover:bg-slate-200 disabled:opacity-40 rounded font-bold"
                        >
                          Previous
                        </button>
                        {Array.from({ length: Math.min(5, totalStatusPages) }, (_, i) => {
                          const pageNum = i + 1;
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setStatusTablePage(pageNum)}
                              className={`px-3 py-1 rounded font-bold ${
                                statusTablePage === pageNum ? 'bg-[#0B1E38] text-white' : 'bg-slate-100 hover:bg-slate-200'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                        <button
                          onClick={() => setStatusTablePage((p) => Math.min(totalStatusPages, p + 1))}
                          disabled={statusTablePage === totalStatusPages}
                          className="px-3 py-1 bg-slate-100 hover:bg-slate-200 disabled:opacity-40 rounded font-bold"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* ==================================================== */}
                {/* TABLE 2: SCHOOL & BLOCK CLASSWISE ATTENDANCE TABLE    */}
                {/* ==================================================== */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden space-y-3 p-5">
                  <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3 border-b border-slate-100 pb-4">
                    <div>
                      <div className="flex items-center space-x-2">
                        <GraduationCap className="w-5 h-5 text-indigo-700" />
                        <h3 className="text-base font-black text-[#0B1E38]">
                          Table 2: School & Block-Wise Classwise Attendance (Classes 6 to 12)
                        </h3>
                        <span className="px-2 py-0.5 bg-indigo-100 text-indigo-900 text-[11px] font-extrabold rounded-full">
                          Class Breakdown Matrix
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        Boys, Girls, and Total student attendance metrics for Classes 6, 7, 8, 9, 10, 11, 12 across all schools & blocks
                      </p>
                    </div>

                    <button
                      onClick={handleExportClasswiseAttendanceExcel}
                      className="px-3.5 py-1.5 bg-indigo-700 hover:bg-indigo-800 text-white rounded-lg font-bold text-xs transition flex items-center space-x-1.5 shadow-2xs cursor-pointer shrink-0"
                    >
                      <FileSpreadsheet className="w-3.5 h-3.5 text-indigo-200" />
                      <span>[ Download Classwise Attendance Excel (5 Sheets) ]</span>
                    </button>
                  </div>

                  {/* View Mode Toggle & Filter Bar */}
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                    <div className="flex bg-slate-100 p-0.5 rounded-xl text-xs font-bold border border-slate-200">
                      <button
                        onClick={() => setClasswiseViewMode('school')}
                        className={`px-3 py-1.5 rounded-lg transition cursor-pointer ${
                          classwiseViewMode === 'school' ? 'bg-[#0B1E38] text-white shadow-xs' : 'text-slate-600 hover:text-slate-900'
                        }`}
                      >
                        School-Wise Class Roster
                      </button>
                      <button
                        onClick={() => setClasswiseViewMode('block')}
                        className={`px-3 py-1.5 rounded-lg transition cursor-pointer ${
                          classwiseViewMode === 'block' ? 'bg-[#0B1E38] text-white shadow-xs' : 'text-slate-600 hover:text-slate-900'
                        }`}
                      >
                        Block-Wise Class Summary
                      </button>
                    </div>

                    {classwiseViewMode === 'school' && (
                      <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                        <select
                          value={classwiseBlockFilter}
                          onChange={(e) => { setClasswiseBlockFilter(e.target.value); setClasswiseTablePage(1); }}
                          className="px-2.5 py-1.5 text-xs font-semibold bg-white border border-slate-300 rounded-lg"
                        >
                          <option value="all">All Blocks</option>
                          {BLOCKS.map((b) => (<option key={b} value={b}>{b}</option>))}
                        </select>

                        <select
                          value={classwiseCategoryFilter}
                          onChange={(e) => { setClasswiseCategoryFilter(e.target.value); setClasswiseTablePage(1); }}
                          className="px-2.5 py-1.5 text-xs font-semibold bg-white border border-slate-300 rounded-lg"
                        >
                          <option value="all">All Categories</option>
                          {CATEGORIES.map((c) => (<option key={c} value={c}>{c}</option>))}
                        </select>

                        <input
                          type="text"
                          placeholder="Search school..."
                          value={classwiseTableSearch}
                          onChange={(e) => { setClasswiseTableSearch(e.target.value); setClasswiseTablePage(1); }}
                          className="text-xs px-2.5 py-1.5 border border-slate-300 rounded-lg bg-white w-36 sm:w-44"
                        />
                      </div>
                    )}
                  </div>

                  {/* Classwise School View Grid */}
                  {classwiseViewMode === 'school' ? (
                    <div className="overflow-x-auto rounded-xl border border-slate-200 max-h-[580px]">
                      <table className="w-full text-xs text-left">
                        <thead className="bg-[#0B1E38] text-white uppercase font-bold text-[10px] tracking-wider sticky top-0 z-10">
                          <tr>
                            <th className="py-2.5 px-3">School Name</th>
                            <th className="py-2.5 px-2 text-center">Block</th>
                            <th className="py-2.5 px-2 text-center">Category</th>
                            {['Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10', 'Class 11', 'Class 12'].map((cl) => (
                              <th key={cl} className="py-2.5 px-2 text-center border-l border-slate-700">
                                {cl} (P/E • %)
                              </th>
                            ))}
                            <th className="py-2.5 px-3 text-center bg-blue-950">Overall Att. %</th>
                            <th className="py-2.5 px-2 text-center">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 font-medium bg-white">
                          {paginatedClasswiseRows.map((s: any) => {
                            const isSub = s.submission_status === 'SUBMITTED' || s.submission_status === 'REPORTED';
                            return (
                              <tr key={s.school_code || s.id} className="hover:bg-blue-50/40 transition">
                                <td className="py-2.5 px-3 max-w-[200px]">
                                  <div
                                    onClick={() => setSelectedSchoolDossier(s)}
                                    className="font-bold text-slate-900 hover:text-blue-700 cursor-pointer truncate"
                                    title={s.school_name}
                                  >
                                    {s.school_name}
                                  </div>
                                  <div className="text-[10px] text-slate-400 font-mono">Code: {s.school_code}</div>
                                </td>
                                <td className="py-2.5 px-2 text-center text-slate-600 whitespace-nowrap">{s.block}</td>
                                <td className="py-2.5 px-2 text-center whitespace-nowrap">
                                  <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-slate-100 text-slate-700">
                                    {s.school_type === 'Senior Secondary School' ? 'Sr Sec' : s.school_type === 'High School' ? 'High' : 'Middle'}
                                  </span>
                                </td>

                                {/* Classes 6 to 12 Columns */}
                                {['class_6', 'class_7', 'class_8', 'class_9', 'class_10', 'class_11', 'class_12'].map((ck) => {
                                  const cData = s.classes?.[ck];
                                  const enr = cData?.enrolled || 0;
                                  const pres = cData?.present || 0;
                                  const pct = cData?.pct;
                                  return (
                                    <td key={ck} className="py-2 px-2 text-center font-mono text-[11px] border-l border-slate-100 whitespace-nowrap">
                                      {isSub && enr > 0 ? (
                                        <div>
                                          <span className="font-bold text-slate-800">{pres}</span>/<span className="text-slate-500">{enr}</span>
                                          <div className={`text-[10px] font-bold ${pct !== null && pct >= 75 ? 'text-emerald-700' : 'text-rose-700'}`}>
                                            {pct !== null ? `${pct}%` : '—'}
                                          </div>
                                        </div>
                                      ) : (
                                        <span className="text-slate-300">—</span>
                                      )}
                                    </td>
                                  );
                                })}

                                <td className="py-2.5 px-3 text-center font-mono font-black text-sm bg-blue-50/60 whitespace-nowrap">
                                  {isSub && s.student_attendance_pct !== null ? (
                                    <span className={s.student_attendance_pct >= 75 ? 'text-emerald-700' : 'text-rose-700'}>
                                      {s.student_attendance_pct}%
                                    </span>
                                  ) : (
                                    <span className="text-slate-400 font-normal text-xs">Pending</span>
                                  )}
                                </td>

                                <td className="py-2.5 px-2 text-center whitespace-nowrap">
                                  <button
                                    onClick={() => setSelectedSchoolDossier(s)}
                                    className="px-2 py-1 bg-slate-100 hover:bg-indigo-600 hover:text-white rounded text-[10px] font-bold"
                                  >
                                    Dossier
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    /* Block-Wise Class Summary Matrix */
                    <div className="overflow-x-auto rounded-xl border border-slate-200">
                      <table className="w-full text-xs text-left">
                        <thead className="bg-[#0B1E38] text-white uppercase font-bold text-[10px] tracking-wider">
                          <tr>
                            <th className="py-2.5 px-3.5">Block Name</th>
                            <th className="py-2.5 px-2.5 text-center">Total Schools</th>
                            <th className="py-2.5 px-2.5 text-center">Reported</th>
                            <th className="py-2.5 px-2 text-center">Class 6 %</th>
                            <th className="py-2.5 px-2 text-center">Class 7 %</th>
                            <th className="py-2.5 px-2 text-center">Class 8 %</th>
                            <th className="py-2.5 px-2 text-center">Class 9 %</th>
                            <th className="py-2.5 px-2 text-center">Class 10 %</th>
                            <th className="py-2.5 px-2 text-center">Class 11 %</th>
                            <th className="py-2.5 px-2 text-center">Class 12 %</th>
                            <th className="py-2.5 px-3 text-center bg-blue-950">Overall Student %</th>
                            <th className="py-2.5 px-3 text-center">Faculty Att. %</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 font-medium bg-white">
                          {dashboardData.block_summary?.map((b: any) => (
                            <tr key={b.block} className="hover:bg-slate-50 transition">
                              <td className="py-2.5 px-3.5 font-bold text-slate-900 flex items-center space-x-2">
                                <Building2 className="w-4 h-4 text-blue-600 shrink-0" />
                                <span>{b.block} Block</span>
                              </td>
                              <td className="py-2.5 px-2.5 text-center font-mono">{b.total_schools}</td>
                              <td className="py-2.5 px-2.5 text-center font-mono font-bold text-blue-700">{b.reported_schools}</td>
                              <td className="py-2.5 px-2 text-center font-mono font-bold text-emerald-700">{b.class_6_pct !== null ? `${b.class_6_pct}%` : 'N/A'}</td>
                              <td className="py-2.5 px-2 text-center font-mono font-bold text-emerald-700">{b.class_7_pct !== null ? `${b.class_7_pct}%` : 'N/A'}</td>
                              <td className="py-2.5 px-2 text-center font-mono font-bold text-emerald-700">{b.class_8_pct !== null ? `${b.class_8_pct}%` : 'N/A'}</td>
                              <td className="py-2.5 px-2 text-center font-mono font-bold text-emerald-700">{b.class_9_pct !== null ? `${b.class_9_pct}%` : 'N/A'}</td>
                              <td className="py-2.5 px-2 text-center font-mono font-bold text-emerald-700">{b.class_10_pct !== null ? `${b.class_10_pct}%` : 'N/A'}</td>
                              <td className="py-2.5 px-2 text-center font-mono font-bold text-emerald-700">{b.class_11_pct !== null ? `${b.class_11_pct}%` : 'N/A'}</td>
                              <td className="py-2.5 px-2 text-center font-mono font-bold text-emerald-700">{b.class_12_pct !== null ? `${b.class_12_pct}%` : 'N/A'}</td>
                              <td className="py-2.5 px-3 text-center font-mono font-black text-sm bg-blue-50 text-emerald-800">
                                {b.student_attendance_pct !== null ? `${b.student_attendance_pct}%` : 'N/A'}
                              </td>
                              <td className="py-2.5 px-3 text-center font-mono font-bold text-indigo-700">
                                {b.teacher_attendance_pct !== null ? `${b.teacher_attendance_pct}%` : 'N/A'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {classwiseViewMode === 'school' && totalClasswisePages > 1 && (
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-2 pt-2 text-xs text-slate-600">
                      <div>
                        Showing <strong>{(classwiseTablePage - 1) * classwiseTablePageSize + 1}</strong> to <strong>{Math.min(classwiseTablePage * classwiseTablePageSize, filteredClasswiseRows.length)}</strong> of <strong>{filteredClasswiseRows.length}</strong> schools
                      </div>
                      <div className="flex space-x-1">
                        <button
                          onClick={() => setClasswiseTablePage((p) => Math.max(1, p - 1))}
                          disabled={classwiseTablePage === 1}
                          className="px-3 py-1 bg-slate-100 hover:bg-slate-200 disabled:opacity-40 rounded font-bold"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => setClasswiseTablePage((p) => Math.min(totalClasswisePages, p + 1))}
                          disabled={classwiseTablePage === totalClasswisePages}
                          className="px-3 py-1 bg-slate-100 hover:bg-slate-200 disabled:opacity-40 rounded font-bold"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* ==================================================== */}
                {/* TABLE 3: BLOCK-WISE SUMMARY TABLE                    */}
                {/* ==================================================== */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden space-y-3 p-5">
                  <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                    <div>
                      <div className="flex items-center space-x-2">
                        <Building2 className="w-5 h-5 text-blue-600" />
                        <h3 className="text-base font-black text-[#0B1E38]">
                          Table 3: Block-Wise Attendance Summary (5 Administrative Blocks)
                        </h3>
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-900 text-[11px] font-extrabold rounded-full">
                          District Geographic Matrix
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        Comparative reporting compliance, student volume, class percentages, and faculty duty statistics
                      </p>
                    </div>
                  </div>

                  <div className="overflow-x-auto rounded-xl border border-slate-200">
                    <table className="w-full text-xs text-left">
                      <thead className="bg-[#0B1E38] text-white uppercase font-bold text-[10px] tracking-wider">
                        <tr>
                          <th className="py-2.5 px-3.5">Block Name</th>
                          <th className="py-2.5 px-2.5 text-center">Total Schools</th>
                          <th className="py-2.5 px-2.5 text-center">Reported</th>
                          <th className="py-2.5 px-2.5 text-center">Pending</th>
                          <th className="py-2.5 px-2.5 text-center">Reporting %</th>
                          <th className="py-2.5 px-3 text-center">Students (Pres / Enr)</th>
                          <th className="py-2.5 px-3 text-center">Student Att. %</th>
                          <th className="py-2.5 px-3 text-center">Faculty (Pres / Work)</th>
                          <th className="py-2.5 px-3 text-center">Faculty Att. %</th>
                          <th className="py-2.5 px-2.5 text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 font-medium bg-white">
                        {dashboardData.block_summary?.map((b: any) => (
                          <tr
                            key={b.block}
                            className={`hover:bg-blue-50/50 cursor-pointer transition ${
                              filterBlock === b.block ? 'bg-blue-50/80 font-bold' : ''
                            }`}
                          >
                            <td className="py-2.5 px-3.5 font-bold text-slate-900 flex items-center space-x-2">
                              <Building2 className="w-4 h-4 text-blue-600 shrink-0" />
                              <span>{b.block} Block</span>
                            </td>
                            <td className="py-2.5 px-2.5 text-center font-mono">{b.total_schools}</td>
                            <td className="py-2.5 px-2.5 text-center font-mono font-bold text-blue-700">{b.reported_schools}</td>
                            <td className="py-2.5 px-2.5 text-center font-mono text-rose-700">{b.pending_schools}</td>
                            <td className="py-2.5 px-2.5 text-center font-mono font-bold">{b.reporting_pct}%</td>
                            <td className="py-2.5 px-3 text-center font-mono">
                              {(b.present || 0).toLocaleString()} / {(b.enrolled || 0).toLocaleString()}
                            </td>
                            <td className="py-2.5 px-3 text-center font-mono font-bold text-emerald-700">
                              {b.student_attendance_pct !== null ? `${b.student_attendance_pct}%` : 'N/A'}
                            </td>
                            <td className="py-2.5 px-3 text-center font-mono">
                              {(b.teachers_present || 0).toLocaleString()} / {(b.teachers_working || 0).toLocaleString()}
                            </td>
                            <td className="py-2.5 px-3 text-center font-mono font-bold text-indigo-700">
                              {b.teacher_attendance_pct !== null ? `${b.teacher_attendance_pct}%` : 'N/A'}
                            </td>
                            <td className="py-2.5 px-2.5 text-center">
                              <button
                                onClick={(e) => { e.stopPropagation(); setFilterBlock(b.block); }}
                                className="px-2.5 py-1 bg-blue-100 hover:bg-blue-200 text-blue-800 rounded text-[10px] font-bold"
                              >
                                Filter Block
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* ==================================================== */}
                {/* TABLE 4: DATE-WISE ATTENDANCE SUMMARY TABLE          */}
                {/* ==================================================== */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden space-y-3 p-5">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 border-b border-slate-100 pb-4">
                    <div>
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-5 h-5 text-emerald-600" />
                        <h3 className="text-base font-black text-[#0B1E38]">
                          Table 4: Date-Wise Attendance Summary Log
                        </h3>
                        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-900 text-[11px] font-extrabold rounded-full">
                          Academic Session 2026-27 (All Available Dates)
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        Chronological date-by-date attendance log with instant snapshot view and full date drill-down
                      </p>
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        placeholder="Search date..."
                        value={dateSummarySearch}
                        onChange={(e) => { setDateSummarySearch(e.target.value); setDateSummaryPage(1); }}
                        className="text-xs px-2.5 py-1.5 border border-slate-300 rounded-lg bg-white w-36"
                      />
                      <button
                        onClick={handleExportAllDatesConsolidatedExcel}
                        className="px-3 py-1.5 bg-[#0B1E38] hover:bg-blue-950 text-white rounded-lg font-bold text-xs transition flex items-center space-x-1 shadow-2xs"
                      >
                        <Download className="w-3.5 h-3.5 text-amber-400" />
                        <span>[ Export All Dates ]</span>
                      </button>
                    </div>
                  </div>

                  <div className="overflow-x-auto rounded-xl border border-slate-200 max-h-[500px]">
                    <table className="w-full text-xs text-left">
                      <thead className="bg-[#0B1E38] text-white uppercase font-bold text-[10px] tracking-wider sticky top-0 z-10">
                        <tr>
                          <th className="py-2.5 px-3.5">Attendance Date</th>
                          <th className="py-2.5 px-2.5 text-center">Expected</th>
                          <th className="py-2.5 px-2.5 text-center">Submitted</th>
                          <th className="py-2.5 px-2.5 text-center">Pending</th>
                          <th className="py-2.5 px-2.5 text-center">Reporting %</th>
                          <th className="py-2.5 px-3 text-center">Students (Pres / Enr)</th>
                          <th className="py-2.5 px-3 text-center">Student Att. %</th>
                          <th className="py-2.5 px-3 text-center">Faculty (Pres / Work)</th>
                          <th className="py-2.5 px-3 text-center">Faculty Att. %</th>
                          <th className="py-2.5 px-2.5 text-center">Snapshot Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 font-medium bg-white">
                        {paginatedDateRows.map((d: any) => (
                          <tr
                            key={d.date}
                            className={`hover:bg-blue-50/50 transition ${
                              filterDate === d.date ? 'bg-blue-50/90 font-bold' : ''
                            }`}
                          >
                            <td className="py-2.5 px-3.5 font-bold text-slate-900 font-mono">
                              {formatDisplayDate(d.date)}
                            </td>
                            <td className="py-2.5 px-2.5 text-center font-mono">{d.expected_schools}</td>
                            <td className="py-2.5 px-2.5 text-center font-mono font-bold text-blue-700">{d.submitted_schools}</td>
                            <td className="py-2.5 px-2.5 text-center font-mono text-rose-700">{d.pending_schools}</td>
                            <td className="py-2.5 px-2.5 text-center font-mono font-bold">{d.reporting_pct}%</td>
                            <td className="py-2.5 px-3 text-center font-mono">
                              {(d.total_present || 0).toLocaleString()} / {(d.total_enrolled || 0).toLocaleString()}
                            </td>
                            <td className="py-2.5 px-3 text-center font-mono font-bold text-emerald-700">
                              {d.student_attendance_pct !== null ? `${d.student_attendance_pct}%` : 'N/A'}
                            </td>
                            <td className="py-2.5 px-3 text-center font-mono">
                              {(d.teachers_present || 0).toLocaleString()} / {(d.teachers_working || 0).toLocaleString()}
                            </td>
                            <td className="py-2.5 px-3 text-center font-mono font-bold text-indigo-700">
                              {d.teacher_attendance_pct !== null ? `${d.teacher_attendance_pct}%` : 'N/A'}
                            </td>
                            <td className="py-2.5 px-2.5 text-center">
                              <button
                                onClick={() => { setFilterDate(d.date); setDateFilterMode('single'); }}
                                className={`px-2.5 py-1 rounded text-[10px] font-black transition cursor-pointer ${
                                  filterDate === d.date
                                    ? 'bg-emerald-600 text-white'
                                    : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'
                                }`}
                              >
                                {filterDate === d.date ? '✓ Active Date' : 'View Date Snapshot'}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {totalDatePages > 1 && (
                    <div className="flex justify-between items-center pt-2 text-xs text-slate-600">
                      <div>
                        Showing <strong>{(dateSummaryPage - 1) * dateSummaryPageSize + 1}</strong> to <strong>{Math.min(dateSummaryPage * dateSummaryPageSize, filteredDateRows.length)}</strong> of <strong>{filteredDateRows.length}</strong> dates
                      </div>
                      <div className="flex space-x-1">
                        <button
                          onClick={() => setDateSummaryPage((p) => Math.max(1, p - 1))}
                          disabled={dateSummaryPage === 1}
                          className="px-3 py-1 bg-slate-100 hover:bg-slate-200 disabled:opacity-40 rounded font-bold"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => setDateSummaryPage((p) => Math.min(totalDatePages, p + 1))}
                          disabled={dateSummaryPage === totalDatePages}
                          className="px-3 py-1 bg-slate-100 hover:bg-slate-200 disabled:opacity-40 rounded font-bold"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : null}

            {/* ---------------------------------------------------- */}
            {/* 5A. DEO EXECUTIVE MULTI-SHEET EXCEL EXPORT HUB       */}
            {/* (Moved to the VERY BOTTOM of DEO Dashboard)          */}
            {/* ---------------------------------------------------- */}
            <div className="bg-[#0B1E38] text-white rounded-2xl p-5 shadow-sm border border-slate-800 space-y-3">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 border-b border-slate-700 pb-3">
                <div>
                  <div className="flex items-center space-x-2">
                    <FileSpreadsheet className="w-5 h-5 text-emerald-400" />
                    <h3 className="text-base font-black tracking-tight">Official Multi-Sheet Excel Reports Hub</h3>
                    <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-300 text-[10px] font-extrabold rounded border border-emerald-500/30">
                      Production Analytics
                    </span>
                  </div>
                  <p className="text-xs text-slate-300 mt-0.5">
                    Download comprehensive multi-tab workbooks across all 403 schools, 5 blocks, and historical session dates
                  </p>
                </div>
                <div className="text-xs text-slate-400 font-mono">
                  Target Snapshot: <strong>{filterDate !== 'all' ? formatDisplayDate(filterDate) : 'Full Academic Session 2026-27'}</strong>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                {/* Export 1: Selected Date 10-Sheet Excel */}
                <button
                  onClick={handleExportSelectedDate10SheetExcel}
                  className="p-3.5 bg-emerald-700 hover:bg-emerald-600 text-white rounded-xl font-bold text-xs transition flex flex-col items-start gap-1 shadow-xs cursor-pointer border border-emerald-500/40 text-left"
                  title="Download comprehensive 10-sheet workbook for the selected date"
                >
                  <div className="flex items-center space-x-2 w-full justify-between">
                    <span className="font-black text-sm flex items-center gap-1.5">
                      <FileSpreadsheet className="w-4 h-4 text-emerald-200 shrink-0" />
                      <span>1. Download Selected Date</span>
                    </span>
                    <span className="text-[10px] bg-emerald-900/60 px-2 py-0.5 rounded font-mono">10 Sheets</span>
                  </div>
                  <span className="text-[11px] text-emerald-100 font-normal">
                    All 403 Schools • Classes 6–12 • Block Matrix • Teacher Roster • Top/Bottom
                  </span>
                </button>

                {/* Export 2: Date Range 10-Sheet Excel */}
                <button
                  onClick={handleExportDateRange10SheetExcel}
                  className="p-3.5 bg-indigo-700 hover:bg-indigo-600 text-white rounded-xl font-bold text-xs transition flex flex-col items-start gap-1 shadow-xs cursor-pointer border border-indigo-500/40 text-left"
                  title="Download aggregated 10-sheet workbook across the selected date range"
                >
                  <div className="flex items-center space-x-2 w-full justify-between">
                    <span className="font-black text-sm flex items-center gap-1.5">
                      <GraduationCap className="w-4 h-4 text-indigo-200 shrink-0" />
                      <span>2. Download Date Range</span>
                    </span>
                    <span className="text-[10px] bg-indigo-900/60 px-2 py-0.5 rounded font-mono">10 Sheets</span>
                  </div>
                  <span className="text-[11px] text-indigo-100 font-normal">
                    Multi-Day Aggregation • Summed Counts • Overall Attendance % • Trend Analysis
                  </span>
                </button>

                {/* Export 3: All Available Dates 10-Sheet Master Excel */}
                <button
                  onClick={handleExportAllAvailableDates10SheetExcel}
                  className="p-3.5 bg-amber-600 hover:bg-amber-500 text-slate-950 rounded-xl font-black text-xs transition flex flex-col items-start gap-1 shadow-xs cursor-pointer border border-amber-400 text-left"
                  title="Download complete 10-sheet master archive covering all available session dates in Academic Year 2026-27"
                >
                  <div className="flex items-center space-x-2 w-full justify-between">
                    <span className="font-black text-sm flex items-center gap-1.5 text-slate-950">
                      <Download className="w-4 h-4 text-slate-900 shrink-0" />
                      <span>3. Download All Available Dates</span>
                    </span>
                    <span className="text-[10px] bg-amber-800 text-amber-100 px-2 py-0.5 rounded font-mono font-bold">10 Sheets Master</span>
                  </div>
                  <span className="text-[11px] text-slate-900 font-medium">
                    Full Academic Session 2026-27 • Complete Archive • All 403 Schools
                  </span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ==================================================== */}
        {/* TAB 2: DAILY SUBMISSION FORM                        */}
        {/* ==================================================== */}
        {activeTab === 'submit' && (
          <div className="space-y-6">
            {/* Duplicate Submission Warning Banner */}
            {duplicateWarning && (
              <div className="bg-amber-50 border-2 border-amber-500 rounded-2xl p-5 shadow-sm text-slate-900 space-y-3">
                <div className="flex items-start space-x-3.5">
                  <div className="w-11 h-11 bg-amber-500 text-slate-950 rounded-xl flex items-center justify-center shrink-0 shadow-xs font-black text-xl">
                    ⚠️
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <h3 className="text-base font-black text-amber-950">
                        {duplicateWarning.message || 'इस विद्यालय की Attendance Report इस दिन के लिए पहले ही दर्ज की जा चुकी है।'}
                      </h3>
                      <button
                        onClick={() => setDuplicateWarning(null)}
                        className="text-xs font-bold text-amber-800 hover:text-amber-950 underline cursor-pointer"
                      >
                        Dismiss
                      </button>
                    </div>
                    <p className="text-xs text-amber-900 mt-1 font-medium">
                      विद्यालय: <strong>{duplicateWarning.school_name || formSelectedSchool?.school_name}</strong> (Code: <strong>{duplicateWarning.school_code || formSelectedSchool?.school_code}</strong> | UDISE: <strong>{duplicateWarning.udise_code || formSelectedSchool?.udise_code}</strong>)
                    </p>
                    <p className="text-xs text-amber-800 mt-0.5">
                      दिनांक: <strong>{duplicateWarning.date || formDate}</strong> — एक विद्यालय द्वारा एक ही दिनांक के लिए केवल एक बार Attendance दर्ज करने की अनुमति है।
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Submission Error Banner */}
            {submissionError && (
              <div className="bg-rose-50 border-2 border-rose-500 rounded-2xl p-5 shadow-sm text-slate-900 space-y-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-rose-600 rounded-full flex items-center justify-center text-white shrink-0 shadow-xs font-bold">
                      ✕
                    </div>
                    <div>
                      <h3 className="text-sm sm:text-base font-black text-rose-950">
                        Attendance Report दर्ज करने में त्रुटि
                      </h3>
                      <p className="text-xs text-rose-800 mt-0.5">
                        {submissionError}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSubmissionError(null)}
                    className="text-xs font-bold text-rose-800 hover:text-rose-950 underline"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}

            {/* Official Hindi Attendance Report Receipt */}
            {submissionSuccess && (
              <div className="bg-gradient-to-b from-emerald-50 to-white border-2 border-emerald-600 rounded-2xl p-6 shadow-md text-slate-900 space-y-4">
                {/* Receipt Header */}
                <div className="border-b border-emerald-200 pb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                  <div className="flex items-start space-x-3.5">
                    <div className="w-12 h-12 bg-emerald-600 rounded-2xl flex items-center justify-center text-white shrink-0 shadow-sm">
                      <CheckCircle2 className="w-7 h-7" />
                    </div>
                    <div>
                      <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-100 border border-emerald-300 text-emerald-900 text-[11px] font-extrabold mb-1">
                        <span>🇮🇳</span>
                        <span>Attendance Report प्राप्ति रसीद</span>
                      </div>
                      <h3 className="text-lg font-black text-emerald-950">
                        आपकी Attendance Report सफलतापूर्वक दर्ज कर ली गई है।
                      </h3>
                      <div className="text-xs text-emerald-800 mt-0.5 font-medium">
                        प्राप्तकर्ता: <strong>जिला शिक्षा अधिकारी, नूंह</strong> (Department of School Education, Haryana)
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 shrink-0">
                    <button
                      onClick={handlePrintReport}
                      className="px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-xs font-bold transition flex items-center space-x-1.5 shadow-xs cursor-pointer"
                    >
                      <Printer className="w-4 h-4" />
                      <span>Print Slip</span>
                    </button>
                    <button
                      onClick={() => setSubmissionSuccess(null)}
                      className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition"
                    >
                      Close
                    </button>
                  </div>
                </div>

                {/* Receipt Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-xs">
                  <div className="bg-white p-3.5 rounded-xl border border-emerald-100 shadow-2xs space-y-1">
                    <span className="text-[10px] font-bold uppercase text-slate-500 tracking-wider">Attendance Date & Ack ID</span>
                    <div className="text-sm font-black text-blue-950">{submissionSuccess.attendance_date}</div>
                    <div className="font-mono text-[11px] font-bold text-emerald-800">{submissionSuccess.acknowledgement_number}</div>
                  </div>

                  <div className="bg-white p-3.5 rounded-xl border border-emerald-100 shadow-2xs space-y-1">
                    <span className="text-[10px] font-bold uppercase text-slate-500 tracking-wider">School Details</span>
                    <div className="text-xs font-bold text-slate-900">{submissionSuccess.school_name}</div>
                    <div className="text-[11px] text-slate-600">
                      Code: <strong>{submissionSuccess.school_code}</strong> | UDISE: <strong>{submissionSuccess.udise_code}</strong>
                    </div>
                  </div>

                  <div className="bg-white p-3.5 rounded-xl border border-emerald-100 shadow-2xs space-y-1">
                    <span className="text-[10px] font-bold uppercase text-slate-500 tracking-wider">Submitter & Receiving Officer</span>
                    <div className="text-xs font-bold text-slate-900">{submissionSuccess.submitter_name} ({submissionSuccess.designation})</div>
                    <div className="text-[11px] text-emerald-800 font-bold">
                      Receiving: जिला शिक्षा अधिकारी, नूंह
                    </div>
                  </div>

                  <div className="bg-white p-3.5 rounded-xl border border-emerald-100 shadow-2xs space-y-1">
                    <span className="text-[10px] font-bold uppercase text-slate-500 tracking-wider">Students Attendance Summary</span>
                    <div className="text-xs font-bold text-slate-900">
                      Present: <strong className="text-emerald-700">{submissionSuccess.summary?.totalPresent ?? 0}</strong> / Enrolled: <strong>{submissionSuccess.summary?.totalEnrolled ?? 0}</strong>
                    </div>
                    <div className="text-[11px] font-bold text-blue-900">
                      Attendance: {submissionSuccess.summary?.attendancePct ?? 'N/A'}%
                    </div>
                  </div>

                  <div className="bg-white p-3.5 rounded-xl border border-emerald-100 shadow-2xs space-y-1">
                    <span className="text-[10px] font-bold uppercase text-slate-500 tracking-wider">Faculty Attendance Summary</span>
                    <div className="text-xs font-bold text-slate-900">
                      Present: <strong className="text-emerald-700">{submissionSuccess.teachers?.present ?? 0}</strong> / Working: <strong>{submissionSuccess.teachers?.totalWorking ?? 0}</strong>
                    </div>
                    <div className="text-[11px] text-slate-600">
                      Leave: {submissionSuccess.teachers?.leave ?? 0} | Duty: {submissionSuccess.teachers?.otherDuty ?? 0}
                    </div>
                  </div>

                  <div className="bg-white p-3.5 rounded-xl border border-emerald-100 shadow-2xs space-y-1">
                    <span className="text-[10px] font-bold uppercase text-slate-500 tracking-wider">Verification Status</span>
                    <div className="flex items-center space-x-1.5 text-xs font-black text-emerald-800">
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
                      <span>Google Sheet Primary: SYNCED</span>
                    </div>
                    <div className="text-[10px] text-slate-500 font-mono">
                      Recorded: {new Date(submissionSuccess.submitted_at || Date.now()).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Live Timing Window & Form Status Banner */}
            <div className={`p-4 sm:p-5 rounded-2xl border shadow-xs flex flex-col md:flex-row justify-between items-start md:items-center gap-4 ${
              formControl.is_open
                ? 'bg-emerald-50/90 border-emerald-300 text-emerald-950'
                : 'bg-rose-50/90 border-rose-300 text-rose-950'
            }`}>
              <div className="flex items-center space-x-3.5">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-white shrink-0 shadow-xs ${
                  formControl.is_open ? 'bg-emerald-600' : 'bg-rose-600'
                }`}>
                  {formControl.is_open ? <Unlock className="w-6 h-6" /> : <Lock className="w-6 h-6" />}
                </div>
                <div>
                  <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                    <h3 className="text-sm sm:text-base font-black uppercase tracking-wide">
                      {formControl.is_open ? '🟢 DAILY ATTENDANCE SUBMISSION WINDOW IS OPEN' : '🔴 DAILY ATTENDANCE SUBMISSION WINDOW IS CLOSED'}
                    </h3>
                    <span className={`text-[10px] font-extrabold px-2.5 py-0.5 rounded-full ${
                      formControl.is_open ? 'bg-emerald-200 text-emerald-900 border border-emerald-300' : 'bg-rose-200 text-rose-900 border border-rose-300'
                    }`}>
                      {formControl.mode === 'auto' ? 'Automated Schedule' : 'DEO Override Active'}
                    </span>
                  </div>
                  <p className="text-xs mt-1 opacity-90">
                    Daily Submission Hours: <strong>08:00 AM – 09:30 AM IST</strong> • India Standard Time: <strong>{currentTimeIST || 'IST'}</strong>
                  </p>
                  <p className="text-[11px] text-slate-600 mt-0.5">
                    Status: <strong className={formControl.is_open ? 'text-emerald-800' : 'text-rose-800'}>{formControl.status_reason}</strong>
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2 shrink-0">
                <span className={`text-xs font-mono font-black px-3.5 py-2 rounded-xl border ${
                  formControl.is_open ? 'bg-emerald-100 border-emerald-300 text-emerald-900' : 'bg-rose-100 border-rose-300 text-rose-900'
                }`}>
                  {formControl.status_label}
                </span>
                {isDEO && (
                  <button
                    type="button"
                    onClick={() => handleSetFormControl(formControl.is_open ? 'force_closed' : 'force_open')}
                    disabled={isUpdatingControl}
                    className={`px-3.5 py-2 rounded-xl text-xs font-black text-white transition shadow-xs cursor-pointer ${
                      formControl.is_open ? 'bg-rose-700 hover:bg-rose-800' : 'bg-emerald-700 hover:bg-emerald-800'
                    }`}
                  >
                    {formControl.is_open ? '[ Close Form ]' : '[ Open Form ]'}
                  </button>
                )}
              </div>
            </div>

            {/* Daily Form */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
              className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-6"
            >
              {/* Form Title */}
              <div className="border-b border-slate-200 pb-4 flex justify-between items-center">
                <div>
                  <h2 className="text-base font-black text-[#0B1E38]">
                    Daily Student & Faculty Attendance Submission
                  </h2>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Select Block → School Category → School to record attendance
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handlePrefillSample}
                  className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-xs font-bold transition border border-blue-200"
                >
                  Prefill Verified Sample
                </button>
              </div>

              {/* Section 1: Submitter Info */}
              <div className="space-y-3">
                <div className="text-xs font-extrabold text-blue-800 uppercase tracking-wider flex items-center gap-1.5">
                  <User className="w-4 h-4" />
                  <span>Section 1: Basic Submitter & Date Information</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">Attendance Date *</label>
                    <input
                      type="date"
                      value={formDate}
                      onChange={(e) => setFormDate(e.target.value)}
                      className="w-full text-xs px-3 py-2 border border-slate-300 rounded-lg bg-slate-50 focus:bg-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">Submitter Name *</label>
                    <input
                      type="text"
                      placeholder="e.g. Ramesh Chandra"
                      value={submitterName}
                      onChange={(e) => setSubmitterName(e.target.value)}
                      className="w-full text-xs px-3 py-2 border border-slate-300 rounded-lg bg-slate-50 focus:bg-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">Designation *</label>
                    <select
                      value={designation}
                      onChange={(e) => setDesignation(e.target.value)}
                      className="w-full text-xs px-3 py-2 border border-slate-300 rounded-lg bg-slate-50 font-semibold focus:bg-white"
                    >
                      {DESIGNATIONS.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">Contact Mobile Number *</label>
                    <input
                      type="tel"
                      maxLength={10}
                      placeholder="10-digit mobile"
                      value={mobileNumber}
                      onChange={(e) => setMobileNumber(e.target.value)}
                      className="w-full text-xs px-3 py-2 border border-slate-300 rounded-lg bg-slate-50 focus:bg-white font-mono"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Section 2: Block -> Category -> School Cascade */}
              <div className="space-y-3 pt-3 border-t border-slate-200">
                <div className="text-xs font-extrabold text-blue-800 uppercase tracking-wider flex items-center gap-1.5">
                  <Building2 className="w-4 h-4" />
                  <span>Section 2: Cascading School Hierarchy (Block → Category → School)</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                  {/* Block */}
                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">1. Select Block *</label>
                    <select
                      value={formBlock}
                      onChange={(e) => handleFormBlockChange(e.target.value)}
                      className="w-full text-xs px-3 py-2 border border-slate-300 rounded-lg bg-slate-50 font-black text-[#0B1E38] focus:bg-white"
                    >
                      {BLOCKS.map((b) => (
                        <option key={b} value={b}>{b} Block</option>
                      ))}
                    </select>
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">2. Select School Category *</label>
                    <select
                      value={formCategory}
                      onChange={(e) => handleFormCategoryChange(e.target.value)}
                      className="w-full text-xs px-3 py-2 border border-slate-300 rounded-lg bg-slate-50 font-bold focus:bg-white"
                    >
                      {formAvailableCategories.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>

                  {/* School */}
                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">3. Select School *</label>
                    <select
                      value={formSchoolCode}
                      onChange={(e) => handleFormSchoolChange(e.target.value)}
                      className="w-full text-xs px-3 py-2 border border-slate-300 rounded-lg bg-slate-50 font-semibold focus:bg-white"
                    >
                      <option value="">-- Choose School ({formAvailableSchools.length} available) --</option>
                      {formAvailableSchools.map((s) => (
                        <option key={s.school_code} value={s.school_code}>
                          {s.school_name} ({s.school_code})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {formSelectedSchool && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl text-xs flex justify-between items-center text-blue-950 font-medium">
                    <div>
                      Selected: <strong>{formSelectedSchool.school_name}</strong> • Code: <strong>{formSelectedSchool.school_code}</strong> • UDISE: <strong>{formSelectedSchool.udise_code}</strong>
                    </div>
                    <span className="px-2 py-0.5 bg-blue-700 text-white rounded text-[10px] font-bold">
                      {formSelectedSchool.school_type}
                    </span>
                  </div>
                )}
              </div>

              {/* Section 3: Class-Wise Student Attendance Table */}
              <div className="space-y-3 pt-3 border-t border-slate-200">
                <div className="flex justify-between items-center">
                  <div className="text-xs font-extrabold text-blue-800 uppercase tracking-wider flex items-center gap-1.5">
                    <GraduationCap className="w-4 h-4" />
                    <span>Section 3: Class-Wise Student Attendance (Classes 6 to 12)</span>
                  </div>
                  <div className="text-xs font-black text-emerald-800 font-mono">
                    Total Enrolled: {formStudentSummary.totalEnrolled} • Total Present: {formStudentSummary.totalPresent} ({formStudentSummary.attendancePct ?? 0}%)
                  </div>
                </div>

                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  <table className="w-full text-xs text-left">
                    <thead className="bg-[#0B1E38] text-white uppercase font-bold text-[10px]">
                      <tr>
                        <th className="py-2.5 px-3">Class</th>
                        <th className="py-2.5 px-2 text-center">Boys Enrolled</th>
                        <th className="py-2.5 px-2 text-center">Boys Present</th>
                        <th className="py-2.5 px-2 text-center">Girls Enrolled</th>
                        <th className="py-2.5 px-2 text-center">Girls Present</th>
                        <th className="py-2.5 px-2 text-center">Total Enrolled</th>
                        <th className="py-2.5 px-2 text-center">Total Present</th>
                        <th className="py-2.5 px-3 text-center">Attendance %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 font-medium">
                      {classRows.map((r, idx) => {
                        const st = formClassStats[idx];
                        return (
                          <tr key={r.className} className="hover:bg-slate-50">
                            <td className="py-2 px-3 font-bold text-slate-900">{r.className}</td>
                            <td className="py-1 px-2 text-center">
                              <input
                                type="number"
                                min={0}
                                value={r.boysEnrolled}
                                onChange={(e) => handleClassValueChange(idx, 'boysEnrolled', e.target.value)}
                                className="w-16 text-center py-1 border border-slate-300 rounded font-mono"
                                placeholder="0"
                              />
                            </td>
                            <td className="py-1 px-2 text-center">
                              <input
                                type="number"
                                min={0}
                                value={r.boysPresent}
                                onChange={(e) => handleClassValueChange(idx, 'boysPresent', e.target.value)}
                                className="w-16 text-center py-1 border border-emerald-300 bg-emerald-50/40 rounded font-mono font-bold text-emerald-800"
                                placeholder="0"
                              />
                            </td>
                            <td className="py-1 px-2 text-center">
                              <input
                                type="number"
                                min={0}
                                value={r.girlsEnrolled}
                                onChange={(e) => handleClassValueChange(idx, 'girlsEnrolled', e.target.value)}
                                className="w-16 text-center py-1 border border-slate-300 rounded font-mono"
                                placeholder="0"
                              />
                            </td>
                            <td className="py-1 px-2 text-center">
                              <input
                                type="number"
                                min={0}
                                value={r.girlsPresent}
                                onChange={(e) => handleClassValueChange(idx, 'girlsPresent', e.target.value)}
                                className="w-16 text-center py-1 border border-emerald-300 bg-emerald-50/40 rounded font-mono font-bold text-emerald-800"
                                placeholder="0"
                              />
                            </td>
                            <td className="py-2 px-2 text-center font-mono font-bold text-slate-800">{st.totalEnrolled}</td>
                            <td className="py-2 px-2 text-center font-mono font-bold text-emerald-700">{st.totalPresent}</td>
                            <td className="py-2 px-3 text-center font-mono font-extrabold text-emerald-800">{st.attendancePct}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Section 4: Teacher Attendance */}
              <div className="space-y-3 pt-3 border-t border-slate-200">
                <div className="flex justify-between items-center">
                  <div className="text-xs font-extrabold text-blue-800 uppercase tracking-wider flex items-center gap-1.5">
                    <Users className="w-4 h-4" />
                    <span>Section 4: Faculty & Staff Attendance</span>
                  </div>
                  <div className="text-xs font-bold text-indigo-900 font-mono">
                    Total Working: {formTeacherStats.totalWorking} • Present: {formTeacherStats.present} ({formTeacherStats.attendancePct ?? 0}%)
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-xs">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">Total Faculty *</label>
                    <input
                      type="number"
                      min={0}
                      value={teacherState.totalWorking}
                      onChange={(e) => setTeacherState(prev => ({ ...prev, totalWorking: e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10) || 0) }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-slate-50 font-mono font-bold"
                      placeholder="0"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-emerald-800 mb-1">Faculty Present *</label>
                    <input
                      type="number"
                      min={0}
                      value={teacherState.present}
                      onChange={(e) => setTeacherState(prev => ({ ...prev, present: e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10) || 0) }))}
                      className="w-full px-3 py-2 border border-emerald-300 bg-emerald-50/40 rounded-lg font-mono font-bold text-emerald-800"
                      placeholder="0"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">On Approved Leave</label>
                    <input
                      type="number"
                      min={0}
                      value={teacherState.leave}
                      onChange={(e) => setTeacherState(prev => ({ ...prev, leave: e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10) || 0) }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-slate-50 font-mono"
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">On Other Duty (BLO/Exam)</label>
                    <input
                      type="number"
                      min={0}
                      value={teacherState.otherDuty}
                      onChange={(e) => setTeacherState(prev => ({ ...prev, otherDuty: e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10) || 0) }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-slate-50 font-mono"
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-rose-800 mb-1">Faculty Absent</label>
                    <input
                      type="number"
                      min={0}
                      value={teacherState.absent}
                      onChange={(e) => setTeacherState(prev => ({ ...prev, absent: e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10) || 0) }))}
                      className="w-full px-3 py-2 border border-rose-300 bg-rose-50/30 rounded-lg font-mono font-bold text-rose-800"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={handleResetForm}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-bold transition cursor-pointer"
                >
                  Reset Form
                </button>

                <div className="flex items-center space-x-3 w-full sm:w-auto justify-end">
                  {!formControl.is_open && !isDEO && !windowOverride && (
                    <span className="text-[11px] text-rose-700 font-bold bg-rose-50 px-2.5 py-1 rounded border border-rose-200">
                      Window Closed (08:00 AM – 09:30 AM IST)
                    </span>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting || (!formControl.is_open && !isDEO && !windowOverride)}
                    className="px-6 py-2.5 bg-gradient-to-r from-blue-700 to-[#0B1E38] hover:from-blue-800 hover:to-[#09172c] text-white font-black rounded-lg text-xs transition flex items-center space-x-2 shadow-md disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
                  >
                    <Send className="w-4 h-4 text-amber-400" />
                    <span>
                      {isSubmitting
                        ? 'Recording Submission...'
                        : !formControl.is_open && !isDEO && !windowOverride
                        ? 'Submission Window Closed'
                        : 'Submit Daily Attendance'}
                    </span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* ==================================================== */}
        {/* TAB 3: RECORDS & SUBMISSIONS LOG                    */}
        {/* ==================================================== */}
        {activeTab === 'history' && (
          <div className="space-y-4">
            <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 space-y-3">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                <div>
                  <h2 className="text-base font-black text-[#0B1E38] flex items-center gap-2">
                    <span>Daily Attendance Historical Master Log</span>
                    <span className="text-xs bg-blue-100 text-blue-800 font-extrabold px-2 py-0.5 rounded-full">
                      {historyTotal > 0 ? `${historyTotal.toLocaleString()} Records` : '24,599 Records'}
                    </span>
                  </h2>
                  <div className="text-xs text-slate-500">
                    Search and verify all genuine Class 6–12 historical attendance submissions from May to October 2026
                  </div>
                </div>

                <button
                  onClick={handleExportExcel}
                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition flex items-center space-x-1"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>Download Excel</span>
                </button>
              </div>

              {/* Filters */}
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 pt-2 border-t border-slate-100 text-xs">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search school, code, UDISE..."
                    value={historySearch}
                    onChange={(e) => { setHistorySearch(e.target.value); setHistoryPage(1); }}
                    className="w-full pl-8 pr-3 py-1.5 border border-slate-300 rounded-lg bg-slate-50 text-xs"
                  />
                </div>

                <select
                  value={historyFilterDate}
                  onChange={(e) => { setHistoryFilterDate(e.target.value); setHistoryPage(1); }}
                  className="px-2.5 py-1.5 border border-slate-300 rounded-lg bg-slate-50 font-medium text-xs"
                >
                  <option value="all">All Dates ({cascadingData.dates.length} dates)</option>
                  {cascadingData.dates.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>

                <select
                  value={historyFilterBlock}
                  onChange={(e) => { setHistoryFilterBlock(e.target.value); setHistoryPage(1); }}
                  className="px-2.5 py-1.5 border border-slate-300 rounded-lg bg-slate-50 font-bold text-xs"
                >
                  <option value="all">All 5 Blocks</option>
                  {BLOCKS.map((b) => (
                    <option key={b} value={b}>{b} Block</option>
                  ))}
                </select>

                <select
                  value={historyFilterCategory}
                  onChange={(e) => { setHistoryFilterCategory(e.target.value); setHistoryPage(1); }}
                  className="px-2.5 py-1.5 border border-slate-300 rounded-lg bg-slate-50 font-semibold text-xs"
                >
                  <option value="all">All Categories</option>
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left">
                  <thead className="bg-[#0B1E38] text-white uppercase font-bold text-[10px]">
                    <tr>
                      <th className="py-2.5 px-3">Ack / Import ID</th>
                      <th className="py-2.5 px-2.5">Date</th>
                      <th className="py-2.5 px-3">School Name</th>
                      <th className="py-2.5 px-2.5">Block</th>
                      <th className="py-2.5 px-2.5">Category</th>
                      <th className="py-2.5 px-2.5 text-center">Students (P/E)</th>
                      <th className="py-2.5 px-2 text-center">Student %</th>
                      <th className="py-2.5 px-2 text-center">Faculty %</th>
                      <th className="py-2.5 px-2.5">Submitter</th>
                      <th className="py-2.5 px-2 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 font-medium">
                    {historyList.map((h) => {
                      const ack = h.acknowledgement_number || h.source_import_id;
                      const sname = h.source_school_name || h.school_name;
                      const blk = h.block_normalized || h.block;
                      const stPres = h.total_present ?? h.summary?.totalPresent ?? 0;
                      const stEnr = h.total_enrolled ?? h.summary?.totalEnrolled ?? 0;
                      const stPct = h.student_attendance_pct ?? h.summary?.attendancePct ?? (stEnr > 0 ? ((stPres / stEnr) * 100).toFixed(1) : 'N/A');
                      const tcPct = h.teacher_attendance_pct ?? h.teachers?.attendancePct ?? 'N/A';

                      return (
                        <tr key={h.id || ack} className="hover:bg-slate-50 transition">
                          <td className="py-2 px-3 font-mono font-bold text-blue-700 whitespace-nowrap">{ack}</td>
                          <td className="py-2 px-2.5 font-semibold text-slate-800 whitespace-nowrap">{h.attendance_date}</td>
                          <td className="py-2 px-3 font-bold text-slate-900 max-w-[200px] truncate" title={sname}>{sname}</td>
                          <td className="py-2 px-2.5 text-slate-600 whitespace-nowrap">{blk}</td>
                          <td className="py-2 px-2.5 whitespace-nowrap">
                            <span className="px-1.5 py-0.5 rounded bg-slate-100 font-semibold text-[10px]">{h.school_type}</span>
                          </td>
                          <td className="py-2 px-2.5 text-center font-mono whitespace-nowrap">{stPres} / {stEnr}</td>
                          <td className="py-2 px-2 text-center font-mono font-bold text-emerald-700">{stPct}%</td>
                          <td className="py-2 px-2 text-center font-mono font-bold text-indigo-700">{tcPct}%</td>
                          <td className="py-2 px-2.5 text-slate-700 truncate max-w-[140px]">{h.submitter_name} ({h.designation})</td>
                          <td className="py-2 px-2 text-center whitespace-nowrap">
                            <button
                              onClick={() => setSelectedHistoryItem(h)}
                              className="px-2 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded text-[11px] font-bold border border-blue-200"
                            >
                              Dossier
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {historyTotalPages > 1 && (
                <div className="bg-slate-50 px-4 py-3 border-t border-slate-200 flex justify-between items-center text-xs">
                  <div className="text-slate-600 font-medium">
                    Showing Page <strong>{historyPage}</strong> of <strong>{historyTotalPages}</strong> ({historyTotal.toLocaleString()} Total Records)
                  </div>
                  <div className="flex items-center space-x-1.5">
                    <button
                      onClick={() => setHistoryPage(1)}
                      disabled={historyPage <= 1}
                      className="px-2.5 py-1 bg-white hover:bg-slate-100 border rounded font-bold disabled:opacity-40"
                    >
                      First
                    </button>
                    <button
                      onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                      disabled={historyPage <= 1}
                      className="px-2.5 py-1 bg-white hover:bg-slate-100 border rounded font-bold disabled:opacity-40"
                    >
                      Prev
                    </button>
                    <span className="px-3 py-1 bg-[#0B1E38] text-white rounded font-mono font-bold">
                      {historyPage}
                    </span>
                    <button
                      onClick={() => setHistoryPage(p => Math.min(historyTotalPages, p + 1))}
                      disabled={historyPage >= historyTotalPages}
                      className="px-2.5 py-1 bg-white hover:bg-slate-100 border rounded font-bold disabled:opacity-40"
                    >
                      Next
                    </button>
                    <button
                      onClick={() => setHistoryPage(historyTotalPages)}
                      disabled={historyPage >= historyTotalPages}
                      className="px-2.5 py-1 bg-white hover:bg-slate-100 border rounded font-bold disabled:opacity-40"
                    >
                      Last
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ==================================================== */}
        {/* TAB 4: MONTH-WISE ANALYSIS                          */}
        {/* ==================================================== */}
        {activeTab === 'monthly' && (
          <div className="space-y-4">
            <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
              <div className="text-xs font-bold text-blue-700 uppercase tracking-wider">
                Academic Session 2026–27 Analysis
              </div>
              <h2 className="text-base font-bold text-[#0B1E38]">
                Month-Wise Attendance Trends & Compliance Matrix (May – October 2026)
              </h2>
            </div>

            {/* Month Tabs */}
            <div className="flex items-center space-x-2 overflow-x-auto pb-1">
              {['May 2026', 'June 2026', 'July 2026', 'August 2026', 'September 2026', 'October 2026'].map((m) => {
                const mObj = monthlyData[m];
                const count = mObj?.total_submissions || 0;
                return (
                  <button
                    key={m}
                    onClick={() => setSelectedMonthTab(m)}
                    className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center space-x-2 border whitespace-nowrap shadow-2xs ${
                      selectedMonthTab === m
                        ? 'bg-[#0B1E38] text-white border-[#0B1E38]'
                        : 'bg-white text-slate-700 hover:bg-slate-50 border-slate-200'
                    }`}
                  >
                    <span>{m}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold ${
                      selectedMonthTab === m ? 'bg-amber-400 text-slate-950' : 'bg-slate-100 text-slate-600'
                    }`}>
                      {count.toLocaleString()} entries
                    </span>
                  </button>
                );
              })}
            </div>

            {monthlyData[selectedMonthTab] && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-2xs">
                    <div className="text-[10px] uppercase font-bold text-slate-400">Total Submissions</div>
                    <div className="text-lg font-black text-slate-900 mt-1">
                      {monthlyData[selectedMonthTab].total_submissions?.toLocaleString()}
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-2xs">
                    <div className="text-[10px] uppercase font-bold text-slate-400">Unique Schools Reported</div>
                    <div className="text-lg font-black text-blue-700 mt-1">
                      {monthlyData[selectedMonthTab].unique_schools_reported} / 403
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-2xs">
                    <div className="text-[10px] uppercase font-bold text-slate-400">Student Attendance %</div>
                    <div className="text-lg font-black text-emerald-700 mt-1">
                      {monthlyData[selectedMonthTab].avg_student_att_pct !== null ? `${monthlyData[selectedMonthTab].avg_student_att_pct}%` : 'N/A'}
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-2xs">
                    <div className="text-[10px] uppercase font-bold text-slate-400">Faculty Attendance %</div>
                    <div className="text-lg font-black text-indigo-700 mt-1">
                      {monthlyData[selectedMonthTab].avg_teacher_att_pct !== null ? `${monthlyData[selectedMonthTab].avg_teacher_att_pct}%` : 'N/A'}
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                  <div className="p-3 bg-slate-50 border-b border-slate-200 font-bold text-xs">
                    Block-Wise Summary for {selectedMonthTab}
                  </div>
                  <table className="w-full text-xs text-left">
                    <thead className="bg-slate-100 text-slate-700 uppercase font-bold text-[10px]">
                      <tr>
                        <th className="py-2.5 px-4">Block</th>
                        <th className="py-2.5 px-3 text-center">Submissions</th>
                        <th className="py-2.5 px-3 text-center">Unique Schools</th>
                        <th className="py-2.5 px-3 text-center">Enrolled</th>
                        <th className="py-2.5 px-3 text-center">Present</th>
                        <th className="py-2.5 px-3 text-center">Student Att. %</th>
                        <th className="py-2.5 px-3 text-center">Faculty Att. %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 font-medium">
                      {monthlyData[selectedMonthTab].blocks?.map((b: any) => (
                        <tr key={b.block} className="hover:bg-slate-50">
                          <td className="py-2.5 px-4 font-bold text-slate-900">{b.block}</td>
                          <td className="py-2.5 px-3 text-center font-mono">{b.submissions_count?.toLocaleString()}</td>
                          <td className="py-2.5 px-3 text-center font-mono font-semibold">{b.unique_schools}</td>
                          <td className="py-2.5 px-3 text-center font-mono">{b.total_enrolled?.toLocaleString()}</td>
                          <td className="py-2.5 px-3 text-center font-mono font-bold text-emerald-700">{b.total_present?.toLocaleString()}</td>
                          <td className="py-2.5 px-3 text-center font-mono font-bold text-emerald-800">{b.avg_student_att_pct}%</td>
                          <td className="py-2.5 px-3 text-center font-mono font-bold text-indigo-900">{b.avg_teacher_att_pct}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
          </>
        )}
      </main>

      {/* ---------------------------------------------------- */}
      {/* CONFIRMATION MODAL BEFORE SUBMISSION                 */}
      {/* ---------------------------------------------------- */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-4 border border-slate-200">
            <div className="flex items-center space-x-3 text-[#0B1E38] border-b border-slate-200 pb-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-base font-bold">Confirm Daily Attendance Submission</h3>
                <div className="text-xs text-slate-500">
                  {formSelectedSchool?.school_name} • {formDate}
                </div>
              </div>
            </div>

            <div className="bg-slate-50 rounded-lg p-3.5 text-xs space-y-2 border border-slate-200">
              <div className="flex justify-between">
                <span className="text-slate-500">Submitter:</span>
                <strong className="text-slate-900">{submitterName} ({designation})</strong>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Mobile:</span>
                <strong className="text-slate-900 font-mono">{mobileNumber}</strong>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">School Code / UDISE:</span>
                <strong className="text-slate-900 font-mono">{formSelectedSchool?.school_code} / {formSelectedSchool?.udise_code}</strong>
              </div>
              <div className="border-t border-slate-200 pt-2 flex justify-between font-bold text-emerald-800">
                <span>Total Students Present:</span>
                <span className="font-mono text-sm">{formStudentSummary.totalPresent} / {formStudentSummary.totalEnrolled} ({formStudentSummary.attendancePct}%)</span>
              </div>
              <div className="flex justify-between font-bold text-indigo-900">
                <span>Teachers Present:</span>
                <span className="font-mono text-sm">{formTeacherStats.present} / {formTeacherStats.totalWorking} ({formTeacherStats.attendancePct}%)</span>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-2">
              <button
                type="button"
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-bold transition"
              >
                Go Back & Edit
              </button>
              <button
                type="button"
                onClick={confirmAndSend}
                className="px-5 py-2 bg-[#0B1E38] hover:bg-blue-900 text-white rounded-lg text-xs font-bold transition flex items-center space-x-1.5 shadow-sm"
              >
                <Check className="w-4 h-4 text-emerald-400" />
                <span>Confirm & Submit</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------------------------------------------------- */}
      {/* SCHOOL DOSSIER & CLASS-WISE MODAL                    */}
      {/* ---------------------------------------------------- */}
      {(selectedSchoolDossier || selectedHistoryItem) && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full p-6 shadow-2xl space-y-4 border border-slate-200 max-h-[90vh] overflow-y-auto">
            {(() => {
              const item = selectedHistoryItem ? { ...selectedSchoolDossier, ...selectedHistoryItem } : (selectedSchoolDossier || selectedHistoryItem);
              const activeDateStr = item.attendance_date || (filterDate !== 'all' ? filterDate : 'Snapshot');
              return (
                <>
                  <div className="flex justify-between items-start border-b border-slate-200 pb-3">
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="text-[10px] font-bold text-blue-600 uppercase bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
                          Daily Attendance Dossier
                        </span>
                        <span className="text-[10px] font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded">
                          {item.school_type}
                        </span>
                        <span className="text-[10px] font-mono font-bold text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                          Date: {activeDateStr}
                        </span>
                      </div>
                      <h3 className="text-base font-black text-slate-900 mt-1">
                        {item.source_school_name || item.school_name}
                      </h3>
                      <div className="text-xs text-slate-500 font-mono mt-0.5">
                        Code: <strong>{item.source_school_code || item.school_code}</strong> • UDISE: <strong>{item.source_udise_code || item.udise_code}</strong> • Block: <strong>{item.block_normalized || item.block}</strong>
                      </div>
                    </div>
                    <button
                      onClick={() => { setSelectedSchoolDossier(null); setSelectedHistoryItem(null); }}
                      className="p-1.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-bold"
                    >
                      ✕ Close
                    </button>
                  </div>

                  <div className="space-y-4 text-xs">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 bg-slate-50 p-3.5 rounded-xl border border-slate-200">
                      <div>
                        <span className="text-slate-400 text-[10px] uppercase font-bold block">Submitter</span>
                        <strong className="text-slate-900 text-xs">{item.submitter_name || 'N/A'}</strong>
                        <span className="text-[11px] text-slate-500 block">{item.designation}</span>
                      </div>
                      <div>
                        <span className="text-slate-400 text-[10px] uppercase font-bold block">Mobile</span>
                        <strong className="font-mono text-slate-900">{item.mobile_number || 'N/A'}</strong>
                      </div>
                      <div>
                        <span className="text-slate-400 text-[10px] uppercase font-bold block">Student Att. %</span>
                        <strong className="text-emerald-700 font-black text-sm">
                          {item.student_attendance_pct !== null && item.student_attendance_pct !== undefined ? `${item.student_attendance_pct}%` : 'N/A'}
                        </strong>
                        <span className="text-[11px] text-slate-500 block">
                          {item.total_present} / {item.total_enrolled}
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-400 text-[10px] uppercase font-bold block">Faculty Att. %</span>
                        <strong className="text-indigo-700 font-black text-sm">
                          {item.teacher_attendance_pct !== null && item.teacher_attendance_pct !== undefined ? `${item.teacher_attendance_pct}%` : 'N/A'}
                        </strong>
                        <span className="text-[11px] text-slate-500 block">
                          {item.teachers_present} / {item.teachers_working || item.teachers_total_working}
                        </span>
                      </div>
                    </div>

                    {/* Class-wise 6 to 12 Breakdown Table */}
                    <div className="border border-slate-200 rounded-xl overflow-hidden">
                      <div className="bg-slate-100 p-2 px-3 font-bold text-slate-800 text-[11px] border-b border-slate-200 flex justify-between items-center">
                        <span>Class-Wise Student Attendance Breakdown (Classes 6 to 12)</span>
                        <span className="font-mono text-[10px] text-slate-500">Target Date: {activeDateStr}</span>
                      </div>
                      <table className="w-full text-xs text-left">
                        <thead className="bg-slate-50 font-bold text-slate-700 text-[10px] border-b border-slate-200">
                          <tr>
                            <th className="py-2 px-3">Class</th>
                            <th className="py-2 px-3 text-center">Present Students</th>
                            <th className="py-2 px-3 text-center">Enrolled Students</th>
                            <th className="py-2 px-3 text-center">Absent Students</th>
                            <th className="py-2 px-3 text-center">Class Attendance %</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 font-medium">
                          {[
                            { name: 'Class 6', data: item.classes?.class_6 || item.class_6 || item.classes?.find?.((c: any) => c.className === 'Class 6') },
                            { name: 'Class 7', data: item.classes?.class_7 || item.class_7 || item.classes?.find?.((c: any) => c.className === 'Class 7') },
                            { name: 'Class 8', data: item.classes?.class_8 || item.class_8 || item.classes?.find?.((c: any) => c.className === 'Class 8') },
                            { name: 'Class 9', data: item.classes?.class_9 || item.class_9 || item.classes?.find?.((c: any) => c.className === 'Class 9') },
                            { name: 'Class 10', data: item.classes?.class_10 || item.class_10 || item.classes?.find?.((c: any) => c.className === 'Class 10') },
                            { name: 'Class 11', data: item.classes?.class_11 || item.class_11 || item.classes?.find?.((c: any) => c.className === 'Class 11') },
                            { name: 'Class 12', data: item.classes?.class_12 || item.class_12 || item.classes?.find?.((c: any) => c.className === 'Class 12') },
                          ].map((cl) => {
                            const pres = cl.data?.present ?? cl.data?.totalPresent ?? 0;
                            const enr = cl.data?.enrolled ?? cl.data?.totalEnrolled ?? 0;
                            const abs = Math.max(0, enr - pres);
                            const pct = cl.data?.pct ?? cl.data?.attendance_pct ?? cl.data?.attendancePct ?? (enr > 0 ? ((pres / enr) * 100).toFixed(1) : null);

                            return (
                              <tr key={cl.name} className="hover:bg-slate-50">
                                <td className="py-2 px-3 font-bold text-slate-900">{cl.name}</td>
                                <td className="py-2 px-3 text-center font-mono font-semibold text-emerald-800">{enr > 0 ? pres : '—'}</td>
                                <td className="py-2 px-3 text-center font-mono text-slate-700">{enr > 0 ? enr : '—'}</td>
                                <td className="py-2 px-3 text-center font-mono text-rose-700">{enr > 0 ? abs : '—'}</td>
                                <td className="py-2 px-3 text-center font-bold font-mono">
                                  {pct !== null && enr > 0 ? (
                                    <span className="text-emerald-700">{pct}%</span>
                                  ) : (
                                    <span className="text-slate-400">N/A</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Historical Timeline Across All Available Dates */}
                    <div className="border border-slate-200 rounded-xl overflow-hidden">
                      <div className="bg-slate-100 p-2 px-3 font-bold text-slate-800 text-[11px] border-b border-slate-200 flex justify-between items-center">
                        <span className="flex items-center gap-1.5">
                          <History className="w-3.5 h-3.5 text-blue-600" />
                          <span>Historical Attendance Timeline Across All Available Dates</span>
                        </span>
                        <span className="text-[10px] bg-blue-100 text-blue-800 px-2 py-0.5 rounded font-mono font-bold">
                          {schoolHistoryList.length} Sessions Logged
                        </span>
                      </div>
                      
                      {loadingSchoolHistory ? (
                        <div className="p-4 text-center text-slate-500 text-xs">
                          Loading complete historical records...
                        </div>
                      ) : schoolHistoryList.length === 0 ? (
                        <div className="p-4 text-center text-slate-400 text-xs">
                          No historical attendance records logged for this school yet.
                        </div>
                      ) : (
                        <div className="max-h-56 overflow-y-auto">
                          <table className="w-full text-xs text-left">
                            <thead className="bg-slate-50 font-bold text-slate-600 text-[10px] border-b border-slate-200 sticky top-0">
                              <tr>
                                <th className="py-1.5 px-3">Date</th>
                                <th className="py-1.5 px-3">Submitter</th>
                                <th className="py-1.5 px-3 text-center">Students (Pres/Enr)</th>
                                <th className="py-1.5 px-3 text-center">Student Att. %</th>
                                <th className="py-1.5 px-3 text-center">Faculty (Pres/Work)</th>
                                <th className="py-1.5 px-3 text-center">Faculty Att. %</th>
                                <th className="py-1.5 px-3 text-center">Action</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 font-medium">
                              {schoolHistoryList.map((hist: any) => {
                                const isCurrentActive = (item.attendance_date === hist.attendance_date);
                                return (
                                  <tr
                                    key={hist.attendance_date}
                                    className={`hover:bg-blue-50/50 transition ${isCurrentActive ? 'bg-blue-50 font-semibold' : ''}`}
                                  >
                                    <td className="py-1.5 px-3 font-mono text-slate-900 whitespace-nowrap">
                                      {hist.attendance_date}
                                    </td>
                                    <td className="py-1.5 px-3 text-slate-700 whitespace-nowrap">
                                      <div>{hist.submitter_name || 'N/A'}</div>
                                      <div className="text-[10px] text-slate-400">{hist.designation || ''}</div>
                                    </td>
                                    <td className="py-1.5 px-3 text-center font-mono text-slate-800 whitespace-nowrap">
                                      {hist.total_present} / {hist.total_enrolled}
                                    </td>
                                    <td className="py-1.5 px-3 text-center font-mono font-bold text-emerald-700 whitespace-nowrap">
                                      {hist.student_attendance_pct !== null ? `${hist.student_attendance_pct}%` : 'N/A'}
                                    </td>
                                    <td className="py-1.5 px-3 text-center font-mono text-slate-800 whitespace-nowrap">
                                      {hist.teachers_present} / {hist.teachers_working}
                                    </td>
                                    <td className="py-1.5 px-3 text-center font-mono font-bold text-indigo-700 whitespace-nowrap">
                                      {hist.teacher_attendance_pct !== null ? `${hist.teacher_attendance_pct}%` : 'N/A'}
                                    </td>
                                    <td className="py-1.5 px-3 text-center whitespace-nowrap">
                                      <button
                                        onClick={() => setSelectedHistoryItem(hist)}
                                        className={`px-2 py-1 rounded text-[10px] font-bold cursor-pointer transition ${
                                          isCurrentActive
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-slate-200 hover:bg-slate-300 text-slate-800'
                                        }`}
                                      >
                                        {isCurrentActive ? 'Active' : 'View'}
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    {(item.remark || item.override_reason) && (
                      <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-950">
                        <strong>Submitter Remark:</strong> {item.remark || item.override_reason}
                      </div>
                    )}
                  </div>

                  <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                    <div className="text-[11px] text-slate-400 font-mono">
                      Timestamp: {item.latest_timestamp || item.submission_timestamp || item.submitted_at || 'Recorded'}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={handlePrintReport}
                        className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-lg text-xs font-bold flex items-center space-x-1"
                      >
                        <Printer className="w-3.5 h-3.5" />
                        <span>Print Dossier</span>
                      </button>
                      <button
                        onClick={() => { setSelectedSchoolDossier(null); setSelectedHistoryItem(null); }}
                        className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-bold"
                      >
                        Done
                      </button>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* FOOTER */}
      <footer className="bg-white border-t border-slate-200 py-4 px-4 sm:px-6 lg:px-8 text-center text-xs text-slate-500 print:hidden">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-2">
          <div>
            <strong>Daily Attendance Monitoring System (6–12)</strong> • District Education Office, Nuh • Department of School Education, Haryana
          </div>
          <div className="text-[11px] text-slate-400 font-mono">
            Direct Module Access: <a href="/daily-attendance" className="text-blue-600 underline font-semibold">/daily-attendance</a>
          </div>
        </div>
      </footer>
    </div>
  );
};
